import React, { useState, useRef, useEffect } from 'react';
import { formatQuantity } from '../utils/unitDisplay';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faXmark, faBan, faExclamationTriangle, faCheckCircle, faTrash, faBars, faUpload } from '@fortawesome/free-solid-svg-icons';
import { Check, Camera as LucideCamera, Upload as LucideUpload, Calendar as LucideCalendar } from 'lucide-react';
import OCRService from '../services/ocrService';
import SalesService from '../services/salesService';
import MenuService from '../services/menuService';
import http from '../services/httpClient';
import { postUndo, askUndoReason } from '../utils/ccsUndo';
import NcStockWarnings from './NcStockWarnings';
import { NoDateFileWarning, CountedDateNote, StockAbsorbedNote } from './TsrDateNotices';
import { postWithLockedRetry } from '../utils/ccsLockedRetry';
import { Table, TableBody, TableCell, TableFooter, TableHead, TableHeader, TableRow } from '../styles/standardStyles';
import { sortBySearchPriority } from '../utils/searchPriority';
import NCItemsPage from './NCItemsPage';
import SwiggyOrderReportModal from './SwiggyOrderReportModal';
import DayWiseTsrModal from './DayWiseTsrModal';
// Banquet: the third entry lane. Everything it needs lives under components/banquet,
// so the only footprint here is a mode flag, two menu entries and one mount point.
import useBanquetFlag from '../hooks/useBanquetFlag';
import BanquetSection from './banquet/BanquetSection';
import { lineWithFileDiscount, notInMenuMoney } from '../utils/tsrLineMoney';
// Sales Name Check (spec §9.3): one name key, remembered Skips set aside.
import { lineNameKey, isSetAsideRow, mergeByNameKey, setAsideNote, priceUnresolvedNote, notInMenuOptionFor, manualNotInMenuEntry, manualRowProblems } from '../utils/tsrNotInMenuLines';
import AddAsNotInMenuOption from './notInMenu/AddAsNotInMenuOption';
import OutletPickerPopup from './outletSale/OutletPickerPopup';
import OutletRevertModal from './outletSale/OutletRevertModal';
import BanquetRevertModal from './banquet/BanquetRevertModal';
import './TodaysSalesReport.css';

// The one-line note under the totals that names the lines set aside as non-stock.
const SET_ASIDE_NOTE_STYLE = { margin: '6px 2px 0', fontSize: '13px', color: '#6b7280', textAlign: 'left' };

const formatTitleCase = (value) => {
    if (!value) return '';
    return String(value)
        .toLowerCase()
        .split(' ')
        .filter(Boolean)
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
};

const formatShortQty = (value, fallback = '0') => {
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) return fallback;
    const rounded = Math.round(parsed * 100) / 100;
    // Never collapse a real, non-zero quantity to "0". A tiny value like 0.004
    // (e.g. an ingredient stored in kg that is really a few grams) rounds to 0
    // at 2 decimals and misleadingly reads as "Required: 0". Fall back to the
    // first two significant figures so the value stays visible (0.004, 0.0012…).
    if (rounded === 0 && parsed !== 0) {
        return String(Number(parsed.toPrecision(2)));
    }
    return rounded.toFixed(2).replace(/\.00$/, '').replace(/(\.\d)0$/, '$1');
};

// Resolve the business's Non-Chargeable wastage reason.
//
// Selection is by system_code === 'NC' — the machine identity — because the
// label is user-editable free text. The old label-substring search matched on
// "nc", which also matches "Cancelled order", so it could pick a completely
// unrelated reason and record NC wastage against it.
//
// The label scan survives only as a fallback for businesses whose reasons have
// not been tagged yet (pre-migration). When nothing matches, a properly tagged
// reason is created so the next call takes the fast path.
const resolveNcWastageReason = async (httpClient, businessId) => {
    const reasonsResponse = await httpClient.get(`/wastage/reasons?business_id=${businessId}`);
    const reasonsList = reasonsResponse?.data || [];

    const byCode = reasonsList.find(r => r.system_code === 'NC');
    if (byCode) return byCode;

    // Legacy fallback: equality on known labels, NOT substring — substring is
    // exactly the bug this replaces.
    const KNOWN_NC_LABELS = [
        'non chargeable (nc)',
        'non chargeable',
        'non chargable (nc)',
        'no charge',
        'owner use'
    ];
    const byLabel = reasonsList.find(r =>
        KNOWN_NC_LABELS.includes(String(r.reason_label || '').trim().toLowerCase())
    );
    if (byLabel) {
        console.warn('[NC] Reason matched by label fallback; it has no system_code yet.');
        return byLabel;
    }

    const created = await httpClient.post('/wastage/reasons', {
        business_id: businessId,
        reason_label: 'Non Chargeable (NC)',
        reason_category: 'General Waste',
        is_active: true,
        system_code: 'NC'
    });
    return created.data;
};

const parseISODateToLocal = (isoDate) => {
    if (!isoDate || typeof isoDate !== 'string') return null;
    const [year, month, day] = isoDate.split('-').map(Number);
    if (!year || !month || !day) return null;
    return new Date(year, month - 1, day);
};

const formatLocalDateToISO = (dateObj) => {
    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const formatDisplayDate = (isoDate) => {
    const parsed = parseISODateToLocal(isoDate);
    if (!parsed) return '';

    const day = String(parsed.getDate()).padStart(2, '0');
    const month = String(parsed.getMonth() + 1).padStart(2, '0');
    const year = parsed.getFullYear();
    return `${day}-${month}-${year}`;
};

const isSameCalendarDay = (left, right) => {
    if (!left || !right) return false;
    return left.getFullYear() === right.getFullYear()
        && left.getMonth() === right.getMonth()
        && left.getDate() === right.getDate();
};

/** '2026-10-01' → '01 Oct 2026' (the loaded outlet's day). */
const OUTLET_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const formatOutletReportDate = (iso) => {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso || ''));
    return m ? `${m[3]} ${OUTLET_MONTHS[Number(m[2]) - 1]} ${m[1]}` : String(iso || '');
};

/** The server refuses a file that states another day or several days, and any future date. */
const FILE_DATE_REFUSAL_TITLES = {
    FILE_DATE_MISMATCH: "Wrong day's file",
    FILE_MULTI_DAY: 'This file covers several days',
    MULTI_DAY_REPORT: 'This file covers several days',
    FUTURE_TSR_DATE: 'That date is in the future',
    TSR_DATE_REQUIRED: "Can't tell which day this file is for",
};

const TodaysSalesReport = ({ goTo, onComplete, previousScreen = null, fromLockModal = false, targetDate = null, onBeforeNavigateToWastage = null, isOnboarding = false, onRecoveryTsrCompleted = null }) => {
    // ── Date selector: default to IST today ──
    // Fallback only — the authoritative filing date arrives as `targetDate`
    // from /operations/today-status. Used when that call has not answered yet.
    //
    // The manual +5:30 that used to be here was applied BEFORE formatting with
    // timeZone:'Asia/Kolkata', so IST was added twice. It cancelled out only
    // because an Indian device's getTimezoneOffset() is -330; on a UTC or US
    // device it returned tomorrow's date. toLocaleDateString does the whole
    // conversion on its own.
    const getISTDate = () => {
        return new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
    };

    // ✨ NEW: Date-specific TSR state
    const [selectedDate, setSelectedDate] = useState(targetDate || getISTDate());
    // targetDate is the server's filing date and usually arrives AFTER mount
    // (App fetches it on a 30s poll). A useState initialiser runs once, so
    // without the effect below the late answer would be ignored and the picker
    // would keep the device-clock fallback — which is the whole bug.
    const filingDateAppliedRef = useRef(false);
    const userPickedDateRef = useRef(false);
    const [showDateCalendar, setShowDateCalendar] = useState(false);
    const [calendarViewMonth, setCalendarViewMonth] = useState(() => {
        const baseDate = parseISODateToLocal(targetDate || getISTDate()) || new Date();
        return new Date(baseDate.getFullYear(), baseDate.getMonth(), 1);
    });
    const [dateStatus, setDateStatus] = useState({
        wastageRecorded: false,
        tsrSubmitted: false,
        dayStatus: 'not_started'
    });
    const [isLoadingDateData, setIsLoadingDateData] = useState(false);

    // TSR Access Control State
    const [isCheckingAccess, setIsCheckingAccess] = useState(true);
    const [tsrAccessGranted, setTsrAccessGranted] = useState(false);
    const [pendingApprovalsCount, setPendingApprovalsCount] = useState(0);
    const [pendingApprovalsList, setPendingApprovalsList] = useState([]);
    const [accessCheckError, setAccessCheckError] = useState(null);

    // State for OCR scanned data - starts empty, populated by OCR
    const [scanItems, setScanItems] = useState([]); // Items from scan bill
    const [manualItems, setManualItems] = useState([]); // Items from manual entry
    // Computed: combined items for submission
    const salesData = [...scanItems, ...manualItems];
    // Per-row complementary counts keyed by the parser's exact item_name
    const [totalAmount, setTotalAmount] = useState('₹0');

    // ── Total plate count, shown beside every Total amount ───────────────────
    // DERIVED, not state. totalAmount is a string kept in sync by hand through
    // recalcTotal() and four other setters; a second piece of state would be a
    // fifth thing to remember to update and would silently drift the first time
    // someone missed one. Deriving it from salesData — the same list the amount
    // is summed from — makes that impossible.
    //
    // apiData.quantity is the raw number the row was parsed with. item.quantity
    // is the DISPLAY string ("2 plates") produced by formatQuantity, so it is
    // only a fallback for rows that carry no apiData.
    const rowQuantity = (row) => {
        const raw = row?.apiData?.quantity;
        const n = typeof raw === 'number' ? raw : parseFloat(raw);
        if (Number.isFinite(n)) return n;
        const fromLabel = parseFloat(String(row?.quantity ?? '').replace(/[^0-9.]/g, ''));
        return Number.isFinite(fromLabel) ? fromLabel : 0;
    };
    // Rounded so float noise never surfaces as "0.30000000000000004 plates".
    const totalPlates = Math.round(salesData.reduce((sum, row) => sum + rowQuantity(row), 0) * 100) / 100;
    const totalPlatesLabel = `${totalPlates} ${totalPlates === 1 ? 'plate' : 'plates'}`;
    // Scanned lines the server matched to a remembered Skip ("Packing Charges"):
    // recorded with the sale, never asked about. DERIVED like totalPlates, so the
    // note under the totals can never disagree with the table.
    const setAsideText = setAsideNote(scanItems.filter(isSetAsideRow));
    // Store complimentary items for each menu item
    const [complimentaryItemsMap, setComplimentaryItemsMap] = useState({});
    // Editable complimentary quantity overrides: { menuItemId: { compName: qty } }
    const [complimentaryOverrides, setComplimentaryOverrides] = useState({});
    const [isScanning, setIsScanning] = useState(false);
    const [error, setError] = useState(null);
    const [isDeducting, setIsDeducting] = useState(false);
    const [processingProgress, setProcessingProgress] = useState(0);
    const [processingMessage, setProcessingMessage] = useState('');
    const [menuList, setMenuList] = useState([]);

    const getMenuItemName = (menuItem) => (
        menuItem?.name || menuItem?.item_name || menuItem?.menu_item_name || ''
    );

    const getTopMenuSuggestions = (searchValue) => {
        const query = String(searchValue ?? '').trim();
        if (!query) return [];

        return sortBySearchPriority(menuList, query, getMenuItemName, { limit: 10 });
    };

    const normalizeComplimentaryQty = (value, fallback = 0) => {
        const digitsOnly = String(value ?? '').replace(/\D/g, '');
        if (!digitsOnly) return fallback;
        const parsed = parseInt(digitsOnly, 10);
        return Number.isFinite(parsed) ? Math.max(parsed, 0) : fallback;
    };


    // Multi-image upload support
    const [selectedFiles, setSelectedFiles] = useState([]);
    // Swiggy Order Report (Online Order Mode) structured-parse review
    const [showOrderReportModal, setShowOrderReportModal] = useState(false);
    const [orderReportData, setOrderReportData] = useState(null);
    const [orderReportMenu, setOrderReportMenu] = useState([]);
    const [orderReportParsing, setOrderReportParsing] = useState(false);
    // Multi-date ("day wise") POS export detected during a normal TSR scan.
    const [dayWiseReport, setDayWiseReport] = useState(null);
    const [isUploadDragOver, setIsUploadDragOver] = useState(false);
    const [fileProcessingProgress, setFileProcessingProgress] = useState({ current: 0, total: 0 });
    const cancelScanRef = useRef(false);

    // Missing ingredients modal state
    const [showMissingModal, setShowMissingModal] = useState(false);
    const [miSearchQuery, setMiSearchQuery] = useState('');
    useEffect(() => {
        if (!showMissingModal) {
            setMiSearchQuery('');
        }
    }, [showMissingModal]);
    const [missingIngredients, setMissingIngredients] = useState([]); // [{ itemName, ingredientName, required, available, unit }]

    // Complimentary shortage state
    const [showComplimentaryShortageModal, setShowComplimentaryShortageModal] = useState(false);
    const [complimentaryShortages, setComplimentaryShortages] = useState([]); // Shortages from complimentary items

    // Price mismatch detection state
    const [showPriceMismatchModal, setShowPriceMismatchModal] = useState(false);
    const [priceMismatches, setPriceMismatches] = useState([]); // [{ itemName, scannedPrice, databasePrice, menuItemId }]
    const [selectedMissingItems, setSelectedMissingItems] = useState({}); // key by itemName|ingredientName
    const [pendingSaleData, setPendingSaleData] = useState(null);
    // How to resume a DAY-WISE import once negative stock is approved. The
    // Missing Ingredients modal below is shared with the single-date flow, so
    // it needs to know which of the two it is confirming: when this is set, the
    // confirm button resumes the bulk import instead of calling processSale().
    // Held in a ref-style wrapper because a function in useState would be
    // invoked as a lazy initialiser rather than stored.
    const [dayWiseProceed, setDayWiseProceed] = useState(null);

    // Dismissing the shared Missing Ingredients modal must also drop any
    // day-wise resume it was holding. Leaving it set would make the NEXT
    // single-date shortage confirm re-run the bulk import instead.
    const closeMissingModal = () => {
        setShowMissingModal(false);
        setDayWiseProceed(null);
    };

    // ingredientId is part of the key because itemName+ingredientName alone
    // collides whenever one dish contributes the same ingredient twice (e.g. via
    // a preparation AND directly). Colliding keys made two rows share one
    // checkbox — ticking either one silently ticked both.
    const getMissingIngredientKey = (ingredient) =>
        `${ingredient.itemName}|${ingredient.ingredientName || ingredient.preparationName}|${ingredient.ingredientId ?? ''}`;

    const getFilteredSelection = (ingredients, sourceSelection = selectedMissingItems) => {
        const filtered = {};
        (ingredients || []).forEach((ingredient) => {
            const key = getMissingIngredientKey(ingredient);
            if (sourceSelection[key]) {
                filtered[key] = true;
            }
        });
        return filtered;
    };

    const getValidSelectedMissingKeys = () => {
        const validKeys = new Set((missingIngredients || []).map(getMissingIngredientKey));
        return Object.keys(selectedMissingItems).filter((key) => selectedMissingItems[key] && validKeys.has(key));
    };

    // Manual entry confirmation modal state
    const [showManualConfirmModal, setShowManualConfirmModal] = useState(false);
    // Scan bill confirmation modal state
    const [showScanConfirmModal, setShowScanConfirmModal] = useState(false);

    const [resolvedIngredients, setResolvedIngredients] = useState(new Set()); // Track substituted/resolved ingredients

    // Hamburger menu dropdown state
    const [showHamburgerMenu, setShowHamburgerMenu] = useState(false);
    const hamburgerMenuRef = useRef(null);
    // Match Stock In UX: Scan Bill / Manual Entry tabs
    const [tsrEntryTab, setTsrEntryTab] = useState('scan');
    const tsrTabGroupRef = useRef(null);
    const [tsrTabIndicatorStyle, setTsrTabIndicatorStyle] = useState({ opacity: 0, width: '0px', transform: 'translateX(0px)' });
    const [manualEntrySuggestions, setManualEntrySuggestions] = useState([]);
    const [showManualSuggestions, setShowManualSuggestions] = useState(false);
    const [manualSuggestionIndex, setManualSuggestionIndex] = useState(-1);
    const manualInputRefs = useRef({});
    const [selectedMenuItemId, setSelectedMenuItemId] = useState(null);

    // Success modal state
    const [showSuccessModal, setShowSuccessModal] = useState(false);

    // No TSR Recorded state
    const [showNoTSRModal, setShowNoTSRModal] = useState(false);

    // Wastage warning modal state
    const [showWastageWarningModal, setShowWastageWarningModal] = useState(false);

    // NC (No Charge) Items State - Items used by owner that should be recorded as wastage
    const [ncItems, setNcItems] = useState([]); // [{ menuItemId, itemName, quantity, price }]
    const [showNcModal, setShowNcModal] = useState(false);
    const [showNcPage, setShowNcPage] = useState(false);
    const [showNcSummaryPage, setShowNcSummaryPage] = useState(false);
    const [ncForm, setNcForm] = useState({
        itemName: '',
        quantity: 1,
        price: ''
    });
    const [ncSuggestions, setNcSuggestions] = useState([]);
    const [showNcSuggestions, setShowNcSuggestions] = useState(false);
    const [selectedNcMenuItemId, setSelectedNcMenuItemId] = useState(null);

    // Online Order Mode - When enabled, unit prices become editable in TSR
    const [isOnlineOrderMode, setIsOnlineOrderMode] = useState(false);
    // Platform picker for online orders
    const [showPlatformPicker, setShowPlatformPicker] = useState(false);
    const [selectedPlatform, setSelectedPlatform] = useState(null);
    const [platforms, setPlatforms] = useState([]);
    const [addingPlatform, setAddingPlatform] = useState(false);
    const [newPlatformName, setNewPlatformName] = useState('');

    // ── Banquet mode ────────────────────────────────────────────────────────
    // A banquet is billed plates x rate with no per-item price and books revenue
    // into its own tables. It deducts stock through the TSR engine, but from its
    // own endpoint (/banquet/submit). It is a third lane beside Regular and
    // Online, never a variation of either: a banquet row reaching /tsr/submit
    // would write a zero-priced line into the sales ledger.
    const banquetEnabled = useBanquetFlag();
    const [isBanquetMode, setIsBanquetMode] = useState(false);
    const [showBanquetRevertModal, setShowBanquetRevertModal] = useState(false);
    // An outlet's sale (a POS outlet report filed outlet by outlet on this
    // page) is reverted from its own list: the normal Revert TSR is not offered
    // in production.
    const [showOutletRevertModal, setShowOutletRevertModal] = useState(false);

    // One value the render reads, so a third mode cannot be half-applied.
    const entryMode = isBanquetMode ? 'banquet' : isOnlineOrderMode ? 'online' : 'regular';

    // setEntryMode is defined further down, once every piece of state it clears
    // has been declared — see the note beside it.

    // Online mode is turned on by the platform picker's Confirm button, which
    // calls setIsOnlineOrderMode directly and knows nothing about banquet. This
    // is what keeps the two mutually exclusive without editing that picker.
    React.useEffect(() => {
        if (isOnlineOrderMode && isBanquetMode) setIsBanquetMode(false);
    }, [isOnlineOrderMode, isBanquetMode]);

    // Unmapped Items Warning Modal State
    const [showUnmappedWarningModal, setShowUnmappedWarningModal] = useState(false);
    const [unmappedItemsList, setUnmappedItemsList] = useState([]); // [{ menuItemId, itemName }]
    // A3: NC units found on rows with no menu match. Surfaced in the Unmapped
    // modal because they cannot be recorded until the item is mapped.
    const [unmatchedNcItems, setUnmatchedNcItems] = useState([]); // [{ itemName, ncQty, ncAmount }]
    // Phase 8: cross-family unit mismatches reported by the last submission.
    // Persistent, because these deductions are quantitatively WRONG and a
    // toast that disappears is not an adequate warning for that.
    const [unitMismatchWarnings, setUnitMismatchWarnings] = useState([]);
    const [pendingUnmappedItems, setPendingUnmappedItems] = useState(null); // items to resume sale after user clicks "Continue Anyway"
    // Duplicate / near-duplicate warning. Holds the server's explanation AND the exact
    // arguments to replay, so Continue re-sends the same submission rather than a rebuilt
    // one. null = no warning showing.
    const [duplicateWarning, setDuplicateWarning] = useState(null);
    // "You already filed from this file" — raised BEFORE the file is parsed, so
    // answering it costs nothing either way. The scan loop parks on a promise
    // while this is open; reuploadDecisionRef holds its resolver.
    const [fileReuseWarning, setFileReuseWarning] = useState(null);
    const reuploadDecisionRef = useRef(null);
    // A POS outlet report is filed ONE OUTLET AT A TIME, as an ordinary TSR.
    // outletPicker: the uploaded report's outlets, waiting for the owner's pick
    //   ({ file, picker, confirmReupload }).
    // scanOutlet: the outlet whose lines are in the scan table — sent to
    //   /tsr/submit as the sale's outlet, and the file to offer the next outlet from.
    const [outletPicker, setOutletPicker] = useState(null);
    const [scanOutlet, setScanOutlet] = useState(null);
    // A Sales By Items file for another day (or with no day in its name) is
    // refused; the popup says why.
    const [fileDateRefusal, setFileDateRefusal] = useState(null);
    // Files that state no day of their own: warned about, and still filed.
    const [noDateFiles, setNoDateFiles] = useState([]);
    // sha256 of every file that actually contributed rows, carried to /tsr/submit
    // so the file can be recognised the next time it is uploaded.
    const [scannedSourceFiles, setScannedSourceFiles] = useState([]);

    // Discount Management State
    const [showDiscountModal, setShowDiscountModal] = useState(false);
    const [discountedItems, setDiscountedItems] = useState([]); // [{ menuItemId, itemName, originalPrice, discountPercentage, discountedPrice }]
    const [discountForm, setDiscountForm] = useState({
        itemName: '',
        menuItemId: null,
        originalPrice: 0,
        discountPercentage: ''
    });

    React.useEffect(() => {
        const updateTsrEntryIndicator = () => {
            const tabGroupNode = tsrTabGroupRef.current;
            if (!tabGroupNode) {
                setTsrTabIndicatorStyle({ opacity: 0, width: '0px', transform: 'translateX(0px)' });
                return;
            }

            const activeTabNode = tabGroupNode.querySelector('.tsr-sub-tab.active');
            if (!activeTabNode) {
                setTsrTabIndicatorStyle({ opacity: 0, width: '0px', transform: 'translateX(0px)' });
                return;
            }

            setTsrTabIndicatorStyle({
                width: `${activeTabNode.offsetWidth}px`,
                transform: `translateX(${activeTabNode.offsetLeft}px)`,
                opacity: 1
            });
        };

        const frameId = window.requestAnimationFrame(updateTsrEntryIndicator);
        window.addEventListener('resize', updateTsrEntryIndicator);

        return () => {
            window.cancelAnimationFrame(frameId);
            window.removeEventListener('resize', updateTsrEntryIndicator);
        };
    }, [tsrEntryTab]);

    // Auto-switch to Scan Bill tab when no items exist
    React.useEffect(() => {
        if (scanItems.length === 0 && manualItems.length === 0) {
            setTsrEntryTab('scan');
        }
    }, [scanItems, manualItems]);

    // The outlet label belongs to the outlet's scanned lines: once they leave
    // the table (a new scan, a cleared sheet, a filed sale) it goes with them.
    useEffect(() => {
        if (scanItems.length === 0) setScanOutlet(null);
    }, [scanItems]);

    const handleConfirmAndDeduct = async () => {
        // A banquet is submitted by BanquetSection's own button, to its own
        // endpoint, which runs its own stock pre-check and wastage gate. Nothing
        // below this line may run for one: the very next step drops every row
        // with no unit price, which is every banquet row.
        if (isBanquetMode) return;

        // A typed manual row must be a picked dish or marked "Not in Menu" (never a
        // silent ₹0 line), and a marked row needs its price per plate.
        const manualProblems = manualRowProblems(manualItems);
        if (manualProblems.unpicked.length > 0) {
            setError({ type: 'error', message: `Pick a dish or choose "Add as Not in Menu" for: ${manualProblems.unpicked.join(', ')}` });
            return;
        }
        if (manualProblems.unpriced.length > 0) {
            setError({ type: 'error', message: `Enter the price per plate for: ${manualProblems.unpriced.join(', ')}` });
            return;
        }
        if (manualProblems.unquantified.length > 0) {
            setError({ type: 'error', message: `Enter the plates for: ${manualProblems.unquantified.join(', ')}` });
            return;
        }

        try {
            setIsDeducting(true);
            setProcessingProgress(0);
            setProcessingMessage('Validating sale items...');
            setError(null);


            // Simulate progress
            setTimeout(() => {
                setProcessingProgress(20);
                setProcessingMessage('Checking ingredient availability...');
            }, 300);

            const parseNumber = (val) => {
                if (typeof val === 'number') return val;
                const n = Number(String(val ?? '').replace(/[^0-9.-]/g, ''));
                return Number.isFinite(n) ? n : 0;
            };

            // A4: rows dropped for having no usable unit price (see below).
            const zeroPriceExcluded = [];

            // Filter out items with valid menuItemIds and prepare API data
            const items = salesData
                .filter(item => {
                    const hasMenuId = Boolean(item.apiData?.menuItemId || item.menuItemId);
                    if (!hasMenuId) {
                    }
                    return hasMenuId;
                })
                .map(item => {
                    const apiData = item.apiData || item;
                    const menuItemId = apiData.menuItemId || item.menuItemId;
                    const quantity = parseNumber(apiData.quantity);
                    const unitPrice = parseNumber(apiData.rate || apiData.unitPrice || item.rate);

                    // Validate required fields
                    if (!menuItemId) {
                        return null;
                    }
                    if (quantity <= 0) {
                        return null;
                    }
                    if (unitPrice <= 0) {
                        // A4: counted, not just dropped. These rows are excluded from
                        // the submission; the confirm modal states how many so the
                        // exclusion is seen rather than discovered later.
                        zeroPriceExcluded.push(item.item || apiData.item_name || 'Unknown item');
                        return null;
                    }

                    return {
                        menuItemId,
                        quantity,
                        unitPrice,
                        complimentary_overrides: complimentaryOverrides[menuItemId] || null,
                        ...(apiData.fileAuthoritative ? { fileDiscount: parseNumber(apiData.fileDiscount) } : {})
                    };
                })
                .filter(Boolean); // Remove null entries

            // A4: the confirm modal counts these independently (it renders before
            // this filter runs); this log records exactly which rows were dropped.
            if (zeroPriceExcluded.length > 0) {
                console.warn(`⚠️ [TSR] ${zeroPriceExcluded.length} row(s) excluded for ₹0 amount:`, zeroPriceExcluded);
            }

            // ══════ UNMAPPED / UNRECOGNIZED ITEMS CHECK ══════
            // This MUST run before the "no valid items" error check,
            // because unrecognized items (no menuItemId) are filtered out of `items`
            // and would cause items.length === 0 → error before the modal can show.

            console.log(`\n🔍 [TSR CHECK] salesData has ${salesData.length} item(s), items (with menuId) has ${items.length} item(s)`);
            console.log(`🔍 [TSR CHECK] salesData details:`, salesData.map(s => ({
                name: s.item || s.item_name,
                menuItemId: s.apiData?.menuItemId || s.menuItemId || 'NULL',
                amount: s.apiData?.amount || s.amount
            })));

            // 1) Collect items that have NO menuItemId at all (not in the database)
            // Rows the owner marked "Not in Menu" were already answered: recorded with
            // the sale, never asked about again in the popup.
            const markedNotInMenu = mergeByNameKey(
                salesData.filter(item => item?.apiData?.notInMenu === true).map(manualNotInMenuEntry)
            );
            const noMenuRows = salesData.filter(item => {
                if (item?.apiData?.notInMenu === true) return false;
                const hasMenuId = Boolean(item.apiData?.menuItemId || item.menuItemId);
                if (!hasMenuId) {
                    console.log(isSetAsideRow(item)
                        ? `  🗂️ SET ASIDE: "${item.item || item.item_name}" is a remembered non-stock line`
                        : `  🚫 UNRECOGNIZED: "${item.item || item.item_name}" has NO menuItemId`);
                }
                return !hasMenuId;
            });
            const toNotInMenuEntry = (item) => {
                // Carry the money through. These fields are what makes the
                // line auditable server-side — without them the record would
                // say something was sold but not what it was worth, which is
                // the whole reason unmapped lines were invisible before.
                const apiData = item.apiData || item;
                const quantity = parseNumber(apiData.quantity);
                const unitPrice = parseNumber(apiData.rate || apiData.unitPrice || item.rate);
                // Booked at what was charged — the Net when the parser states
                // one — or a discounted file overstates its Not-in-Menu revenue.
                const money = notInMenuMoney({ quantity, unitPrice, amount: parseNumber(apiData.amount ?? item.amount), netAmount: apiData.netAmount });
                return {
                    menuItemId: null,
                    itemName: item.item || item.item_name || item.apiData?.item_name || 'Unknown Item',
                    nameKey: lineNameKey(item),
                    reason: 'not_in_menu',
                    quantity,
                    unitPrice: money.unitPrice,
                    amount: money.amount,
                    // A size name sold at a price no remembered size matches: the
                    // popup says so instead of guessing a size (spec §9.3).
                    priceUnresolved: item.priceUnresolved || null
                };
            };
            // Remembered Skips ("Packing Charges") are split off first. They are
            // still recorded with the sale (their revenue is real), flagged
            // skipped, and never raise the Unmapped popup. The server sets them
            // aside again on its own and ignores this flag (contract C4); the flag
            // only keeps them out of this screen's questions.
            const rememberedSkips = mergeByNameKey(noMenuRows.filter(isSetAsideRow).map(toNotInMenuEntry))
                .map(entry => ({ ...entry, skipped: true }));
            // Lines recorded with the sale without a question: set-aside skips and marked rows.
            const quietUnmapped = [...rememberedSkips, ...markedNotInMenu];
            // Deduplicate by name KEY, summing the duplicates rather than
            // discarding them — two bill lines of the same unknown dish are
            // two real sales, and keeping only the first would under-report.
            // "Veg Pasta." and "veg pasta" are one dish, as in Not in Menu.
            const unrecognized = mergeByNameKey(noMenuRows.filter(item => !isSetAsideRow(item)).map(toNotInMenuEntry));

            // 2) Collect items that exist in menu but have 0 recipe ingredients
            const zeroIngredient = items.reduce((acc, item) => {
                const menuItem = menuList.find(m => m.id === item.menuItemId || m.menu_item_id === item.menuItemId);
                const ingredientCount = parseInt(menuItem?.ingredient_count ?? -1);
                if (ingredientCount === 0) {
                    console.log(`  ⚠️ ZERO INGREDIENTS: "${menuItem?.name}" (id=${item.menuItemId}) has 0 recipe ingredients`);
                    if (!acc.some(u => u.menuItemId === item.menuItemId)) {
                        acc.push({
                            menuItemId: item.menuItemId,
                            itemName: menuItem?.name || menuItem?.item_name || `Item #${item.menuItemId}`,
                            reason: 'no_ingredients',
                            quantity: parseNumber(item.quantity),
                            unitPrice: parseNumber(item.unitPrice),
                            amount: parseNumber(item.quantity) * parseNumber(item.unitPrice)
                        });
                    }
                }
                return acc;
            }, []);

            // 3) Merge all lists into a single warning.
            //
            // A3: rows carrying NC units that never matched a menu item get a
            // third badge. Every NC detection layer requires a menu match, so
            // without this the NC units on an unmapped row are dropped in
            // silence — no modal, no wastage, no trace.
            // Carried WITH their plate count, so mapping the dish later can still
            // account for them (they used to be sent with no quantity at all).
            const ncUnmatchedEntries = (unmatchedNcItems || []).map(nc => ({
                menuItemId: null,
                itemName: nc.itemName,
                // The same key as the unrecognised rows, so the pairing below
                // survives a respelling ("Veg Pasta." / "veg pasta").
                nameKey: nc.nameKey || lineNameKey({ itemName: nc.itemName }),
                reason: 'nc_unmatched',
                ncQty: nc.ncQty,
                quantity: nc.ncQty,
                unitPrice: 0,
                amount: 0
            }));

            // The NC badge replaces a ₹0 "Not in Menu" row of the same dish (nothing
            // was charged for it). A row that took money keeps its own line: the
            // paid plates of a dish that also had NC plates are real revenue, and
            // replacing them with the badge lost it. "The same dish" = the same name key.
            const ncKeys = new Set(ncUnmatchedEntries.map(e => e.nameKey));
            const allUnmapped = [
                ...ncUnmatchedEntries,
                ...unrecognized.filter(u => !ncKeys.has(u.nameKey) || Number(u.amount) > 0),
                ...zeroIngredient
            ];

            console.log(`🔍 [TSR CHECK] Result: ${unrecognized.length} unrecognized, ${zeroIngredient.length} zero-ingredient → ${allUnmapped.length} total unmapped; ${rememberedSkips.length} set aside as remembered non-stock`);

            if (allUnmapped.length > 0) {
                console.log(`⚠️ [TSR] ${allUnmapped.length} unmapped/unrecognized item(s) detected:`, allUnmapped.map(u => `${u.itemName} (${u.reason})`));
                setUnmappedItemsList(allUnmapped);
                // Only lines that are actually DROPPED get recorded server-side.
                // A 'no_ingredients' row still has a menuItemId, so it is
                // submitted as a normal sale line and its revenue is already in
                // SalesTransactions — persisting it here too would double-count
                // it and render it twice in the sales detail.
                const droppedUnmapped = allUnmapped.filter(u => u.reason !== 'no_ingredients');
                // Set-aside lines ride along with them: recorded with the sale, never shown in this popup.
                setPendingUnmappedItems({ items, unmapped: [...droppedUnmapped, ...quietUnmapped] }); // items may be empty if ALL were unrecognized — that's fine
                setShowUnmappedWarningModal(true);
                setIsDeducting(false);
                setProcessingProgress(0);
                setProcessingMessage('');
                return; // Pause — modal takes over
            }
            // ══════ END UNMAPPED CHECK ══════

            // A bill whose only lines are remembered Skips that took money is an
            // all-Not-in-Menu bill. It follows the path Continue Anyway takes for
            // one: ₹0 menu takings, its revenue held aside with the sale, no stock
            // moved. The refusal below is for a bill with nothing to record at all.
            if (items.length === 0 && quietUnmapped.some(s => Number(s.amount) > 0)) {
                setProcessingProgress(60);
                setProcessingMessage('Submitting TSR...');
                await processSale([], false, quietUnmapped);
                return;
            }

            // If no unmapped items found but also no valid items, show error
            if (items.length === 0) {
                const unmatched = salesData
                    .map(item => item.item || item.item_name)
                    .join(', ');

                throw new Error(
                    `No valid menu items found for items ${unmatched}. `
                );
            }

            // Call pre-check stock API to get real missing ingredients
            setProcessingProgress(40);
            setProcessingMessage('Checking stock levels...');
            const stockCheckResult = await SalesService.preCheckStock({ items });


            if (stockCheckResult.success && stockCheckResult.hasNegativeStock) {
                // Show missing ingredients modal with real data
                setProcessingProgress(100);
                setProcessingMessage('Missing ingredients detected');

                setTimeout(() => {
                    setMissingIngredients(stockCheckResult.missingIngredients || []);
                    setSelectedMissingItems({});
                    // Set-aside lines cross the missing-ingredients hop with the sale.
                    setPendingSaleData({ items, unmapped: quietUnmapped });
                    setShowMissingModal(true);
                    setIsDeducting(false);
                    setProcessingProgress(0);
                }, 500);
                return;
            }

            // If no missing ingredients, proceed directly with sale
            setProcessingProgress(60);
            setProcessingMessage('Deducting ingredients...');
            await processSale(items, false, quietUnmapped);

        } catch (err) {
            const backendMsg = err?.response?.data?.message || err?.data?.message;
            const message = backendMsg || err?.message || 'Failed to process sale and deduct ingredients';
            setError({
                type: 'error',
                message
            });
            setIsDeducting(false);
        }
    };

    // Handler for "Continue Anyway" on the unmapped items warning modal
    const handleContinueWithUnmapped = async () => {
        setShowUnmappedWarningModal(false);
        const pending = pendingUnmappedItems;
        setPendingUnmappedItems(null);
        setUnmappedItemsList([]);

        if (!pending?.items) return;

        try {
            setIsDeducting(true);

            // Every line Not in Menu (an outlet selling packaged goods off the
            // menu, say): no menu dish, so no stock to check — the sale books
            // their revenue as Not in Menu.
            if (pending.items.length === 0) {
                setProcessingProgress(60);
                setProcessingMessage('Submitting TSR...');
                await processSale([], false, pending.unmapped || []);
                return;
            }

            setProcessingProgress(40);
            setProcessingMessage('Checking stock levels...');

            // Resume from where we left off — stock pre-check
            const stockCheckResult = await SalesService.preCheckStock({ items: pending.items });

            if (stockCheckResult.success && stockCheckResult.hasNegativeStock) {
                setProcessingProgress(100);
                setProcessingMessage('Missing ingredients detected');
                setTimeout(() => {
                    setMissingIngredients(stockCheckResult.missingIngredients || []);
                    setSelectedMissingItems({});
                    // Carry the unmapped list across the missing-ingredients hop
                    // too, or it is lost whenever a shortage modal intervenes.
                    setPendingSaleData({ items: pending.items, unmapped: pending.unmapped || [] });
                    setShowMissingModal(true);
                    setIsDeducting(false);
                    setProcessingProgress(0);
                }, 500);
                return;
            }

            setProcessingProgress(60);
            setProcessingMessage('Deducting ingredients...');
            await processSale(pending.items, false, pending.unmapped || []);
        } catch (err) {
            const backendMsg = err?.response?.data?.message || err?.data?.message;
            const message = backendMsg || err?.message || 'Failed to process sale and deduct ingredients';
            setError({ type: 'error', message });
            setIsDeducting(false);
        }
    };

    // Duplicate warning — Cancel. Nothing was written server-side (the submit rolled
    // back before the line items), so this only has to dismiss. The form is left intact
    // so the owner can change the date and file it correctly instead.
    const handleCancelDuplicate = () => {
        setDuplicateWarning(null);
        setIsDeducting(false);
        setProcessingProgress(0);
        setProcessingMessage('');
    };

    // Duplicate warning — Continue. Replays the identical submission with the server's
    // guard waived for this one request.
    const handleConfirmDuplicate = async () => {
        const pending = duplicateWarning?.retry;
        setDuplicateWarning(null);
        if (!pending?.items) return;
        try {
            setIsDeducting(true);
            setProcessingProgress(60);
            setProcessingMessage('Submitting TSR...');
            await processSale(pending.items, pending.allowNegativeStock, pending.unmappedItems || [], true);
        } catch (err) {
            const backendMsg = err?.response?.data?.message || err?.data?.message;
            setError({ type: 'error', message: backendMsg || err?.message || 'Failed to submit' });
            setIsDeducting(false);
        }
    };

    // Handler for "Map Items Now" on the unmapped items warning modal
    const handleGoToItemMap = () => {
        setShowUnmappedWarningModal(false);
        setPendingUnmappedItems(null);
        setUnmappedItemsList([]);

        let isQRUser = false;
        try {
            const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
            isQRUser = storedUser?.isQRBilling === true || storedUser?.business?.isQRBilling === true;
        } catch (_) {
            isQRUser = false;
        }
        goTo && goTo(isQRUser ? 'qr-itemmap' : 'itemmap');
    };

    const [discountSuggestions, setDiscountSuggestions] = useState([]);
    const [showDiscountSuggestions, setShowDiscountSuggestions] = useState(false);
    const [showNcSuccessModal, setShowNcSuccessModal] = useState(false);
    // The last sale's NC plates, as the server recorded them with it ({ recorded, plates }).
    const [lastSaleNc, setLastSaleNc] = useState(null);
    // Set when a closing count already holds the filed date: money recorded, no stock moved.
    const [lastSaleCountDate, setLastSaleCountDate] = useState(null);
    const [ncSuccessMessage, setNcSuccessMessage] = useState([]);
    const [ncModalError, setNcModalError] = useState(''); // Local error for NC modal

    // Menu-match confirmation modal state (from bill parsing).
    // Runs FIRST in the modal chain: a wrong menu match means the wrong recipe is
    // deducted, so every downstream modal (NC/discount/price) is reasoning about
    // the wrong dish until this is settled.
    const [showMatchConfirmModal, setShowMatchConfirmModal] = useState(false);
    const [matchConfirmations, setMatchConfirmations] = useState([]);
    // [{ scanIndex, billName, menuItemId, matchedName, matchScore, confirmReason, candidates[], resolvedMenuItemId }]
    const [matchConfirmSearch, setMatchConfirmSearch] = useState({}); // scanIndex -> query string
    const [matchConfirmOpenIdx, setMatchConfirmOpenIdx] = useState(null); // which dropdown is open

    // Auto-detected discount modal state (from bill parsing)
    const [showAutoDiscountModal, setShowAutoDiscountModal] = useState(false);
    const [autoDetectedDiscounts, setAutoDetectedDiscounts] = useState([]);
    // [{ itemName, dbPrice, billedUnitPrice, discountPerUnit, totalDiscount, quantity, menuItemId }]
    const [billTotalDiscount, setBillTotalDiscount] = useState(0); // Total discount from the parsed bill file

    /**
     * The ONLY way banquet mode is entered or left.
     *
     * Declared HERE, below every setter it calls, rather than beside the
     * banquet state it belongs to — referencing a `const` from above its own
     * declaration is legal inside a function body but sits in the temporal dead
     * zone until called, which is exactly the kind of correct-but-fragile
     * arrangement that breaks the first time somebody moves a line.
     *
     * Entering CLEARS the sheet: a banquet starts from an empty menu, and the
     * rows already there belong to a different kind of document. That is
     * destructive, so it asks first when there is anything to lose.
     *
     * Leaving clears nothing, because it does not have to — BanquetSection is
     * unmounted, and unmounting takes its whole state with it. A manual
     * teardown here would be a second list to keep in step with that component.
     */
    const setEntryMode = (next) => {
        if (next === entryMode) return;

        if (next === 'banquet') {
            const dirty = scanItems.length > 0 || manualItems.length > 0 || selectedFiles.length > 0;
            if (dirty && !window.confirm('Switching to Banquet clears the items on this sheet. Continue?')) {
                return;
            }
            setIsOnlineOrderMode(false);
            setSelectedPlatform(null);
            setError(null);
            setScanItems([]);
            setManualItems([]);
            setSelectedFiles([]);
            setNcItems([]);
            setDiscountedItems([]);
            setAutoDetectedDiscounts([]);
            setBillTotalDiscount(0);
            setScannedSourceFiles([]);
            setTotalAmount('₹0');
        }

        setIsBanquetMode(next === 'banquet');
    };

    // Auto-detected NC (Non-Chargeable) modal state (from bill parsing)
    const [showAutoNcModal, setShowAutoNcModal] = useState(false);
    const [autoDetectedNcItems, setAutoDetectedNcItems] = useState([]);
    // [{ itemName, menuItemId, totalQty, ncQty, billedQty, grossAmount, netAmount, ncAmount, dbPrice, reason }]
    const ncReasonInputRefs = useRef([]);
    const ncConfirmBtnRef = useRef(null);

    useEffect(() => {
        if (showAutoNcModal && autoDetectedNcItems.length > 0) {
            const timer = setTimeout(() => {
                ncReasonInputRefs.current[0]?.focus();
            }, 100);
            return () => clearTimeout(timer);
        }
    }, [showAutoNcModal]);

    // =================== TSR REVERT STATE (LOCALHOST ONLY) ===================
    const [showTsrRevertModal, setShowTsrRevertModal] = useState(false);
    const [tsrRevertList, setTsrRevertList] = useState([]); // latest 3 TSRs
    const [tsrRevertLoading, setTsrRevertLoading] = useState(false);
    const [tsrRevertingId, setTsrRevertingId] = useState(null); // saleId being reverted
    const [tsrRevertError, setTsrRevertError] = useState(null);
    const [tsrRevertSuccess, setTsrRevertSuccess] = useState(null);
    const [tsrRevertConfirmId, setTsrRevertConfirmId] = useState(null); // saleId awaiting confirm
    // How far back reverting is allowed, and why. An empty list means two very
    // different things — "you have posted nothing since the count" versus
    // "everything you have is already certified" — and the screen has to say
    // which. { reconciled_through, floor, basis, max_rows }
    const [tsrRevertWindow, setTsrRevertWindow] = useState(null);

    const isLocalhost = (
        window.location.hostname === 'localhost' ||
        window.location.hostname === '127.0.0.1' ||
        window.location.hostname.startsWith('192.168.')
    );

    const handleOpenTsrRevert = async () => {
        setShowTsrRevertModal(true);
        setTsrRevertError(null);
        setTsrRevertSuccess(null);
        setTsrRevertConfirmId(null);
        setTsrRevertList([]);
        setTsrRevertWindow(null);
        setTsrRevertLoading(true);
        try {
            const token = localStorage.getItem('token');
            const businessId = localStorage.getItem('businessId');
            const API_BASE = process.env.REACT_APP_API_URL;
            const resp = await fetch(`${API_BASE}/tsr/recent-list`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'x-business-id': businessId
                }
            });
            const result = await resp.json();
            if (result.success) {
                setTsrRevertList(result.data || []);
                setTsrRevertWindow(result.window || null);
            } else {
                setTsrRevertError(result.error || 'Failed to fetch recent TSRs');
            }
        } catch (err) {
            setTsrRevertError(err.message || 'Network error');
        } finally {
            setTsrRevertLoading(false);
        }
    };

    const handleConfirmRevert = async (saleId) => {
        setTsrRevertingId(saleId);
        setTsrRevertError(null);
        setTsrRevertSuccess(null);
        try {
            const token = localStorage.getItem('token');
            const businessId = localStorage.getItem('businessId');
            const API_BASE = process.env.REACT_APP_API_URL;
            const send = (body) => fetch(`${API_BASE}/tsr/revert/${saleId}`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'x-business-id': businessId,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(body || {})
            });
            let resp = await send({});
            let result = await resp.json();
            // Continuous closing stock: an undo needs a reason (spec §9.5).
            if (result && result.code === 'REASON_REQUIRED') {
                const reason = askUndoReason();
                if (!reason) { setTsrRevertError('Undo cancelled — a reason is required.'); return; }
                resp = await send({ reason });
                result = await resp.json();
            }
            if (result.success) {
                setTsrRevertSuccess(result);
                setTsrRevertConfirmId(null);
                // Remove reverted item from list
                setTsrRevertList(prev => prev.filter(t => t.sale_id !== saleId));
                // Refresh date data
                if (selectedDate) loadTsrDataForDate(selectedDate);
            } else {
                // The backend refuses a revert for reasons the owner can act on —
                // a certified period, a count in progress. Surface its message
                // rather than a generic failure, and drop the row from the list
                // when it is permanently out of bounds so it cannot be retried.
                setTsrRevertError(result.error || 'Revert failed');
                setTsrRevertConfirmId(null);
                if (result.code === 'PERIOD_RECONCILED' || result.code === 'OUTSIDE_REVERT_WINDOW') {
                    setTsrRevertList(prev => prev.filter(t => t.sale_id !== saleId));
                }
            }
        } catch (err) {
            setTsrRevertError(err.message || 'Network error during revert');
            setTsrRevertConfirmId(null);
        } finally {
            setTsrRevertingId(null);
        }
    };

    // =================== ONLINE SALE REVERT STATE (LOCALHOST ONLY) ===================
    const [showOnlineRevertModal, setShowOnlineRevertModal] = useState(false);
    const [onlineRevertList, setOnlineRevertList] = useState([]);
    const [onlineRevertLoading, setOnlineRevertLoading] = useState(false);
    const [onlineRevertingId, setOnlineRevertingId] = useState(null);
    const [onlineRevertError, setOnlineRevertError] = useState(null);
    const [onlineRevertSuccess, setOnlineRevertSuccess] = useState(null);
    const [onlineRevertConfirmId, setOnlineRevertConfirmId] = useState(null);

    const handleOpenOnlineRevert = async () => {
        setShowOnlineRevertModal(true);
        setOnlineRevertError(null);
        setOnlineRevertSuccess(null);
        setOnlineRevertConfirmId(null);
        setOnlineRevertList([]);
        setOnlineRevertLoading(true);
        try {
            const resp = await http.get('/online-sales/recent-list');
            const data = resp.data || resp;
            setOnlineRevertList(data.orders || []);
        } catch (err) {
            setOnlineRevertError(err.message || 'Network error');
        } finally {
            setOnlineRevertLoading(false);
        }
    };

    const handleConfirmOnlineRevert = async (orderId) => {
        setOnlineRevertingId(orderId);
        setOnlineRevertError(null);
        setOnlineRevertSuccess(null);
        try {
            const resp = await postUndo(`/online-sales/revert/${orderId}`, {});
            // httpClient returns JSON body directly; resp.data is the nested sub-field,
            // NOT the full response. Read resp directly since it has the success flag.
            const result = (resp && resp.success !== undefined) ? resp : (resp.data || resp);
            if (result.success) {
                setOnlineRevertSuccess(result);
                setOnlineRevertConfirmId(null);
                setOnlineRevertList(prev => prev.filter(o => o.online_order_id !== orderId));
            } else {
                setOnlineRevertError(result.error || 'Revert failed');
                setOnlineRevertConfirmId(null);
            }
        } catch (err) {
            // httpClient throws with err.data (not err.response.data)
            const errData = err.data || err.response?.data;
            setOnlineRevertError(errData?.error || err.message || 'Network error during revert');
            setOnlineRevertConfirmId(null);
        } finally {
            setOnlineRevertingId(null);
        }
    };

    const scanProgress = fileProcessingProgress.total > 0
        ? Math.min(100, Math.max(0, (fileProcessingProgress.current / fileProcessingProgress.total) * 100))
        : 0;

    const recalcTotal = (nextScanItems, nextManualItems) => {
        const scanSum = nextScanItems.reduce((acc, it) => acc + (it.apiData?.amount || 0), 0);
        const manualSum = nextManualItems.reduce((acc, it) => acc + (it.apiData?.amount || 0), 0);
        setTotalAmount(`₹${(scanSum + manualSum).toFixed(2)}`);
    };

    // Component-scoped so the match-confirmation handler and modal can use it too
    // (the original lived inside processSalesReports and was invisible here).
    const findMenuItemById = (menuItemId) => {
        if (!menuItemId || menuList.length === 0) return null;
        return menuList.find(mi => mi.menu_item_id === menuItemId || mi.id === menuItemId) || null;
    };

    // ═══ MENU MATCH CONFIRMATION ═══

    // User picked a different menu item for a flagged row.
    const handleMatchConfirmReassign = (scanIndex, menuItem) => {
        const id = menuItem.menu_item_id || menuItem.id;
        setMatchConfirmations(prev => prev.map(c =>
            c.scanIndex === scanIndex
                ? { ...c, resolvedMenuItemId: id, matchedName: menuItem.name, reassigned: true }
                : c
        ));
        setMatchConfirmOpenIdx(null);
        setMatchConfirmSearch(prev => ({ ...prev, [scanIndex]: '' }));
    };

    // Apply every confirmation to the scan rows, persist them as aliases, then
    // hand off to the next modal in the chain.
    const handleMatchConfirmSubmit = async () => {
        // 1. Write the user's decisions onto the rows that will actually deduct.
        //    Target by scanIndex, never menuItemId — a collision means two rows
        //    share one id and would both be rewritten.
        const byIndex = new Map(matchConfirmations.map(c => [c.scanIndex, c]));
        const nextScanItems = scanItems.map((row, idx) => {
            const c = byIndex.get(idx);
            if (!c || !c.resolvedMenuItemId) return row;
            const menuItem = findMenuItemById(c.resolvedMenuItemId);
            const unitPrice = menuItem?.price != null ? parseFloat(menuItem.price) : null;
            const qty = row.apiData?.quantity || 0;
            return {
                ...row,
                menuItemId: c.resolvedMenuItemId,
                // Keep the POS name on screen; only the mapping changed.
                apiData: {
                    ...row.apiData,
                    menuItemId: c.resolvedMenuItemId,
                    ...(c.reassigned && unitPrice != null
                        ? { rate: unitPrice, unitPrice, amount: qty * unitPrice }
                        : {})
                },
                ...(c.reassigned && unitPrice != null ? { amount: `₹${(qty * unitPrice).toFixed(2)}` } : {}),
                needsConfirmation: false,
                matchedName: c.matchedName,
                matchMethod: 'user_confirmed'
            };
        });
        setScanItems(nextScanItems);
        recalcTotal(nextScanItems, manualItems);

        // 2. Persist ONLY deliberate decisions as learned aliases. An unchanged
        //    collision is NOT a decision — writing it would lock in a wrong guess
        //    that then suppresses its own collision warning forever. So persist a
        //    row only if the user actually reassigned it, or it wasn't a collision.
        //    Unchanged collisions still apply to THIS TSR; they're just not learned.
        const toPersist = matchConfirmations
            .filter(c => c.resolvedMenuItemId && (c.reassigned || !c.isCollision))
            .map(c => ({ billName: c.billName, menuItemId: c.resolvedMenuItemId }));
        if (toPersist.length > 0) {
            try {
                await http.post('/tsr/confirm-match', { confirmations: toPersist });
            } catch (err) {
                console.warn('⚠️ Could not save match confirmations (they still apply to this TSR):', err?.data || err?.message);
            }
        }

        setShowMatchConfirmModal(false);
        setMatchConfirmations([]);

        // 3. Chain to the next modal, mirroring the NC modal's cascade.
        if (autoDetectedNcItems.length > 0) {
            setShowAutoNcModal(true);
        } else if (autoDetectedDiscounts.length > 0 || billTotalDiscount > 0) {
            setShowAutoDiscountModal(true);
        } else if (priceMismatches.length > 0) {
            setShowPriceMismatchModal(true);
        }
    };

    // =================== TSR ACCESS CONTROL CHECK ===================
    // Check TSR access on component mount
    const checkTSRAccess = React.useCallback(async () => {
        try {
            setIsCheckingAccess(true);
            setAccessCheckError(null);

            const userId = Number(localStorage.getItem('userId'));
            const businessId = Number(localStorage.getItem('businessId'));

            if (!userId || !businessId) {
                setAccessCheckError('User or business information not found');
                setIsCheckingAccess(false);
                return;
            }

            // Get API_URL from environment (already includes /api)
            const API_BASE = process.env.REACT_APP_API_URL;

            // Call the TSR access control API
            const response = await fetch(`${API_BASE}/tsr-access/check/${userId}?business_id=${businessId}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`,
                    'x-business-id': businessId.toString()
                },
                credentials: 'include'
            });

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: Failed to check TSR access`);
            }

            const result = await response.json();

            if (result.data && result.data.can_submit) {
                // Access granted - user can submit TSR
                setTsrAccessGranted(true);
                setPendingApprovalsCount(0);
                setPendingApprovalsList([]);
            } else {
                // Access blocked - check reason
                setTsrAccessGranted(false);
                setPendingApprovalsCount(result.data?.pending_count || 0);
                setPendingApprovalsList(result.data?.pending_approvals || []);
            }

        } catch (err) {
            console.error('❌ TSR Access Check Error:', err);
            setAccessCheckError(err.message || 'Failed to check TSR access');
            // On error, default to blocking access for security
            setTsrAccessGranted(false);
        } finally {
            setIsCheckingAccess(false);
        }
    }, []);

    // =================== WASTAGE CHECK ON PAGE LOAD ===================
    // Check if wastage is recorded - show warning modal if not
    const checkWastageStatus = React.useCallback(async () => {
        try {
            const token = localStorage.getItem('token');
            const businessId = localStorage.getItem('businessId');
            const API_BASE = process.env.REACT_APP_API_URL;

            const checkDate = selectedDate || targetDate || getISTDate();

            // Pass date parameter to the operations endpoint
            const url = `${API_BASE}/operations/today-status?date=${checkDate}`;

            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'x-business-id': businessId
                }
            });

            if (!response.ok) {
                console.warn('⚠️ Failed to check wastage status');
                return;
            }

            const result = await response.json();

            if (result.success && result.data) {
                const wastageRecorded = result.data.wastageRecorded;

                // If wastage is not recorded, show warning modal immediately
                if (!wastageRecorded) {
                    setShowWastageWarningModal(true);
                }
            }
        } catch (err) {
            console.error('❌ Error checking wastage status:', err);
            // Don't block user if check fails
        }
    }, [selectedDate, targetDate]);

    // ✨ NEW: Load TSR data for selected date
    const loadTsrDataForDate = React.useCallback(async (date) => {
        try {
            setIsLoadingDateData(true);
            const token = localStorage.getItem('token');
            const businessId = localStorage.getItem('businessId');
            const API_BASE = process.env.REACT_APP_API_URL;

            // Always start with a fresh form for the selected date.
            // Date status badges (Submitted / No Wastage) are still fetched below.
            // Nothing the old date said may stay up if this date's answer never comes.
            setDateStatus({ wastageRecorded: false, tsrSubmitted: false, dayStatus: 'not_started' });
            setError(null);
            setScanItems([]);
            setManualItems([]);
            setNcItems([]);
            setComplimentaryItemsMap({});
            setComplimentaryOverrides({});
            setTotalAmount('₹0');
            // A file was checked for the date it was uploaded under; none of it may
            // follow the page to another date.
            setSelectedFiles([]);
            setScannedSourceFiles([]);
            setDiscountedItems([]);
            setAutoDetectedDiscounts([]);
            setBillTotalDiscount(0);

            // Fetch existing TSR data
            const tsrResponse = await fetch(`${API_BASE}/tsr/get-by-date?date=${date}`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'x-business-id': businessId
                }
            });

            if (tsrResponse.ok) {
                const tsrResult = await tsrResponse.json();

                if (tsrResult.success && tsrResult.exists) {
                    // Keep form empty by design even when TSR already exists.
                    console.log(`ℹ️ TSR already submitted for ${date}; opening with empty form`);
                } else {
                    console.log(`📝 No existing TSR for ${date} - form cleared`);
                }
            }

            // Fetch date status (wastage + TSR status)
            const statusResponse = await fetch(`${API_BASE}/tsr/date-status?date=${date}`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'x-business-id': businessId
                }
            });

            if (statusResponse.ok) {
                const statusResult = await statusResponse.json();

                if (statusResult.success) {
                    setDateStatus(statusResult.data);

                    // Show wastage warning if not recorded and it can still be recorded
                    if (!statusResult.data.wastageRecorded && !statusResult.data.wastageClosed && !isOnboarding) {
                        setShowWastageWarningModal(true);
                    }
                }
            }

        } catch (err) {
            console.error('❌ Error loading TSR data for date:', err);
        } finally {
            setIsLoadingDateData(false);
        }
    }, [isOnboarding]);

    // Adopt the server's filing date when it arrives. ONE shot, and never over
    // the top of a deliberate choice or of work already on screen.
    React.useEffect(() => {
        if (!targetDate || filingDateAppliedRef.current) return;
        filingDateAppliedRef.current = true;          // one shot, whatever follows
        if (userPickedDateRef.current) return;        // the owner already chose
        if (scanItems.length > 0 || manualItems.length > 0 || selectedFiles.length > 0 || isScanning) return; // don't discard a scan
        setSelectedDate((prev) => (prev === targetDate ? prev : targetDate));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [targetDate]);

    // ✨ NEW: Handle date change
    const handleDateChange = React.useCallback(async (newDate) => {
        console.log(`📅 Date changed to: ${newDate}`);
        // A manual pick outranks the server suggestion from here on.
        userPickedDateRef.current = true;
        setSelectedDate(newDate);
        await loadTsrDataForDate(newDate);
    }, [loadTsrDataForDate]);

    const handleOpenDateCalendar = () => {
        if (isLoadingDateData || isDeducting || isScanning) return;

        const baseDate = parseISODateToLocal(selectedDate) || parseISODateToLocal(getISTDate()) || new Date();
        setCalendarViewMonth(new Date(baseDate.getFullYear(), baseDate.getMonth(), 1));
        setShowDateCalendar(true);
    };

    const handleCloseDateCalendar = () => {
        setShowDateCalendar(false);
    };

    // Every past date is open, for every business — only the future is fenced
    // off. The calendar used to floor itself at the opening count's tracking
    // start, which struck through whole months of real trading days.
    const handleNavigateCalendarMonth = (direction) => {
        const todayDate = parseISODateToLocal(getISTDate()) || new Date();
        const latestMonth = new Date(todayDate.getFullYear(), todayDate.getMonth(), 1);

        setCalendarViewMonth((previousMonth) => {
            const nextMonth = new Date(previousMonth.getFullYear(), previousMonth.getMonth() + direction, 1);
            if (nextMonth > latestMonth) {
                return latestMonth;
            }
            return nextMonth;
        });
    };

    const handleCalendarDateSelect = async (dateObj) => {
        const chosenDate = new Date(dateObj.getFullYear(), dateObj.getMonth(), dateObj.getDate());
        const todayDate = parseISODateToLocal(getISTDate()) || new Date();
        todayDate.setHours(0, 0, 0, 0);

        if (chosenDate > todayDate) return;

        setShowDateCalendar(false);
        const chosenDay = formatLocalDateToISO(chosenDate);
        if (chosenDay === selectedDate) return;
        const dirty = scanItems.length > 0 || manualItems.length > 0 || selectedFiles.length > 0;
        if (dirty && !window.confirm('Changing the date clears the items on this sheet. Continue?')) {
            return;
        }
        await handleDateChange(chosenDay);
    };

    // ✨ NEW: Load data when selected date changes
    React.useEffect(() => {
        if (selectedDate) {
            loadTsrDataForDate(selectedDate);
        }
    }, [selectedDate, loadTsrDataForDate]);

    React.useEffect(() => {
        checkTSRAccess();
        // Skip wastage check during onboarding
        if (!isOnboarding) {
            checkWastageStatus(); // Check wastage immediately on page load
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Load current business menu items once
    React.useEffect(() => {
        let mounted = true;
        (async () => {
            try {
                // ✅ Include inactive items to show all menu items in search
                const resp = await MenuService.getMenuItems('en', true);

                if (!mounted) return;

                if (resp && resp.success && resp.data && Array.isArray(resp.data) && resp.data.length > 0) {
                    console.log('🍽️ [Menu Load] Successfully loaded', resp.data.length, 'menu items');
                    setMenuList(resp.data);
                } else {
                    console.warn('🍽️ [Menu Load] Invalid response structure:', {
                        hasResp: !!resp,
                        hasSuccess: resp?.success,
                        hasData: !!resp?.data,
                        isArray: Array.isArray(resp?.data),
                        length: resp?.data?.length
                    });
                    setMenuList([]); // Set to empty array if no data
                }
            } catch (e) {
                console.error('🍽️ [Menu Load] Failed to load menu items:', e);
                setMenuList([]); // Set to empty array on error
            }
        })();
        return () => { mounted = false; };
    }, []);

    // Close hamburger menu when clicking outside
    React.useEffect(() => {
        const handleClickOutside = (event) => {
            if (hamburgerMenuRef.current && !hamburgerMenuRef.current.contains(event.target)) {
                setShowHamburgerMenu(false);
            }
        };

        if (showHamburgerMenu) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => {
                document.removeEventListener('mousedown', handleClickOutside);
            };
        }
    }, [showHamburgerMenu]);

    // Fetch complimentary items for all menu items in scanItems
    React.useEffect(() => {
        if (scanItems.length === 0) {
            return;
        }

        const fetchComplimentaryItems = async () => {
            try {
                const newMap = { ...complimentaryItemsMap };

                for (const sale of scanItems) {
                    const menuItemId = sale.apiData?.menuItemId;
                    if (!menuItemId || newMap[menuItemId]) continue;

                    try {
                        // Use the correct Features API endpoint for complimentary items
                        const response = await http.get(`/features/recipes/${menuItemId}/complimentary`);
                        if (response.success && response.complimentaryItems) {
                            newMap[menuItemId] = response.complimentaryItems;
                        }
                    } catch (err) {
                        console.warn(`Failed to fetch complimentary items for ${menuItemId}:`, err);
                    }
                }

                setComplimentaryItemsMap(newMap);
            } catch (error) {
                console.error('Error fetching complimentary items:', error);
            }
        };

        fetchComplimentaryItems();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [scanItems.length]);

    // Fetch complimentary items for manual entry items when menuItemId is assigned
    React.useEffect(() => {
        const manualMenuItemIds = manualItems
            .map(item => item.apiData?.menuItemId)
            .filter(id => id && !complimentaryItemsMap[id]);

        if (manualMenuItemIds.length === 0) return;

        const fetchManualComplimentary = async () => {
            try {
                const newMap = { ...complimentaryItemsMap };
                for (const menuItemId of manualMenuItemIds) {
                    if (newMap[menuItemId]) continue;
                    try {
                        const response = await http.get(`/features/recipes/${menuItemId}/complimentary`);
                        if (response.success && response.complimentaryItems) {
                            newMap[menuItemId] = response.complimentaryItems;
                        }
                    } catch (err) {
                        console.warn(`Failed to fetch complimentary items for manual item ${menuItemId}:`, err);
                    }
                }
                setComplimentaryItemsMap(newMap);
            } catch (error) {
                console.error('Error fetching manual complimentary items:', error);
            }
        };

        fetchManualComplimentary();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [manualItems.map(i => i.apiData?.menuItemId).join(',')]);

    // Close autocomplete suggestions when clicking outside
    React.useEffect(() => {
        const handleClickOutside = (e) => {
            // Support both legacy modal markup and new tab UI wrapper
            if (showManualSuggestions && !e.target.closest('.form-group') && !e.target.closest('.tsr-manual-form-group')) {
                setShowManualSuggestions(false);
            }
            // Check if click is outside NC suggestions dropdown
            if (showNcSuggestions && !e.target.closest('.tsr-nc-search-container') && !e.target.closest('.tsr-nc-suggestions')) {
                setShowNcSuggestions(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showManualSuggestions, showNcSuggestions]);

    // Restore TSR data when returning from itemmap
    React.useEffect(() => {
        const pendingTSRDataStr = localStorage.getItem('pendingTSRData');
        if (pendingTSRDataStr) {
            (async () => {
                try {
                    const data = JSON.parse(pendingTSRDataStr);
                    const restoredSelection = data.selectedMissingItems || {};

                    if (data.salesData) {
                        // Split into scan and manual if needed (for now, treat as scan items)
                        const scanData = data.salesData.filter(item => item.sourceFile !== 'Manual Entry');
                        const manualData = data.salesData.filter(item => item.sourceFile === 'Manual Entry');
                        setScanItems(scanData);
                        setManualItems(manualData);

                        // Restore selected entry tab when coming back from item map.
                        if (data.tsrEntryTab === 'manual' || data.tsrEntryTab === 'scan') {
                            setTsrEntryTab(data.tsrEntryTab);
                        } else if (manualData.length > 0 && scanData.length === 0) {
                            // Backward-compatible fallback for older pending data.
                            setTsrEntryTab('manual');
                        }
                    }
                    if (data.pendingSaleData) setPendingSaleData(data.pendingSaleData);

                    // ✅ Restore resolved ingredients set
                    if (data.resolvedIngredients && Array.isArray(data.resolvedIngredients)) {
                        const restored = new Set(data.resolvedIngredients);
                        setResolvedIngredients(restored);
                    }

                    // Calculate total
                    if (data.salesData && data.salesData.length > 0) {
                        const sum = data.salesData.reduce((acc, item) => acc + (item.apiData?.amount || 0), 0);
                        setTotalAmount(`₹${sum.toFixed(2)}`);
                    }

                    // ✅ RE-CHECK STOCK to get UPDATED missing ingredients after recipe changes
                    if (data.pendingSaleData && data.pendingSaleData.items) {
                        try {
                            const stockCheckResult = await SalesService.preCheckStock({
                                items: data.pendingSaleData.items
                            });


                            if (stockCheckResult.success && stockCheckResult.hasNegativeStock) {
                                // ✅ Filter out already resolved ingredients from the fresh check
                                const resolvedSet = data.resolvedIngredients ? new Set(data.resolvedIngredients) : new Set();
                                const freshMissing = (stockCheckResult.missingIngredients || []).filter(ing => {
                                    const isResolved = resolvedSet.has(ing.ingredientName.toLowerCase());
                                    if (isResolved) {
                                    }
                                    return !isResolved;
                                });


                                if (freshMissing.length > 0) {
                                    // Still have missing ingredients - show updated list
                                    setMissingIngredients(freshMissing);
                                    setSelectedMissingItems(getFilteredSelection(freshMissing, restoredSelection));
                                    setShowMissingModal(true);
                                    setError({
                                        type: 'info',
                                        message: `✅ Returned from recipe editor. ${freshMissing.length} ingredient(s) still missing.`
                                    });
                                } else {
                                    // All ingredients now available - show success
                                    setMissingIngredients([]);
                                    setSelectedMissingItems({});
                                    setShowMissingModal(false);
                                    setError({
                                        type: 'success',
                                        message: '✅ All ingredients resolved! You can now confirm the sale.'
                                    });
                                }
                            } else {
                                // All ingredients now available - show success
                                setMissingIngredients([]);
                                setSelectedMissingItems({});
                                setShowMissingModal(false);
                                setError({
                                    type: 'success',
                                    message: '✅ All ingredients resolved! You can now confirm the sale.'
                                });
                            }
                        } catch (err) {
                            // Fallback to old data if API fails
                            if (data.missingIngredients) {
                                setMissingIngredients(data.missingIngredients);
                                setSelectedMissingItems(getFilteredSelection(data.missingIngredients, restoredSelection));
                            }
                            if (data.missingIngredients && data.missingIngredients.length > 0) {
                                setShowMissingModal(true);
                            } else {
                                setSelectedMissingItems({});
                            }
                            setError({
                                type: 'warning',
                                message: '⚠️ Returned from recipe editor. Could not verify stock status.'
                            });
                        }
                    }

                    // Clear localStorage after restoring
                    localStorage.removeItem('pendingTSRData');

                } catch (err) {
                }
            })();
        }
    }, []);




    // Function to handle bill scanning (camera)
    const handleScanBill = () => {
        document.getElementById('bill-scan-input').click();
    };

    // Function to handle camera click (direct from scan card)
    const handleCameraClick = () => {
        document.getElementById('bill-scan-input').click();
    };


    // Function to handle gallery click (direct from upload card)
    const handleGalleryClick = () => {
        document.getElementById('gallery-upload-input').click();
    };

    // Function to add a new empty manual item card (Stock-In-like)
    const handleAddManualItem = () => {
        // Create new empty item entry with correct field names
        const newEntry = {
            item: '',
            quantity: 1,
            amount: '',
            sourceFile: 'Manual Entry',
            apiData: {
                item_name: '',
                quantity: 1,
                rate: 0,
                unitPrice: 0,
                amount: 0,
                parserName: '',
                menuItemId: null
            },
            isEditing: true // Flag to identify editable items
        };

        // Add to manual items
        setManualItems(prev => [...prev, newEntry]);
        return manualItems.length;
    };

    const focusManualInput = (index, field) => {
        requestAnimationFrame(() => {
            manualInputRefs.current[`${index}-${field}`]?.focus();
        });
    };

    // Function to update a manual entry item by index
    const handleUpdateManualItem = (index, field, value) => {
        setManualItems(prev => {
            const updated = [...prev];
            const item = { ...updated[index] };

            if (field === 'itemName') {
                item.item = value;
                // A new name is a new search: a "Not in Menu" mark belonged to the old one.
                item.apiData = { ...item.apiData, item_name: value, parserName: value, notInMenu: false };
            } else if (field === 'notInMenu') {
                // Marked from the dropdown's last option (spec 2026-10-26-tsr-add-as-not-in-menu):
                // no dish; the owner types the price per plate, amount = price × plates.
                item.apiData = { ...item.apiData, notInMenu: true, menuItemId: null };
                const plates = parseFloat(item.quantity) || 0;
                const price = parseFloat(item.apiData.unitPrice) || 0;
                item.apiData.amount = price * plates;
                item.amount = price > 0 ? `₹${(price * plates).toFixed(2)}` : '';
            } else if (field === 'quantity') {
                // A Not in Menu row takes half plates and keeps what is being typed ("0",
                // "0.") until it is a number — Confirm refuses one left at no plates. A menu
                // row keeps whole plates, as before.
                const nim = item.apiData?.notInMenu === true;
                const qty = value === '' ? '' : (nim ? value : (parseInt(value) || 1));
                const plates = nim ? (parseFloat(value) || 0) : qty;
                item.quantity = qty;
                item.apiData = { ...item.apiData, quantity: plates };

                // Recalculate amount if unit price exists
                if (item.apiData.unitPrice > 0) {
                    const numQty = qty === '' ? 0 : plates;
                    const newAmount = numQty * item.apiData.unitPrice;
                    item.apiData.amount = newAmount;
                    item.apiData.rate = item.apiData.unitPrice;
                    item.amount = `₹${newAmount.toFixed(2)}`;
                }
            } else if (field === 'unitPrice') {
                const unitPrice = parseFloat(value) || 0;
                item.apiData = { ...item.apiData, unitPrice, rate: unitPrice };

                // Recalculate amount
                const newAmount = item.quantity * unitPrice;
                item.apiData.amount = newAmount;
                item.amount = `₹${newAmount.toFixed(2)}`;
            } else if (field === 'amount') {
                const amt = parseFloat(value) || 0;
                item.amount = value ? `₹${amt.toFixed(2)}` : '';
                item.apiData = { ...item.apiData, amount: amt };

                // Don't recalculate unit price if item is from menu (has menuItemId)
                // This preserves the actual menu item price
                if (!item.apiData.menuItemId && item.quantity > 0) {
                    const newUnitPrice = amt / item.quantity;
                    item.apiData.rate = newUnitPrice;
                    item.apiData.unitPrice = newUnitPrice;
                }
            } else if (field === 'menuItemId') {
                item.apiData = { ...item.apiData, menuItemId: value };
            }

            updated[index] = item;

            // Update total (combine scan + manual)
            const manualSum = updated.reduce((acc, it) => acc + (it.apiData?.amount || 0), 0);
            const scanSum = scanItems.reduce((acc, it) => acc + (it.apiData?.amount || 0), 0);
            setTotalAmount(`₹${(scanSum + manualSum).toFixed(2)}`);

            return updated;
        });
    };

    const selectManualSuggestion = (itemIndex, suggestionIndex) => {
        const menuItem = manualEntrySuggestions[suggestionIndex];
        if (!menuItem) return;

        const itemName = menuItem.name || menuItem.item_name || menuItem.menu_item_name || 'Unknown';
        const itemId = menuItem.menu_item_id || menuItem.id;
        const unitPrice = parseFloat(menuItem.price || menuItem.item_price || menuItem.menu_item_price || 0);

        handleUpdateManualItem(itemIndex, 'itemName', itemName);
        handleUpdateManualItem(itemIndex, 'unitPrice', unitPrice);
        handleUpdateManualItem(itemIndex, 'menuItemId', itemId);
        setShowManualSuggestions(false);
        setManualEntrySuggestions([]);
        setManualSuggestionIndex(-1);
    };

    // The dropdown's "Add as Not in Menu" row: keep the typed name, mark the row.
    const markManualNotInMenu = (itemIndex) => {
        handleUpdateManualItem(itemIndex, 'notInMenu', true);
        setShowManualSuggestions(false);
        setManualEntrySuggestions([]);
        setManualSuggestionIndex(-1);
        focusManualInput(itemIndex, 'quantity');
    };

    // =================== NC (No Charge) HANDLERS ===================
    // Handle NC item name change with autocomplete
    const handleNcItemNameChange = (e) => {
        const searchValue = e.target.value;
        setNcForm(prev => ({ ...prev, itemName: searchValue, price: '' }));
        setSelectedNcMenuItemId(null);
        setNcModalError('');

        if (searchValue.trim()) {
            const filtered = getTopMenuSuggestions(searchValue);

            setNcSuggestions(filtered);
            setShowNcSuggestions(true);
        } else {
            setNcSuggestions([]);
            setShowNcSuggestions(false);
        }
    };

    // Select an NC item from suggestions
    const handleNcItemSelect = (item) => {
        const itemId = item.menu_item_id || item.id;
        const itemName = getMenuItemName(item);
        const itemPrice = parseFloat(item.price || item.item_price || item.menu_item_price || 0) || 0;
        setNcForm(prev => {
            const newForm = { ...prev, itemName, price: itemPrice };
            return newForm;
        });
        setSelectedNcMenuItemId(itemId);
        setShowNcSuggestions(false);
        setNcSuggestions([]);
        setNcModalError('');
    };

    // Submit NC item
    const handleNcSubmit = () => {
        if (!ncForm.itemName.trim()) {
            setNcModalError('❌ Please enter an item name');
            return;
        }

        if (!selectedNcMenuItemId) {
            setNcModalError('❌ Please select a valid menu item from the search results above');
            return;
        }

        const quantity = parseInt(ncForm.quantity);
        if (isNaN(quantity) || quantity <= 0) {
            setNcModalError('❌ Please enter a valid quantity (minimum 1)');
            return;
        }

        const price = parseFloat(ncForm.price) || 0;

        // Check if item already exists
        const existingItemIndex = ncItems.findIndex(item => item.menuItemId === selectedNcMenuItemId);

        if (existingItemIndex !== -1) {
            // Item exists, update quantity.
            //
            // Compute the new total BEFORE the setter so the toast cannot report
            // a stale figure: the message previously read `ncItems` from the
            // render closure, which is the pre-update array. It happened to add
            // up today, but any change to the updater (clamping, merging,
            // de-duplication) would silently desynchronise the number shown
            // from the number stored.
            const newTotalQty = ncItems[existingItemIndex].quantity + quantity;
            setNcItems(prev => {
                const updated = [...prev];
                updated[existingItemIndex] = {
                    ...updated[existingItemIndex],
                    quantity: updated[existingItemIndex].quantity + quantity
                };
                return updated;
            });
            setError({ type: 'success', message: `Updated "${ncForm.itemName}" quantity to ${newTotalQty}` });
        } else {
            // Item doesn't exist, add new
            const newNcItem = {
                menuItemId: selectedNcMenuItemId,
                itemName: ncForm.itemName,
                quantity: quantity,
                price: price
            };
            setNcItems(prev => [...prev, newNcItem]);
            setError({ type: 'success', message: `Added "${ncForm.itemName}" (${quantity} qty) to NC list` });
        }

        setShowNcModal(false);
        setNcForm({ itemName: '', quantity: 1, price: '' });
        setSelectedNcMenuItemId(null);
        setNcSuggestions([]);
        setShowNcSuggestions(false);
        setNcModalError('');
    };

    // Remove an NC item
    const handleRemoveNcItem = (index) => {
        setNcItems(prev => prev.filter((_, i) => i !== index));
        setError({ type: 'success', message: 'NC item removed' });
    };

    // Update notes for a specific NC item
    const handleUpdateNcItemNotes = (index, notes) => {
        setNcItems(prev => {
            const updated = [...prev];
            updated[index] = {
                ...updated[index],
                notes: notes
            };
            return updated;
        });
    };

    // =================== DISCOUNT HANDLERS ===================
    // Handle discount item name change with autocomplete
    const handleDiscountItemNameChange = (e) => {
        const searchValue = e.target.value;
        setDiscountForm(prev => ({ ...prev, itemName: searchValue, menuItemId: null, originalPrice: 0 }));

        if (searchValue.trim()) {
            const filtered = getTopMenuSuggestions(searchValue);

            setDiscountSuggestions(filtered);
            setShowDiscountSuggestions(true);
        } else {
            setDiscountSuggestions([]);
            setShowDiscountSuggestions(false);
        }
    };

    // Select a discount item from suggestions
    const handleDiscountItemSelect = (item) => {
        const itemId = item.menu_item_id || item.id;
        const itemName = getMenuItemName(item);
        const itemPrice = parseFloat(item.price || item.item_price || item.menu_item_price || 0) || 0;
        setDiscountForm(prev => ({
            ...prev,
            itemName,
            menuItemId: itemId,
            originalPrice: itemPrice
        }));
        setShowDiscountSuggestions(false);
        setDiscountSuggestions([]);
    };

    // Submit discount
    const handleDiscountSubmit = () => {
        if (!discountForm.itemName.trim()) {
            setError({ type: 'error', message: '❌ Please enter an item name' });
            return;
        }

        if (!discountForm.menuItemId) {
            setError({ type: 'error', message: '❌ Please select a valid menu item from the search results' });
            return;
        }

        const discountPercentage = parseFloat(discountForm.discountPercentage);
        if (isNaN(discountPercentage) || discountPercentage < 0 || discountPercentage > 100) {
            setError({ type: 'error', message: '❌ Please enter a valid discount percentage (0-100)' });
            return;
        }

        const originalPrice = parseFloat(discountForm.originalPrice) || 0;
        const discountedPrice = originalPrice * (1 - discountPercentage / 100);

        // Check if item already has a discount
        const existingItemIndex = discountedItems.findIndex(item => item.menuItemId === discountForm.menuItemId);

        if (existingItemIndex !== -1) {
            // Update existing discount
            setDiscountedItems(prev => {
                const updated = [...prev];
                updated[existingItemIndex] = {
                    ...updated[existingItemIndex],
                    discountPercentage: discountPercentage,
                    discountedPrice: discountedPrice
                };
                return updated;
            });
            setError({ type: 'success', message: `Updated discount for "${discountForm.itemName}" to ${discountPercentage}%` });
        } else {
            // Add new discount
            const newDiscountItem = {
                menuItemId: discountForm.menuItemId,
                itemName: discountForm.itemName,
                originalPrice: originalPrice,
                discountPercentage: discountPercentage,
                discountedPrice: discountedPrice
            };
            setDiscountedItems(prev => [...prev, newDiscountItem]);
            setError({ type: 'success', message: `Added ${discountPercentage}% discount for "${discountForm.itemName}"` });
        }

        setShowDiscountModal(false);
        setDiscountForm({ itemName: '', menuItemId: null, originalPrice: 0, discountPercentage: '' });
        setDiscountSuggestions([]);
        setShowDiscountSuggestions(false);
    };

    // Remove a discount
    const handleRemoveDiscount = (index) => {
        const removedItem = discountedItems[index];
        setDiscountedItems(prev => prev.filter((_, i) => i !== index));
        setError({ type: 'success', message: `Removed discount for "${removedItem.itemName}"` });
    };


    // Add an NC item from the NC Items Page
    const handleAddNcItem = (newItem) => {
        // Check if item already exists
        const existingItemIndex = ncItems.findIndex(item => item.menuItemId === newItem.menuItemId);

        if (existingItemIndex !== -1) {
            // Item exists, update quantity
            setNcItems(prev => {
                const updated = [...prev];
                updated[existingItemIndex] = {
                    ...updated[existingItemIndex],
                    quantity: updated[existingItemIndex].quantity + newItem.quantity
                };
                return updated;
            });
        } else {
            // Item doesn't exist, add new
            setNcItems(prev => [...prev, newItem]);
        }
    };

    // Confirm and immediately record NC items as wastage
    const handleConfirmNcItems = async () => {
        if (ncItems.length === 0) {
            setError({ type: 'error', message: 'No NC items to record' });
            setShowNcPage(false);
            return;
        }

        const reportDateForNc = selectedDate || targetDate || getISTDate();

        try {
            setIsDeducting(true);
            setProcessingMessage('Recording NC items...');

            const businessId = Number(localStorage.getItem('businessId'));

            const ncReason = await resolveNcWastageReason(http, businessId);
            const wasteReasonId = ncReason.reason_id;

            // Record each NC item as wastage
            let ncSuccessCount = 0;
            let ncErrorCount = 0;
            const ncRecordedItems = [];

            for (const ncItem of ncItems) {
                try {
                    const ncNotes = ncItem.notes || ncItem.reason || '';
                    await http.post('/wastage', {
                        item_id: ncItem.menuItemId,
                        item_type: 'MenuItem',
                        quantity: ncItem.quantity,
                        waste_reason_id: wasteReasonId,
                        reason_type: 'Waste',
                        notes: ncNotes,
                        deducted_date: reportDateForNc,
                        // This wastage record is the ONLY place the NC portion can
                        // leave stock: this page records NC with no sale attached,
                        // so nothing else ever deducts these ingredients.
                        deduct_stock: true
                    });
                    ncSuccessCount++;
                    ncRecordedItems.push(`${ncItem.itemName} (${ncItem.quantity} qty)`);
                } catch (ncError) {
                    ncErrorCount++;
                    console.error(`❌ Failed to record NC wastage for ${ncItem.itemName}:`, ncError);
                }
            }

            if (ncSuccessCount > 0) {
                setNcSuccessMessage(ncRecordedItems);
                setShowNcSuccessModal(true);
                // Clear NC items after successful recording
                setNcItems([]);
            }

            if (ncErrorCount > 0) {
                setError({
                    type: 'error',
                    message: `Failed to record ${ncErrorCount} NC item(s). Please try again.`
                });
            }

            // Close NC page and return to TSR
            setShowNcPage(false);

        } catch (error) {
            console.error('❌ Error recording NC items:', error);
            setError({
                type: 'error',
                message: 'Failed to record NC items. Please try again.'
            });
        } finally {
            setIsDeducting(false);
            setProcessingMessage('');
        }
    };

    const addSalesReportFiles = (incomingFiles) => {
        const files = Array.from(incomingFiles || []);
        if (files.length === 0) return;

        // Validate file types
        const invalidFiles = files.filter(file => {
            const isImage = file.type.startsWith('image/');
            const isPDF = file.type === 'application/pdf';
            const isExcel = file.type === 'application/vnd.ms-excel' ||
                file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
                file.type === 'text/csv' ||
                file.name.endsWith('.xls') ||
                file.name.endsWith('.xlsx') ||
                file.name.endsWith('.csv');
            return !isImage && !isPDF && !isExcel;
        });

        if (invalidFiles.length > 0) {
            setError(`Invalid file types detected. Please select images (JPG, PNG), PDF, or Excel/CSV files. ${invalidFiles.length} file(s) were invalid.`);
            return;
        }

        // Validate file sizes (max 50MB each)
        const oversizedFiles = files.filter(file => file.size > 50 * 1024 * 1024);
        if (oversizedFiles.length > 0) {
            setError(`Some files are too large. Maximum size is 50MB per file. ${oversizedFiles.length} file(s) exceeded this limit.`);
            return;
        }

        setError(null);
        setSelectedFiles(prev => [...prev, ...files]);

        // Don't auto-process - wait for user to click "Scan All Bills" button
    };

    // Function to handle file selection and OCR processing
    const handleFileSelect = async (event) => {
        addSalesReportFiles(event.target.files);
        event.target.value = '';
    };

    const handleUploadDragOver = (event) => {
        event.preventDefault();
        if (!isScanning) {
            event.dataTransfer.dropEffect = 'copy';
            setIsUploadDragOver(true);
        }
    };

    const handleUploadDragLeave = (event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) {
            setIsUploadDragOver(false);
        }
    };

    const handleUploadDrop = (event) => {
        event.preventDefault();
        setIsUploadDragOver(false);
        if (isScanning) return;

        addSalesReportFiles(event.dataTransfer.files);
    };

    const handleCancelScan = () => {
        cancelScanRef.current = true;
        setIsScanning(false);
        setFileProcessingProgress({ current: 0, total: 0 });
        setError('Processing cancelled.');
    };

    // Online Order Mode: try the structured Swiggy Order Report parser for an Excel/CSV file.
    // Returns true if handled (review modal opened), false to fall back to normal OCR flow.
    const tryParseSwiggyOrderReport = async (file) => {
        // Uses a dedicated lightweight indicator (NOT the OCR scanning/progress UI,
        // which would flash a misleading "0%" for a single structured parse).
        setOrderReportParsing(true);
        try {
            const formData = new FormData();
            formData.append('file', file);
            // Must use postFormData — http.post JSON-stringifies the body (breaks multipart)
            const resp = await http.postFormData('/online-sales/upload', formData);
            const data = resp.data || resp;
            if (data && data.success && data.is_order_report) {
                let menu = [];
                try {
                    const mr = await MenuService.getMenuItems('en', true);
                    if (mr?.success && Array.isArray(mr.data)) menu = mr.data;
                } catch (_) { /* non-fatal */ }
                setOrderReportMenu(menu);
                setOrderReportData(data);
                setShowOrderReportModal(true);
                setSelectedFiles([]);
                setOrderReportParsing(false);
                return true;
            }
        } catch (err) {
            console.warn('Swiggy Order Report parse failed, falling back to OCR:', err?.message || err);
        }
        setOrderReportParsing(false);
        return false;
    };

    // Function to process all selected files
    /**
     * Scan one file, pausing if the server says it has been filed from before.
     *
     * The loop below is sequential and had no way to stop half way, so the
     * decision is awaited on a promise rather than unwound through state: the
     * loop simply blocks here until the owner picks, then carries on with the
     * next file. Returns { skipped: true } when they choose to skip.
     */
    // `outlet`: one outlet of a POS outlet report, chosen in the outlet picker.
    // `confirmReupload`: the owner already accepted this file's re-use once.
    const scanOneFile = async (file, { outlet = null, confirmReupload = false } = {}) => {
        // A file that states its day (Sales By Items, the outlet report) is
        // refused unless it is the TSR day being filed — the server decides, the
        // popup says why.
        const dateRefusal = (err) => (FILE_DATE_REFUSAL_TITLES[err?.data?.code]
            ? { fileDateRefused: { code: err.data.code, error: err.data.error, details: err.data.details || null, fileName: file.name } }
            : null);
        const tsrDate = typeof selectedDate === 'string'
            ? selectedDate.slice(0, 10)
            : new Date(selectedDate).toLocaleDateString('en-CA');
        const extra = { tsrDate, ...(outlet ? { outlet } : {}) };
        try {
            return await OCRService.processSalesReportV2(file, confirmReupload ? { ...extra, confirmReupload: true } : extra);
        } catch (err) {
            const refused = dateRefusal(err);
            if (refused) return refused;
            if (err?.data?.code !== 'DUPLICATE_TSR_FILE') throw err;

            const choice = await new Promise((resolve) => {
                reuploadDecisionRef.current = resolve;
                setFileReuseWarning({ ...err.data, fileName: file.name });
            });

            setFileReuseWarning(null);
            reuploadDecisionRef.current = null;
            if (choice !== 'use') return { skipped: true };

            // Same request, guard waived for this one file only. Marked, so an
            // outlet report's next outlet is not asked about the same file again.
            try {
                const answer = await OCRService.processSalesReportV2(file, { ...extra, confirmReupload: true });
                return answer && typeof answer === 'object' ? { ...answer, reuseConfirmed: true } : answer;
            } catch (retryErr) {
                const refused = dateRefusal(retryErr);
                if (refused) return refused;
                throw retryErr;
            }
        }
    };

    // `pick` reads ONE file again on its own: a POS outlet report with the outlet
    // the owner chose ({ file, outlet, confirmReupload }), or with no outlet to
    // bring the outlet picker back for the next one. A button click passes its
    // event here, which is not a pick.
    const processSalesReports = async (pick = null) => {
        const filePick = pick && typeof Blob !== 'undefined' && pick.file instanceof Blob ? pick : null;
        const files = filePick ? [filePick.file] : selectedFiles;
        if (files.length === 0) {
            setError('Please select at least one sales report file (image, PDF, or Excel/CSV)');
            return;
        }

        // In Online Order Mode, an Excel/CSV upload is treated as a platform report
        // (Swiggy Order Report) and parsed here via the structured parser.
        if (isOnlineOrderMode) {
            const isExcelCsv = (f) => /\.(xlsx?|csv)$/i.test(f.name || '') ||
                /(spreadsheet|excel|csv)/i.test(f.type || '');
            const excelFile = files.find(isExcelCsv);
            if (excelFile) {
                setError(null);
                const handled = await tryParseSwiggyOrderReport(excelFile);
                if (handled) return; // review modal opened; skip OCR flow
            }
        }

        cancelScanRef.current = false;
        setIsScanning(true);
        setError(null);
        setScanItems([]); // Only clear scan items, keep manual items
        // A fresh scan replaces the file identities too, or a file dropped from
        // the selection would still be recorded against the next submission.
        setScannedSourceFiles([]);
        // Update total to only show manual items during scan
        const manualSum = manualItems.reduce((acc, item) => acc + (item.apiData?.amount || 0), 0);
        setTotalAmount(`₹${manualSum.toFixed(2)}`);
        setFileProcessingProgress({ current: 0, total: files.length });

        try {

            const allSalesData = [];
            // The outlet whose lines this scan loaded (an outlet report, one outlet).
            let loadedOutlet = null;
            const filesWithNoDate = [];

            // Process each file sequentially
            for (let i = 0; i < files.length; i++) {
                if (cancelScanRef.current) {
                    break;
                }
                const file = files[i];
                // Show progress at start of file (e.g., 0.0, 1.0, 2.0)
                setFileProcessingProgress({ current: i + 0.1, total: files.length });

                // Use the dedicated TSR Parser V2 endpoint. Pauses here if this
                // file has already been filed from; skipping it leaves the rest
                // of the batch untouched.
                const result = await scanOneFile(file, filePick
                    ? { outlet: filePick.outlet || null, confirmReupload: !!filePick.confirmReupload }
                    : {});
                if (result?.skipped) {
                    console.log(`⏭️ [TSR] Skipped re-used file: ${file.name}`);
                    continue;
                }

                // A POS outlet report with no outlet chosen: stop here, as the
                // day-wise hand-off does, and let the owner pick ONE outlet. It
                // then loads like any other file and is filed the normal way.
                if (result?.is_outlet_report) {
                    setOutletPicker({
                        file,
                        picker: result.outlet_picker,
                        confirmReupload: !!(result.reuseConfirmed || filePick?.confirmReupload),
                    });
                    setSelectedFiles([]);
                    setIsScanning(false);
                    setFileProcessingProgress({ current: 0, total: 0 });
                    return;
                }

                if (result?.fileDateRefused) {
                    setFileDateRefusal(result.fileDateRefused);
                    setSelectedFiles([]);
                    // An earlier file of this batch loaded nothing either.
                    setScannedSourceFiles([]);
                    setIsScanning(false);
                    setFileProcessingProgress({ current: 0, total: 0 });
                    return;
                }

                // A multi-date export cannot join the single-date flow: its rows
                // span many days, and the rest of this function books everything
                // against ONE reportDate. Hand it to the day-wise importer instead.
                if (result.is_day_wise) {
                    setDayWiseReport(result);
                    setSelectedFiles([]);
                    setIsScanning(false);
                    setFileProcessingProgress({ current: 0, total: 0 });
                    return;
                }

                // Handle V2 OCR response format
                if (result.success) {
                    const parsedData = result.parsedData || result.salesData || {};
                    const items = parsedData.items || [];

                    if (items.length === 0) {
                        continue;
                    }

                    // Remember WHICH bytes produced these rows. Recorded only for
                    // files that actually contributed items — a file that parsed to
                    // nothing has fed no sale and must stay re-uploadable.
                    if (result.sourceSha) {
                        setScannedSourceFiles((prev) => (
                            prev.some((f) => f.sha === result.sourceSha)
                                ? prev
                                // checkedFor: the date the server checked this file against.
                                : [...prev, { sha: result.sourceSha, name: file.name, size: file.size, checkedFor: result.metadata?.fileDate?.checkedFor || null }]
                        ));
                    }

                    if (result.metadata?.fileDate?.status === 'none') {
                        filesWithNoDate.push(file.name);
                    }

                    // One outlet of an outlet report: its sale will carry the outlet.
                    if (result.metadata?.outlet) {
                        loadedOutlet = {
                            name: result.metadata.outlet,
                            report_date: result.metadata.report_date || null,
                            file,
                            confirmReupload: !!(result.reuseConfirmed || filePick?.confirmReupload),
                        };
                    }

                    // Add source file information to each item
                    const itemsWithSource = items.map(item => ({
                        ...item,
                        sourceFile: file.name,
                        sourceIndex: i + 1,
                        _platformSource: result.metadata?.platform || parsedData.platform || null
                    }));

                    // Removed auto-enable Online Order Mode when a platform file is detected
                    // per user request. User must select it manually.
                    const detectedPlatform = result.metadata?.platform || parsedData.platform || null;

                    allSalesData.push(...itemsWithSource);
                } else {
                }

                setFileProcessingProgress({ current: i + 1, total: files.length });
            }

            if (cancelScanRef.current) {
                setIsScanning(false);
                setFileProcessingProgress({ current: 0, total: 0 });
                setError('Processing cancelled.');
                return;
            }

            if (allSalesData.length === 0) {
                throw new Error(
                    'Processing failed, please check the image format.'
                );
            }


            // findMenuItemById is defined at component scope (used by the match-
            // confirmation handler and modal too).

            const findMenuItem = (itemName) => {
                const normalize = (s) => (s || '').toString().toLowerCase().replace(/[^a-z0-9\s]/g, ' ').replace(/\s+/g, ' ').trim();
                const target = normalize(itemName);
                if (!target || menuList.length === 0) return null;

                // Exact normalized match
                const exact = menuList.find(mi => normalize(mi.name) === target);
                if (exact) return exact;

                // Check for Half/Full variations
                // If scanned item is "Chicken 65 - Half", try matching "Chicken 65 (Half)" or "Chicken 65 Half"
                const halfFullVariations = [
                    target,
                    target.replace(/\s*-\s*half\s*$/i, ' half'),
                    target.replace(/\s*-\s*half\s*$/i, ' (half)'),
                    target.replace(/\s*-\s*full\s*$/i, ' full'),
                    target.replace(/\s*-\s*full\s*$/i, ' (full)'),
                    target.replace(/\s*half\s*$/i, '- half'),
                    target.replace(/\s*full\s*$/i, '- full'),
                    target.replace(/\s*\(half\)\s*$/i, ' half'),
                    target.replace(/\s*\(full\)\s*$/i, ' full')
                ];

                for (const variation of halfFullVariations) {
                    const match = menuList.find(mi => normalize(mi.name) === variation);
                    if (match) return match;
                }

                // Partial inclusion either way
                const partial = menuList.find(mi => {
                    const n = normalize(mi.name);
                    return n.includes(target) || target.includes(n);
                });
                if (partial) return partial;

                // Token overlap fallback
                const tset = new Set(target.split(' '));
                let best = null; let bestScore = 0;
                for (const mi of menuList) {
                    const mset = new Set(normalize(mi.name).split(' '));
                    let common = 0; tset.forEach(t => { if (mset.has(t)) common++; });
                    const score = common / Math.max(1, mset.size);
                    if (score > bestScore) { best = mi; bestScore = score; }
                }
                return bestScore >= 0.6 ? best : null;
            };


            // Track price mismatches, auto-detected discounts, and NC items
            const detectedPriceMismatches = [];
            const detectedDiscounts = [];
            const detectedNcItems = [];
            // A3: NC signals on rows the backend could NOT match to a menu item.
            // Every NC layer requires a menu match, so these used to vanish
            // silently — the units were non-chargeable, nobody was told, and no
            // wastage was ever recorded for them.
            const detectedNcUnmatched = [];

            // Check if ANY item came from a platform file (Zomato/Swiggy)
            // If so, skip price mismatch detection entirely — platform prices are
            // inherently different from DB prices due to discount distribution
            const isPlatformUpload = allSalesData.some(item => !!item._platformSource);

            // Transform OCR data to match our component format
            const transformedData = allSalesData.map((item, index) => {

                // Clean and normalize item name
                // Parser V2 returns: item_name, total_quantity, total_amount, unit_price, gross_amount, discount_amount, net_amount
                const rawItemName = item.item_name || item.name || 'Unknown Item';
                const itemName = rawItemName.trim();
                const sourceCompositeName = (item.source_composite_name || '').toString().trim();
                const isComboDerivedItem = item.split_from_composite === true || /\bcombo\b/i.test(sourceCompositeName);

                // Extract numeric values safely
                let quantity = 1;
                const qtyValue = item.total_quantity || item.quantity || 1;
                if (typeof qtyValue === 'string') {
                    quantity = parseInt(qtyValue.match(/\d+/)?.[0]) || 1;
                } else if (typeof qtyValue === 'number') {
                    quantity = qtyValue;
                }

                let amount = 0;
                const amtValue = item.total_amount || item.amount || item.final_price || item.total_price || 0;
                if (typeof amtValue === 'string') {
                    amount = parseFloat(amtValue.replace(/[₹,]/g, '')) || 0;
                } else if (typeof amtValue === 'number') {
                    amount = amtValue;
                }

                let rate = amount / quantity;
                const rateValue = item.unit_price || item.rate;
                if (rateValue) {
                    if (typeof rateValue === 'string') {
                        rate = parseFloat(rateValue.replace(/[₹,]/g, '')) || rate;
                    } else if (typeof rateValue === 'number') {
                        rate = rateValue;
                    }
                }

                // Extract discount data from parsed bill
                const billDiscountAmount = parseFloat(item.discount_amount) || 0;
                const billNetAmount = parseFloat(item.net_amount) || parseFloat(item.total_amount) || amount;
                const billGrossAmount = parseFloat(item.gross_amount) || 0;

                // ═══ MENU MATCHING: Trust the backend's match decision ═══
                // The backend runs fuzzy matching (Levenshtein + Jaccard) with a 0.72 threshold.
                // If it says mapped_menu_item_id=null, the item is UNMATCHED — do NOT override with a frontend fallback.
                const backendMatchedId = item.mapped_menu_item_id || null;
                const isBackendMatched = item.is_matched === true;
                const menuItem = backendMatchedId ? findMenuItemById(backendMatchedId) : null;
                const menuItemId = menuItem?.menu_item_id || menuItem?.id || backendMatchedId || null;
                // A line the server set aside as a remembered Skip ("Packing Charges"):
                // no dish, and nothing to ask about, not even its NC plates.
                const setAsideBySkip = !menuItemId && (item.remembered_skip === true || item.match_method === 'remembered_skip');

                // Log matching decision for debugging
                if (!isBackendMatched) {
                    console.log(`⚠️ [TSR Transform] "${itemName}" → UNMATCHED by backend (no menuItemId assigned)`);
                } else {
                    console.log(`✅ [TSR Transform] "${itemName}" → matched to menu #${menuItemId} ("${item.matched_name || menuItem?.name || '?'}")`);
                }

                // Get DB price: prefer db_price from backend, fall back to frontend menuList
                const databasePrice = item.db_price != null
                    ? parseFloat(item.db_price)
                    : (menuItem && menuItem.price ? parseFloat(menuItem.price) : null);

                // ✅ NC DETECTION — only what the file states, never guessed from prices
                // Layer -1: Explicit NC columns/rows from the POS (nc_explicit flag) → exact NC data
                // Layer 0: A ₹0 line of a menu dish (nc_zero_amount flag) → given free
                let ncDetected = false;

                // ── A3: NC on an UNMATCHED row ──
                // Detected from bill data alone (no menu match required, because
                // there isn't one). Collected here so the Unmapped modal can say
                // these NC units exist; they cannot be recorded until the item is
                // mapped, but they will no longer disappear without a word.
                if (!menuItemId && !setAsideBySkip) {
                    const unmatchedNcQty = (item.nc_explicit && item.nc_quantity > 0)
                        ? parseFloat(item.nc_quantity)
                        : (item.nc_zero_amount && quantity > 0 ? quantity : 0);
                    if (unmatchedNcQty > 0) {
                        detectedNcUnmatched.push({
                            itemName: itemName,
                            nameKey: item.name_key || lineNameKey({ itemName }),
                            ncQty: unmatchedNcQty,
                            ncAmount: parseFloat(item.nc_amount) || 0,
                            reason: 'nc_unmatched'
                        });
                        console.log(`⚠️ [NC Unmatched] "${itemName}": ${unmatchedNcQty} NC unit(s) on an unmapped item`);
                    }
                }

                // ── LAYER -1: Explicit NC from POS (Explorex-style with NC Quantity/NC Amount columns) ──
                // The POS already told us exactly how many items were NC — trust it completely.
                if (item.nc_explicit && item.nc_quantity > 0 && menuItem && menuItemId) {
                    // A5: bill data outranks the current menu price for a historical
                    // document. The POS stated what these NC units were worth ON THAT
                    // DAY; today's menu price may have changed since, and using it
                    // would silently restate the value of a past bill.
                    // Layers 1/2 keep the DB price - they carry no independent bill
                    // NC value to prefer.
                    const billNcUnitPrice = (item.nc_amount > 0 && item.nc_quantity > 0)
                        ? Math.round((item.nc_amount / item.nc_quantity) * 100) / 100
                        : null;
                    const ncPrice = billNcUnitPrice != null
                        ? billNcUnitPrice
                        : (databasePrice != null && databasePrice > 0 ? databasePrice : 0);
                    // Paid plates as the POS states them. A file with an NC column but no
                    // paid-quantity column reads 0 there while its quantity includes the
                    // free plates: its paid plates are then the rest (it still took money).
                    const statedPaidQty = Number(item.paid_quantity) || 0;
                    const paidQtyFromPOS = statedPaidQty > 0 || !(amount > 0)
                        ? statedPaidQty
                        : Math.max(0, (Number(item.total_quantity_with_nc) || quantity) - Number(item.nc_quantity));
                    detectedNcItems.push({
                        rowKey: `row-${index}`,   // this bill row only — never every row of the dish
                        itemName: itemName,
                        menuItemId: menuItemId,
                        totalQty: item.total_quantity_with_nc || (paidQtyFromPOS + item.nc_quantity),
                        ncQty: item.nc_quantity,
                        billedQty: paidQtyFromPOS,
                        grossAmount: Math.round(((paidQtyFromPOS + item.nc_quantity) * ncPrice) * 100) / 100,
                        netAmount: item.paid_amount || amount,
                        ncAmount: item.nc_amount > 0 ? item.nc_amount : Math.round(item.nc_quantity * ncPrice * 100) / 100,
                        dbPrice: ncPrice,
                        reason: ''
                    });
                    ncDetected = true;
                    console.log(`🏷️ [NC Explicit] "${itemName}": ${item.nc_quantity} NC + ${paidQtyFromPOS} paid (POS-reported)`);
                }

                // ── LAYER 0: Zero-amount items (₹0 on the bill = 100% NC) ──
                if (!ncDetected && !item.file_authoritative && item.nc_zero_amount && menuItem && menuItemId && quantity > 0) {
                    const ncPrice = databasePrice != null && databasePrice > 0 ? databasePrice : 0;
                    detectedNcItems.push({
                        rowKey: `row-${index}`,
                        itemName: itemName,
                        menuItemId: menuItemId,
                        totalQty: quantity,
                        ncQty: quantity,        // entire quantity is NC
                        billedQty: 0,           // nothing was billed
                        grossAmount: Math.round(quantity * ncPrice * 100) / 100,
                        netAmount: 0,
                        ncAmount: Math.round(quantity * ncPrice * 100) / 100,
                        dbPrice: ncPrice,
                        reason: ''
                    });
                    ncDetected = true;
                }

                // NC is never GUESSED from prices. A line below the menu price — a smaller
                // portion, an old menu price, a discount the file does not show — is not
                // a free plate (Zyca, 11-Oct: 3 × ₹249 against ₹349 became "1 NC"). It
                // goes to the Discount / Price Mismatch checks below. NC comes only from
                // what the file states: its NC columns or rows (Layer -1), or a ₹0 line
                // of a menu dish (Layer 0).

                // Check if this item was already detected as NC (skip discount/mismatch check)
                const isNcItem = ncDetected;

                // ✅ NEW 2-STEP DISCOUNT DETECTION LOGIC (skip if NC detected OR platform upload)
                if (!isPlatformUpload && !item.file_authoritative && !isComboDerivedItem && !isNcItem && menuItem && databasePrice != null && databasePrice > 0 && quantity > 0) {
                    const threshold = 1.0; // Allow ₹1 tolerance for rounding/tax

                    // STEP 1: Net Amount / Qty vs DB Price (full price check)
                    const billedUnitPrice = Math.round((billNetAmount / quantity) * 100) / 100;
                    const step1Diff = Math.abs(billedUnitPrice - databasePrice);

                    if (step1Diff <= threshold) {
                        // ✅ Full price match — no issue
                    } else {
                        // STEP 2: (Net Amount + Discount) / Qty vs DB Price (discount check)
                        if (billDiscountAmount > 0) {
                            const originalUnitPrice = Math.round(((billNetAmount + billDiscountAmount) / quantity) * 100) / 100;
                            const step2Diff = Math.abs(originalUnitPrice - databasePrice);

                            if (step2Diff <= threshold) {
                                // ✅ Discount detected — bill had a discount, original price matches DB
                                const discountPerUnit = Math.round((billDiscountAmount / quantity) * 100) / 100;
                                detectedDiscounts.push({
                                    itemName: itemName,
                                    dbPrice: databasePrice,
                                    billedUnitPrice: billedUnitPrice,
                                    discountPerUnit: discountPerUnit,
                                    totalDiscount: billDiscountAmount,
                                    quantity: quantity,
                                    menuItemId: menuItemId
                                });
                            } else {
                                // ❌ Even with discount added back, doesn't match DB — genuine mismatch
                                detectedPriceMismatches.push({
                                    itemName: itemName,
                                    scannedPrice: billedUnitPrice.toFixed(2),
                                    databasePrice: databasePrice.toFixed(2),
                                    difference: step1Diff.toFixed(2),
                                    menuItemId: menuItemId,
                                    quantity: quantity
                                });
                            }
                        } else {
                            // No discount column in bill but price doesn't match — genuine mismatch
                            detectedPriceMismatches.push({
                                itemName: itemName,
                                scannedPrice: billedUnitPrice.toFixed(2),
                                databasePrice: databasePrice.toFixed(2),
                                difference: step1Diff.toFixed(2),
                                menuItemId: menuItemId,
                                quantity: quantity
                            });
                        }
                    }
                }


                return {
                    rowKey: `row-${index}`,   // which bill row this is — the NC popup changes only its own row
                    item: itemName,           // Display name for table
                    item_name: itemName,      // API field
                    menuItemId: menuItemId,
                    quantity: formatQuantity(quantity, item.unit || item.unit_name || 'plate'),  // Display format (canonical)
                    amount: `₹${amount.toFixed(2)}`, // Display format
                    apiData: {                // Raw data for API submission
                        item_name: itemName,
                        quantity: quantity,
                        rate: rate,
                        amount: amount,
                        category: item.category || 'PREPARED_FOOD',
                        menuItemId: menuItemId,
                        // The parser's Net for the line, when it states one: a
                        // "Not in Menu" line is booked at it, not at the Gross.
                        netAmount: (item.net_amount !== undefined && item.net_amount !== null && item.net_amount !== '') ? parseFloat(item.net_amount) : undefined,
                        // A file that states each line's discount is the authority on it.
                        ...(item.file_authoritative ? { fileAuthoritative: true, fileDiscount: billDiscountAmount } : {})
                    },
                    // Match provenance from the backend matcher. Kept on the row so the
                    // confirmation modal can explain WHY a match is uncertain and offer
                    // the runners-up — without these the row is indistinguishable from
                    // an exact match and a wrong recipe deducts silently.
                    parserName: itemName,           // the raw POS bill name; the alias key
                    matchScore: item.match_score ?? null,
                    matchedName: item.matched_name ?? null,
                    matchMethod: item.match_method ?? null,
                    // Sales Name Check (spec §9.3): the server's name key, which is the
                    // key Not in Menu groups by, and its remembered-name verdicts: set
                    // aside as non-stock, or a size name whose price matched no
                    // remembered size.
                    nameKey: item.name_key || lineNameKey({ itemName }),
                    rememberedSkip: setAsideBySkip,
                    skipReason: item.skip_reason ?? null,
                    priceUnresolved: item.price_unresolved || null,
                    matchMargin: item.match_margin ?? null,
                    isMatched: isBackendMatched,
                    needsConfirmation: item.needs_confirmation === true,
                    confirmReason: item.confirm_reason ?? null,
                    collisionWith: item.collision_with || null,
                    matchCandidates: item.match_candidates || [],
                    vetoReasons: item.veto_reasons || [],
                    sourceFile: item.sourceFile,
                    sourceIndex: item.sourceIndex
                };
            });


            // ✅ ALWAYS SET SALES DATA
            setScanItems(transformedData);
            setScanOutlet(loadedOutlet);
            setNoDateFiles(filesWithNoDate);

            // Calculate total bill discount from ALL parsed items (sum of discount_amount column)
            const totalBillDiscount = allSalesData.reduce((sum, item) => {
                return sum + (parseFloat(item.discount_amount) || 0);
            }, 0);
            setBillTotalDiscount(totalBillDiscount);

            // Calculate total (combine scan + manual)
            const scanSum = transformedData.reduce((acc, item) => acc + item.apiData.amount, 0);
            const manualSum = manualItems.reduce((acc, item) => acc + (item.apiData?.amount || 0), 0);
            setTotalAmount(`₹${(scanSum + manualSum).toFixed(2)}`);

            // ═══ MENU MATCH CONFIRMATION ═══
            // Now config-driven instead of hardcoded off (Phase 3).
            //
            // Enable together with the backend: REACT_APP_MATCH_CONFIRM=true here
            // and SEMANTIC_MATCHING=true + MATCH_CONFIRM_MODE=collisions there.
            // Either side left off keeps the feature parked — the backend simply
            // never sets needsConfirmation, so this list stays empty.
            //
            // Start in 'collisions' mode: only two bill lines landing on the same
            // menu item raise the modal, which is always a real error. Turning on
            // 'full' before that has telemetry buries those under fuzzy noise.
            const SEMANTIC_CONFIRM_ENABLED = process.env.REACT_APP_MATCH_CONFIRM === 'true';
            const needConfirm = !SEMANTIC_CONFIRM_ENABLED ? [] : transformedData
                .map((row, idx) => ({ row, idx }))
                .filter(({ row }) => row.needsConfirmation && row.menuItemId)
                .map(({ row, idx }) => ({
                    scanIndex: idx,
                    billName: row.parserName || row.item_name,
                    menuItemId: row.menuItemId,
                    resolvedMenuItemId: row.menuItemId, // user may change this
                    matchedName: row.matchedName,
                    matchScore: row.matchScore,
                    confirmReason: row.confirmReason,
                    // A collision means two bill lines hit one menu item — at least
                    // one is wrong. Clicking "confirm" without changing it must NOT
                    // be persisted as an alias, or the wrong guess gets locked in and
                    // silently repeats on every future upload.
                    isCollision: !!(row.collisionWith && row.collisionWith.length),
                    candidates: row.matchCandidates || [],
                    quantity: row.apiData?.quantity
                }));

            // ✅ HANDLE RESULTS: Match Confirm → NC modal → Discount modal → Price Mismatch modal → Confirm
            // Store detected items for later modal chain
            if (needConfirm.length > 0) {
                setMatchConfirmations(needConfirm);
                setShowMatchConfirmModal(true);
            }

            // A3: carry unmatched NC forward so the Unmapped modal can report it.
            setUnmatchedNcItems(detectedNcUnmatched);

            if (detectedNcItems.length > 0) {
                setAutoDetectedNcItems(detectedNcItems);
                // Only show immediately if no match confirmation is pending
                if (needConfirm.length === 0) {
                    setShowAutoNcModal(true);
                }
            }

            if (detectedDiscounts.length > 0 || totalBillDiscount > 0) {
                setAutoDetectedDiscounts(detectedDiscounts);

                // Persist auto-detected discounts into the same structure used during TSR submit
                if (detectedDiscounts.length > 0) {
                    setDiscountedItems(prev => {
                        const mergedByMenuItem = new Map(prev.map(item => [item.menuItemId, item]));

                        detectedDiscounts.forEach(disc => {
                            if (!disc.menuItemId) return;

                            const originalPrice = parseFloat(disc.dbPrice) || 0;
                            const discountedPrice = parseFloat(disc.billedUnitPrice) || 0;
                            const computedPct = originalPrice > 0
                                ? ((originalPrice - discountedPrice) / originalPrice) * 100
                                : 0;

                            mergedByMenuItem.set(disc.menuItemId, {
                                menuItemId: disc.menuItemId,
                                itemName: disc.itemName,
                                originalPrice,
                                discountPercentage: Math.round(Math.max(0, computedPct) * 100) / 100,
                                discountedPrice
                            });
                        });

                        return Array.from(mergedByMenuItem.values());
                    });
                }

                // Only show discount modal immediately if no match-confirm and no NC modal
                if (needConfirm.length === 0 && detectedNcItems.length === 0) {
                    setShowAutoDiscountModal(true);
                }
            }

            if (detectedPriceMismatches.length > 0) {
                // Genuine price mismatches found
                setPriceMismatches(detectedPriceMismatches);
                // Only show immediately if no match-confirm, no NC and no discount modals
                if (needConfirm.length === 0 && detectedNcItems.length === 0 && detectedDiscounts.length === 0 && totalBillDiscount === 0) {
                    setShowPriceMismatchModal(true);
                }
                setError({
                    type: 'warning',
                    message: `⚠️ ${detectedPriceMismatches.length} price mismatch(es) detected!${detectedDiscounts.length > 0 ? ` (${detectedDiscounts.length} discount(s) also detected)` : ''}`
                });
            } else if (detectedDiscounts.length === 0 && detectedNcItems.length === 0) {
                // No mismatches, no discounts, no NC — clean success
                const ncItemsInfo = ncItems.length > 0 ? ` (${ncItems.length} NC item(s) flagged for wastage)` : '';
                setError({
                    type: 'success',
                    message: `Successfully processed ${files.length} file(s) with ${transformedData.length} total items${ncItemsInfo}`
                });
            }

        } catch (error) {
            setError(error.message || 'Failed to process sales reports. Please try again.');
        } finally {
            setIsScanning(false);
            setFileProcessingProgress({ current: 0, total: 0 });
        }
    };

    // Function to process the sale (extracted for reuse)
    // `unmappedItems` are bill lines that could not be resolved to a menu item.
    // They are NOT part of `items` (they were filtered out) and never enter
    // totalAmount — they ride along so the backend can record the revenue that
    // used to be discarded silently.
    // confirmDuplicate carries the owner's answer to the duplicate warning back to the
    // server. It is threaded through rather than held in state so the retry re-runs the
    // identical submission — same items, same date, same discounts — with one flag
    // flipped, instead of rebuilding a payload that could drift from the one they saw.
    const processSale = async (items, allowNegativeStock = false, unmappedItems = [], confirmDuplicate = false) => {
        try {
            console.log('🚀 Processing sale with allowNegativeStock:', allowNegativeStock);
            setIsDeducting(true);
            setProcessingProgress(50);
            setProcessingMessage('Preparing sale...');

            // ====== NEW: Call TSR submission API with item details ======
            const reportDate = selectedDate || targetDate || getISTDate();

            // DEBUG: Log initial state
            console.log('📊 Starting sale preparation...');
            console.log('Items count:', items.length);
            console.log('Discounted items:', discountedItems);
            console.log('Is online order mode:', isOnlineOrderMode);

            // Prepare sales items with full details including discount information
            const salesItems = items.map(item => {
                // Check if this item has a discount applied
                const discountInfo = discountedItems.find(d => d.menuItemId === item.menuItemId);

                // DEBUG
                console.log(`Processing item: ${item.menuItemId}`, {
                    itemMenuItemId: item.menuItemId,
                    discountFound: !!discountInfo,
                    discountInfo: discountInfo
                });

                let unitPrice = item.unitPrice;
                let lineAmount = item.quantity * item.unitPrice; // Original amount before discount
                let discountPercentage = 0;
                let discountAmount = 0;

                if (item.fileDiscount !== undefined && item.fileDiscount !== null) {
                    // The file states this line's discount (Sales By Items): it is
                    // the authority, ahead of the screen's own discount guess, which
                    // only recognises a discount when the menu price equals the file's.
                    const fileLine = lineWithFileDiscount({ quantity: item.quantity, unitPrice: item.unitPrice, fileDiscount: item.fileDiscount });
                    lineAmount = fileLine.amount;
                    discountAmount = fileLine.discount_amount;
                    unitPrice = fileLine.unit_price;
                    discountPercentage = fileLine.discount_percentage;
                } else if (discountInfo) {
                    // Calculate discount for this item
                    const originalPrice = parseFloat(discountInfo.originalPrice) || item.unitPrice;
                    const discountedPrice = parseFloat(discountInfo.discountedPrice) || item.unitPrice;
                    discountPercentage = parseFloat(discountInfo.discountPercentage) || 0;
                    discountAmount = (originalPrice - discountedPrice) * item.quantity;
                    unitPrice = discountedPrice;
                    lineAmount = originalPrice * item.quantity; // Use original price for line_item_amount

                    console.log(`✅ Discount applied: ${discountPercentage}% off, ₹${discountAmount} saved`);
                }

                return {
                    menu_item_id: item.menuItemId,
                    item_name: salesData.find(s => s.apiData?.menuItemId === item.menuItemId)?.item || 'Unknown',
                    quantity: item.quantity,
                    unit_price: unitPrice,
                    amount: lineAmount, // Original amount before discount
                    discount_percentage: discountPercentage,
                    discount_amount: discountAmount,
                    complimentary_overrides: item.complimentary_overrides || null
                };
            });

            // Calculate total amount (sum of amounts after discount is applied by backend)
            const totalAmount = salesItems.reduce((sum, item) => sum + (item.amount - item.discount_amount), 0);

            // Two-layer discount (Phase 4). Both layers are now sent explicitly:
            //   lineDiscountTotal - what is attributable to specific lines
            //   billDiscountTotal - the total the parsed file declares
            // The backend stores the difference as unallocated_discount instead
            // of collapsing the two with a max(), which used to silently drop
            // any bill-level discount that could not be mapped onto lines.
            const lineItemDiscountTotal = salesItems.reduce((sum, item) => sum + (item.discount_amount || 0), 0);
            const parsedBillDiscountTotal = parseFloat(billTotalDiscount) || 0;
            // LEGACY field, kept for one release so an old backend still works.
            const totalDiscount = Math.round(Math.max(lineItemDiscountTotal, parsedBillDiscountTotal) * 100) / 100;

            // DEBUG: Log what we're sending
            console.log('🔍 DEBUG: Submitting Order with Discount Info');
            console.log('Total Items:', salesItems.length);
            console.log('Total Amount:', totalAmount);
            console.log('Total Discount:', totalDiscount);
            console.log('Line-Item Discount Total:', lineItemDiscountTotal);
            console.log('Parsed Bill Discount Total:', parsedBillDiscountTotal);
            console.log('Discounted Items Array:', discountedItems);
            console.log('Sales Items with Discount:', salesItems.filter(item => item.discount_amount > 0));

            setProcessingProgress(60);
            setProcessingMessage('Submitting TSR...');


            let tsrResult;
            try {
                console.log('📤 Sending TSR request with:', {
                    itemCount: salesItems.length,
                    totalAmount,
                    allowNegativeStock,
                    isOnlineOrderMode,
                    discountAmount: totalDiscount
                });

                // A Locked month (continuous closing stock) offers a prior-month adjustment.
                tsrResult = await postWithLockedRetry('/tsr/submit', {
                    salesItems,
                    reportDate,
                    totalAmount,
                    allowNegativeStock, // Pass the flag to backend
                    isOnlineOrderMode, // Pass online order mode flag
                    onlinePlatform: selectedPlatform || 'manual', // Pass selected platform
                    discountAmount: totalDiscount, // LEGACY (lossy) - remove after one release
                    // Phase 4: explicit two-layer discount
                    lineDiscountTotal: Math.round(lineItemDiscountTotal * 100) / 100,
                    billDiscountTotal: parsedBillDiscountTotal,
                    // Recorded for audit; deliberately excluded from totalAmount
                    // above so COGS and gross profit stay untouched.
                    unmappedItems,
                    // Set only after the owner pressed Continue on the duplicate warning.
                    confirmDuplicate,
                    // Byte identity of every file that fed this submission. Written
                    // server-side only on success, so an abandoned review leaves no
                    // trace and the same file can be scanned again without a warning.
                    sourceFiles: scannedSourceFiles,
                    // One outlet of a POS outlet report: the sale carries its outlet.
                    ...(scanOutlet ? { outlet: scanOutlet.name } : {}),
                    // The bill's NC plates, recorded by the server in this sale's own
                    // transaction (linked to it, allowed negative stock). Posting them
                    // to /wastage afterwards failed silently whenever stock was short.
                    ncItems: ncItems
                        .filter((nc) => nc.menuItemId && Number(nc.quantity) > 0)
                        .map((nc) => ({
                            menu_item_id: nc.menuItemId,
                            quantity: Number(nc.quantity),
                            reason: String(nc.notes || nc.reason || '').trim(),
                            item_name: nc.itemName || null
                        }))
                });

                console.log('✅ TSR submission successful');

                // Phase 8: the submission succeeded, but if the engine reported
                // cross-family unit mismatches then some ingredient quantities
                // were deducted in the wrong unit (potentially 1000x out). Keep
                // that on screen rather than burying it in the server log.
                const reportedMismatches = tsrResult?.data?.unit_mismatches || [];
                setUnitMismatchWarnings(reportedMismatches);
                if (reportedMismatches.length > 0) {
                    console.warn(`⚠️ ${reportedMismatches.length} recipe unit conflict(s) — deductions unreliable`, reportedMismatches);
                }
            } catch (tsrError) {
                console.error('❌ TSR submission failed:', tsrError);
                // Check if error is due to wastage not being recorded
                if (tsrError.data?.code === 'WASTAGE_NOT_RECORDED' && !isOnboarding) {
                    setShowWastageWarningModal(true);
                    setIsDeducting(false);
                    setProcessingProgress(0);
                    setProcessingMessage('');
                    return; // Stop execution, wait for user action
                }

                // Duplicate guard — two shapes, one modal.
                //   DUPLICATE_TSR        this exact bill, same date
                //   SIMILAR_TSR_NEARBY   this exact bill, a date 1-2 days away
                //   TSR_DATE_HAS_SALE    a different report, on a date that has a sale
                // All are warnings, not dead ends: the owner chooses Cancel or Continue.
                // Surfacing this as a plain failure would be actively harmful — assuming
                // the submit failed and pressing it again is precisely how the duplicates
                // were created. The modal says NOTHING WAS RECORDED for that reason.
                const dupCode = tsrError.data?.code;
                if (dupCode === 'DUPLICATE_TSR' || dupCode === 'SIMILAR_TSR_NEARBY' || dupCode === 'TSR_DATE_HAS_SALE') {
                    setDuplicateWarning({
                        code: dupCode,
                        title: { DUPLICATE_TSR: 'This report was already submitted', TSR_DATE_HAS_SALE: 'This date already has a sale' }[dupCode]
                            || 'Same report already filed on a nearby date',
                        error: tsrError.data.error,
                        message: tsrError.data.message,
                        existing: tsrError.data.existing || null,
                        reportDate: tsrError.data.report_date || null,
                        // Everything needed to replay the identical submission on Continue.
                        retry: { items, allowNegativeStock, unmappedItems }
                    });
                    setIsDeducting(false);
                    setProcessingProgress(0);
                    setProcessingMessage('');
                    return;
                }

                console.error('Error details:', {
                    message: tsrError.message,
                    data: tsrError.data,
                    status: tsrError.status,
                    code: tsrError.data?.code,
                    allowNegativeStock: allowNegativeStock
                });

                // 🆕 Check if error is due to complimentary item shortage
                // BUT skip showing modal if user already approved negative stock
                if (tsrError.data?.code === 'COMPLIMENTARY_SHORTAGE' && tsrError.data?.shortages && !allowNegativeStock) {

                    // Convert complimentary shortages to the same format as main ingredient shortages
                    const complimentaryMissingIngredients = tsrError.data.shortages.map(shortage => ({
                        itemName: shortage.menuItemName,
                        ingredientName: `${shortage.ingredientName} (${shortage.complimentaryName})`,
                        ingredientId: shortage.ingredientId || 0,
                        required: shortage.required,
                        available: shortage.available,
                        unit: shortage.unit,
                        availableUnit: shortage.availableUnit || shortage.unit,
                        shortage: shortage.shortage,
                        isComplimentary: true // Mark as complimentary
                    }));

                    // Merge with existing missing ingredients (if any from stock check)
                    const allMissingIngredients = [
                        ...missingIngredients,
                        ...complimentaryMissingIngredients
                    ];

                    // Show unified Missing Ingredients modal
                    setMissingIngredients(allMissingIngredients);
                    setSelectedMissingItems((prev) => getFilteredSelection(allMissingIngredients, prev));
                    setShowMissingModal(true);
                    // Carry the unmapped lines (remembered Skips included) across this
                    // hop, or the retry would file the sale without them.
                    setPendingSaleData({ items, unmapped: unmappedItems });
                    setIsDeducting(false);
                    setProcessingProgress(0);
                    setProcessingMessage('');
                    return; // Stop execution, wait for user action
                }

                throw new Error(tsrError.data?.error || tsrError.message || 'Failed to submit TSR');
            }

            if (!tsrResult.success) {
                throw new Error(tsrResult.error || 'Failed to submit TSR');
            }

            // ====== END NEW CODE ======

            // ❌ REMOVED DUPLICATE SALE CREATION - TSR endpoint already handles everything
            // const result = await SalesService.createSale(...);

            setProcessingProgress(85);
            setProcessingMessage('Finalizing...');

            // TSR already created sale, deducted ingredients, and updated reports
            const result = tsrResult; // Use TSR result instead

            // ====== NC PLATES ======
            // Recorded by the server WITH the sale (/tsr/submit, data.nc): linked to it,
            // allowed negative stock, and a failure refuses the sale. Nothing is posted
            // to /wastage afterwards any more. The success popup says how many.
            const ncRecorded = result.data?.nc || { recorded: 0, plates: 0 };
            setLastSaleNc(ncRecorded);
            setLastSaleCountDate(result.data?.stock_absorbed?.by_count_date || null);
            if (ncItems.length > 0) {
                setNcItems([]);
                setShowNcPage(false);
            }
            const ncAutoRecordSummary = ncRecorded.plates > 0
                ? `\n📋 ${ncRecorded.plates} NC plate(s) recorded with the sale`
                : '';
            // ====== END NC PLATES ======

            setProcessingProgress(100);
            setProcessingMessage('Success!');

            // Create detailed success message from TSR response
            let successMessage = `Successfully processed sale for ${items.length} item(s)!\n`;
            successMessage += `Total Amount: ₹${totalAmount}\n`;

            // Add TSR-specific information
            if (result.data?.ingredient_deduction) {
                const deduction = result.data.ingredient_deduction;
                successMessage += `\nIngredients Deducted: ${deduction.items_success}/${deduction.items_processed}\n`;
                successMessage += `Total COGS: ₹${deduction.total_cogs.toFixed(2)}\n`;

                if (deduction.errors && deduction.errors.length > 0) {
                    successMessage += `\n⚠️ ${deduction.errors.length} deduction error(s) occurred`;
                }
            }

            // Append NC auto-record results to success message
            if (ncAutoRecordSummary) {
                successMessage += ncAutoRecordSummary;
            }
            // Dispatch custom event to clear daily operations lock immediately
            window.dispatchEvent(new CustomEvent('daily-operations:refresh'));

            // Show success modal or auto-navigate if locked TSR
            setTimeout(() => {
                setIsDeducting(false);
                setProcessingProgress(0);
                setProcessingMessage('');

                if (fromLockModal) {
                    // Clear the sales data
                    setScanItems([]);
                    setManualItems([]);
                    setNcItems([]);
                    setDiscountedItems([]);
                    setAutoDetectedDiscounts([]);
                    setBillTotalDiscount(0);
                    setTotalAmount('₹0');
                    setSelectedFiles([]);
                    setIsOnlineOrderMode(false);
                    setTsrEntryTab('scan');

                    if (typeof onRecoveryTsrCompleted === 'function') {
                        onRecoveryTsrCompleted();
                    }
                    goTo && goTo('dashboard');
                } else {
                    setShowSuccessModal(true);
                }
            }, 1000); // 1 second to show 100% completion
        } catch (err) {
            const backendMsg = err?.response?.data?.message || err?.data?.message;
            const message = backendMsg || err?.message || 'Failed to process sale and deduct ingredients';
            setError({
                type: 'error',
                message
            });
            setProcessingProgress(0);
            setProcessingMessage('');
            setIsDeducting(false);
        }
    };

    // Modal handlers
    const handleToggleMissingItem = (key) => {
        setSelectedMissingItems(prev => ({
            ...prev,
            [key]: !prev[key]
        }));
    };

    const handleConfirmNegativeStock = async () => {
        // Option 1: Confirm & Create Negative Stock
        //
        // Two callers share this button. A DAY-WISE import supplies its own
        // resume function (it writes many dates through /tsr/submit-day-wise,
        // not one through processSale), so that takes precedence when set.
        if (dayWiseProceed) {
            const resume = dayWiseProceed.fn;
            setShowMissingModal(false);
            setDayWiseProceed(null);
            setMissingIngredients([]);
            setSelectedMissingItems({});
            try {
                await resume();
            } catch (err) {
                console.error('❌ Day-wise import with negative stock failed:', err);
                setError({
                    type: 'error',
                    message: `${err.message || 'Import failed'}. Please check inventory and try again.`
                });
            }
            return;
        }

        if (!pendingSaleData) return;

        try {
            console.log('✅ User confirmed negative stock creation');
            setIsDeducting(true);
            setShowMissingModal(false);
            await processSale(pendingSaleData.items, true, pendingSaleData.unmapped || []); // Pass true to indicate negative stock is approved

            // Only clear state if successful
            console.log('✅ Negative stock created successfully');
            setPendingSaleData(null);
            setMissingIngredients([]);
            setSelectedMissingItems({});
        } catch (err) {
            console.error('❌ Failed to create negative stock:', err);
            setIsDeducting(false);

            // Clear modal state even on error to prevent loop
            setPendingSaleData(null);
            setMissingIngredients([]);
            setSelectedMissingItems({});

            // Show specific error message
            const errorMessage = err.message || 'Failed to process sale with negative stock';
            setError({
                type: 'error',
                message: `${errorMessage}. Please check inventory and try again.`
            });
        }
    };


    const handleRemoveFromRecipe = async () => {
        // Option 3: Navigate to Recipe Editor (Don't remove yet - user edits manually)
        try {
            const selectedKeys = getValidSelectedMissingKeys();

            if (selectedKeys.length === 0) {
                setError({ type: 'error', message: 'Please select at least one ingredient to edit' });
                return;
            }

            const sanitizedSelection = selectedKeys.reduce((acc, key) => {
                acc[key] = true;
                return acc;
            }, {});

            // Collect unique menu items to edit
            const menuItemsToEdit = new Set();

            selectedKeys.forEach(key => {
                const [itemName, ingredientName] = key.split('|');

                // Find the menu item ID by matching item name from salesData
                const salesItem = salesData.find(saleItem => {
                    const saleName = saleItem.item || saleItem.item_name || '';
                    return saleName.toLowerCase().trim() === itemName.toLowerCase().trim();
                });

                const menuItemId = salesItem?.menuItemId ||
                    salesItem?.apiData?.menuItemId ||
                    pendingSaleData?.items?.[0]?.menuItemId;

                if (menuItemId) {
                    menuItemsToEdit.add(menuItemId);
                }
            });

            if (menuItemsToEdit.size > 0) {

                // Store the first menuItemId in localStorage for auto-selection
                const menuItemIdToEdit = Array.from(menuItemsToEdit)[0];
                localStorage.setItem('autoSelectRecipeId', menuItemIdToEdit);

                // Store pending sale data so we can return to it.
                //
                // NOT for a day-wise import: what would need restoring is the
                // parsed multi-date report and a resume CLOSURE, and a function
                // cannot be serialised. Writing a half-restorable blob here
                // would bring the user back to a Missing Ingredients modal whose
                // confirm button silently does nothing. The file is re-uploaded
                // instead — cheap, and the import ledger makes it safe.
                if (dayWiseProceed) {
                    setDayWiseProceed(null);
                    localStorage.removeItem('pendingTSRData');
                } else {
                    localStorage.setItem('pendingTSRData', JSON.stringify({
                        salesData,
                        missingIngredients,
                        selectedMissingItems: sanitizedSelection,
                        pendingSaleData,
                        resolvedIngredients: Array.from(resolvedIngredients), // Convert Set to Array for JSON
                        tsrEntryTab
                    }));
                }

                // Navigate to recipe editor
                let isQRUser = false;
                try {
                    const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
                    isQRUser = storedUser?.isQRBilling === true || storedUser?.business?.isQRBilling === true;
                } catch (_) {
                    isQRUser = false;
                }

                goTo && goTo(isQRUser ? 'qr-itemmap' : 'itemmap');
            } else {
                setError({
                    type: 'error',
                    message: 'Could not find menu item to edit'
                });
            }
        } catch (err) {
            setError({
                type: 'error',
                message: err?.message || 'Failed to navigate to recipe editor'
            });
        }
    };

    // =================== TSR ACCESS CONTROL UI ===================
    // Show loading state while checking access
    if (isCheckingAccess) {
        return (
            <div className="sales-container">
                <div className="content-wrapper">
                    <div className="tsr-header">
                        {!isOnboarding && (
                            <button
                                onClick={() => goTo && goTo('dashboard')}
                                aria-label="Back"
                                title="Back to Dashboard"
                                className="tsr-back-btn"
                            >
                                <FontAwesomeIcon icon={faArrowLeft} className="tsr-back-icon" />
                            </button>
                        )}
                        <h2 className="report-title tsr-title">Today's Sales Report</h2>
                    </div>
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: 'calc(100vh - 200px)',
                        padding: '40px',
                        textAlign: 'center',
                        gap: '20px',
                        position: 'relative',
                        top: '-10px'
                    }}>
                        <div style={{ position: 'relative', width: '95px', height: '95px' }}>
                            <img
                                src="/invexis-logo.png"
                                alt="Invexis"
                                style={{
                                    position: 'absolute',
                                    top: '50%',
                                    left: '50%',
                                    transform: 'translate(-50%, -50%)',
                                    width: '70px',
                                    height: '70px',
                                    borderRadius: '50%',
                                    backgroundColor: '#fff',
                                    padding: '0px',
                                    pointerEvents: 'none',
                                    objectFit: 'contain',
                                    boxShadow: '0 2px 12px rgba(0,0,0,0.15)',
                                    zIndex: 1
                                }}
                            />
                            <div style={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                                width: '70px',
                                height: '70px',
                                border: '6px solid #000000',
                                borderTop: '6px solid #f9c806',
                                borderRight: '6px solid #f9c806',
                                borderRadius: '50%',
                                animation: 'tsrSpin 1s linear infinite',
                                zIndex: 16
                            }}></div>
                        </div>
                        <p style={{ fontSize: '16px', color: '#666', margin: 0 }}>Checking TSR access permissions...</p>
                        <style>{`
                            @keyframes tsrSpin { 
                                0% { transform: translate(-50%, -50%) rotate(0deg); } 
                                100% { transform: translate(-50%, -50%) rotate(360deg); } 
                            }
                        `}</style>
                    </div>
                </div>
            </div>
        );
    }

    // Show blocked state if access is denied
    if (!tsrAccessGranted) {
        return (
            <div className="sales-container">
                <div className="content-wrapper">
                    <div className="tsr-header">
                        {!isOnboarding && (
                            <button
                                onClick={() => goTo && goTo('dashboard')}
                                aria-label="Back"
                                title="Back to Dashboard"
                                className="tsr-back-btn"
                            >
                                <FontAwesomeIcon icon={faArrowLeft} className="tsr-back-icon" />
                            </button>
                        )}
                        <h2 className="report-title tsr-title">Today's Sales Report</h2>
                    </div>

                    <div style={{
                        maxWidth: '600px',
                        margin: '40px auto',
                        padding: '30px',
                        backgroundColor: '#fff3cd',
                        border: '2px solid #ffc107',
                        borderRadius: '12px',
                        boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                    }}>
                        {/* Error Icon */}
                        <div style={{
                            textAlign: 'center',
                            marginBottom: '20px',
                            fontSize: '64px',
                            color: 'red'
                        }}>
                            <FontAwesomeIcon icon={faBan} />
                        </div>

                        {/* Main Message */}
                        <h3 style={{
                            textAlign: 'center',
                            color: '#856404',
                            fontSize: '24px',
                            fontWeight: '700',
                            marginBottom: '15px'
                        }}>
                            TSR Submission Blocked
                        </h3>

                        <p style={{
                            textAlign: 'center',
                            color: '#856404',
                            fontSize: '16px',
                            lineHeight: '1.6',
                            marginBottom: '20px'
                        }}>
                            You have <strong>{pendingApprovalsCount}</strong> pending wastage approval(s) that must be reviewed by the owner before you can submit Today's Sales Report.
                        </p>

                        {/* Pending Approvals List */}
                        {pendingApprovalsList.length > 0 && (
                            <div style={{
                                backgroundColor: '#ffffff',
                                border: '1px solid #ffc107',
                                borderRadius: '8px',
                                padding: '20px',
                                marginBottom: '20px'
                            }}>
                                <h4 style={{
                                    color: '#856404',
                                    fontSize: '16px',
                                    fontWeight: '600',
                                    marginBottom: '15px'
                                }}>
                                    Pending Wastage Approvals:
                                </h4>
                                <ul style={{
                                    listStyle: 'none',
                                    padding: '0',
                                    margin: '0'
                                }}>
                                    {pendingApprovalsList.map((approval, index) => (
                                        <li key={index} style={{
                                            padding: '12px',
                                            backgroundColor: '#f8f9fa',
                                            borderLeft: '4px solid #ffc107',
                                            marginBottom: '10px',
                                            borderRadius: '4px'
                                        }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <div>
                                                    <strong style={{ color: '#333', fontSize: '14px' }}>
                                                        {approval.item_name || 'Unknown Item'}
                                                    </strong>
                                                    <div style={{ color: '#666', fontSize: '12px', marginTop: '4px' }}>
                                                        Requested: {new Date(approval.created_at).toLocaleDateString()} at {new Date(approval.created_at).toLocaleTimeString()}
                                                    </div>
                                                    {approval.has_evidence && (
                                                        <div style={{ color: '#28a745', fontSize: '12px', marginTop: '2px' }}>
                                                            📷 Photo Evidence Attached
                                                        </div>
                                                    )}
                                                </div>
                                                <span style={{
                                                    backgroundColor: '#ffc107',
                                                    color: '#856404',
                                                    padding: '4px 12px',
                                                    borderRadius: '12px',
                                                    fontSize: '12px',
                                                    fontWeight: '600'
                                                }}>
                                                    PENDING
                                                </span>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                    </div>

                    {accessCheckError && (
                        <div style={{
                            maxWidth: '600px',
                            margin: '20px auto',
                            padding: '15px',
                            backgroundColor: '#f8d7da',
                            border: '1px solid #f5c6cb',
                            borderRadius: '8px',
                            color: '#721c24',
                            fontSize: '14px'
                        }}>
                            ⚠️ Error checking access: {accessCheckError}
                        </div>
                    )}
                </div>
            </div>
        );
    }

    const tsrCalendarMonthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    const tsrCalendarDayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    const selectedDateObject = parseISODateToLocal(selectedDate);
    const todayCalendarDate = parseISODateToLocal(getISTDate()) || new Date();
    todayCalendarDate.setHours(0, 0, 0, 0);

    const firstOfCalendarMonth = new Date(calendarViewMonth.getFullYear(), calendarViewMonth.getMonth(), 1);
    const lastOfCalendarMonth = new Date(calendarViewMonth.getFullYear(), calendarViewMonth.getMonth() + 1, 0);
    const firstDayOffset = (firstOfCalendarMonth.getDay() + 6) % 7;
    const calendarCells = [];

    for (let index = 0; index < firstDayOffset; index += 1) {
        calendarCells.push(null);
    }

    for (let day = 1; day <= lastOfCalendarMonth.getDate(); day += 1) {
        calendarCells.push(new Date(calendarViewMonth.getFullYear(), calendarViewMonth.getMonth(), day));
    }

    const latestMonth = new Date(todayCalendarDate.getFullYear(), todayCalendarDate.getMonth(), 1);
    const canGoNextMonth = calendarViewMonth.getTime() < latestMonth.getTime();
    const selectedWastageDate = selectedDate || targetDate || getISTDate();
    const selectedWastageDateLabel = formatDisplayDate(selectedWastageDate) || selectedWastageDate;

    // =================== NORMAL TSR UI (Access Granted) ===================
    return (
        <>
            {/* NC Items Page - Full Screen */}
            {showNcPage && (
                <NCItemsPage
                    ncItems={ncItems}
                    onAddItem={handleAddNcItem}
                    onRemoveItem={handleRemoveNcItem}
                    onBack={() => setShowNcPage(false)}
                    onConfirm={handleConfirmNcItems}
                    onUpdateItemNotes={handleUpdateNcItemNotes}
                />
            )}

            {/* Confirmed NC Items Summary Page - Full Screen */}
            {!showNcPage && showNcSummaryPage && (
                <div className="sales-container">
                    <div className="content-wrapper">
                        <div className="tsr-header">
                            {!isOnboarding && (
                                <button
                                    onClick={() => setShowNcSummaryPage(false)}
                                    aria-label="Back"
                                    title="Back to Total Sales Report"
                                    className="tsr-back-btn"
                                >
                                    <FontAwesomeIcon icon={faArrowLeft} className="tsr-back-icon" />
                                </button>
                            )}
                            <h2 className="report-title tsr-title">NC Items (Non Chargeable)</h2>
                        </div>

                        <div className="tsr-nc-items-box" style={{ marginTop: '8px' }}>
                            <h4 className="tsr-nc-items-title">NC Items (Non Chargeable)</h4>
                            <div className="tsr-nc-items-list">
                                {ncItems.map((ncItem, index) => (
                                    <div key={index} className="tsr-nc-item" style={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                                            <span className="tsr-nc-item-info">
                                                {ncItem.itemName} - {ncItem.quantity} qty @ ₹{ncItem.price || '0'} = ₹{(ncItem.quantity * (ncItem.price || 0)).toFixed(2)}
                                            </span>
                                            <button
                                                className="tsr-nc-remove-btn"
                                                onClick={() => handleRemoveNcItem(index)}
                                                disabled={isDeducting}
                                            >
                                                <FontAwesomeIcon icon={faTrash} />
                                            </button>
                                        </div>
                                        {(ncItem.notes || ncItem.reason) && (
                                            <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px', fontStyle: 'italic', textAlign: 'left' }}>
                                                Note: {ncItem.notes || ncItem.reason}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Main TSR Content */}
            {!showNcPage && !showNcSummaryPage && (
                <div className="sales-container">
                    <div className="content-wrapper">
                        {/* Header with back arrow beside the title */}
                        <div className="tsr-header">
                            {!isOnboarding && (
                                <button
                                    onClick={() => goTo && goTo('dashboard')}
                                    aria-label="Back"
                                    title="Back to Dashboard"
                                    className="tsr-back-btn"
                                >
                                    <FontAwesomeIcon icon={faArrowLeft} className="tsr-back-icon" />
                                </button>
                            )}
                            <h2 className="report-title tsr-title">Total Sales Report</h2>
                        </div>

                        {/* ✨ Date Picker with Status */}
                        <div className="tsr-date-picker-container">
                                    <div className="tsr-date-picker-wrapper">
                                        <label htmlFor="tsr-date-input" className="tsr-date-label">
                                            Report Date
                                        </label>
                                        <div className="tsr-date-input-group">
                                            <button
                                                id="tsr-date-input"
                                                type="button"
                                                onClick={handleOpenDateCalendar}
                                                className="tsr-date-trigger"
                                                disabled={isLoadingDateData || isDeducting || isScanning}
                                                aria-label="Select report date"
                                            >
                                                <span className="tsr-date-trigger-value">
                                                    {formatDisplayDate(selectedDate) || 'Select date'}
                                                </span>
                                                <LucideCalendar size={18} className="tsr-date-trigger-icon" />
                                            </button>
                                            {isLoadingDateData && (
                                                <div className="tsr-date-loading">
                                                    <div className="tsr-date-spinner"></div>
                                                </div>
                                            )}
                                            {!isLoadingDateData && dateStatus && (
                                                <div className="tsr-date-status-badges">
                                                    {dateStatus.tsrSubmitted && (
                                                        <span className="tsr-date-badge tsr-badge-success">
                                                            <FontAwesomeIcon icon={faCheckCircle} /> Submitted
                                                        </span>
                                                    )}
                                                    {/* Wastage is a DAILY-LOOP checkpoint, not an onboarding
                                                        step: the server skips the check entirely while a
                                                        business is onboarding. Showing the warning here made
                                                        the last setup step look blocked when it was not. */}
                                                    {!dateStatus.wastageRecorded && !dateStatus.wastageClosed && !isOnboarding && (
                                                        <span className="tsr-date-badge tsr-badge-warning">
                                                            <FontAwesomeIcon icon={faExclamationTriangle} /> No Wastage
                                                        </span>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                        {!isLoadingDateData && (
                                            <CountedDateNote
                                                countDateLabel={dateStatus.absorbedBy ? formatDisplayDate(dateStatus.absorbedBy) : null}
                                                noRevert={!!dateStatus.revertClosed}
                                            />
                                        )}
                                        {showDateCalendar && (
                                            <div className="tsr-calendar-overlay" onClick={handleCloseDateCalendar} role="presentation">
                                                <div
                                                    className="tsr-calendar-popup"
                                                    role="dialog"
                                                    aria-modal="true"
                                                    aria-label="Choose report date"
                                                    onClick={(event) => event.stopPropagation()}
                                                >
                                                    <div className="tsr-calendar-header">
                                                        <button
                                                            type="button"
                                                            onClick={() => handleNavigateCalendarMonth(-1)}
                                                            className="tsr-calendar-nav-btn"
                                                            aria-label="Previous month"
                                                        >
                                                            ‹
                                                        </button>
                                                        <h3>{tsrCalendarMonthNames[calendarViewMonth.getMonth()]} {calendarViewMonth.getFullYear()}</h3>
                                                        <button
                                                            type="button"
                                                            onClick={() => handleNavigateCalendarMonth(1)}
                                                            className="tsr-calendar-nav-btn"
                                                            aria-label="Next month"
                                                            disabled={!canGoNextMonth}
                                                        >
                                                            ›
                                                        </button>
                                                    </div>

                                                    <div className="tsr-calendar-weekdays">
                                                        {tsrCalendarDayNames.map((day) => (
                                                            <div key={day} className="tsr-calendar-weekday">{day}</div>
                                                        ))}
                                                    </div>

                                                    <div className="tsr-calendar-grid">
                                                        {calendarCells.map((dateObj, index) => {
                                                            if (!dateObj) {
                                                                return <div key={`empty-${index}`} className="tsr-calendar-empty" />;
                                                            }

                                                            const isFutureDate = dateObj.getTime() > todayCalendarDate.getTime();
                                                            const isTodayDate = isSameCalendarDay(dateObj, todayCalendarDate);
                                                            const isSelectedDate = isSameCalendarDay(dateObj, selectedDateObject);

                                                            const cellClassNames = [
                                                                'tsr-calendar-day',
                                                                isSelectedDate ? 'selected' : '',
                                                                isTodayDate ? 'today' : ''
                                                            ].filter(Boolean).join(' ');

                                                            return (
                                                                <button
                                                                    type="button"
                                                                    key={`day-${dateObj.getDate()}-${index}`}
                                                                    className={cellClassNames}
                                                                    disabled={isFutureDate || isLoadingDateData || isDeducting}
                                                                    onClick={() => handleCalendarDateSelect(dateObj)}
                                                                >
                                                                    {dateObj.getDate()}
                                                                </button>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                        </div>

                        {/* Stock-In-like entry tabs */}
                        <div className="tsr-sub-tabs">
                            {/* In banquet mode the Scan/Manual choice does not apply — there is
                                one way to enter a banquet — so the pair is replaced by a single
                                static label rather than left showing tabs that do nothing. */}
                            {entryMode === 'banquet' ? (
                                <div className="tsr-tab-group tsrtabv2-group">
                                    <span className="tsr-sub-tab active">Banquet</span>
                                </div>
                            ) : (
                            <div className="tsr-tab-group tsrtabv2-group" ref={tsrTabGroupRef}>
                                <button
                                    type="button"
                                    className={`tsr-sub-tab ${tsrEntryTab === 'scan' ? 'active' : ''}`}
                                    onClick={() => {
                                        setTsrEntryTab('scan');
                                        setShowManualSuggestions(false);
                                    }}
                                >
                                    Scan Bill
                                </button>
                                <button
                                    type="button"
                                    className={`tsr-sub-tab ${tsrEntryTab === 'manual' ? 'active' : ''}`}
                                    onClick={() => {
                                        setTsrEntryTab('manual');
                                        setShowManualSuggestions(false);

                                        // Add an initial empty card if no manual items exist (Stock-In-like)
                                        if (manualItems.length === 0) {
                                            const newEntry = {
                                                item: '',
                                                quantity: 1,
                                                amount: '',
                                                sourceFile: 'Manual Entry',
                                                apiData: {
                                                    item_name: '',
                                                    quantity: 1,
                                                    rate: 0,
                                                    unitPrice: 0,
                                                    amount: 0,
                                                    parserName: '',
                                                    menuItemId: null
                                                },
                                                isEditing: true
                                            };
                                            setManualItems([newEntry]);
                                        }
                                    }}
                                >
                                    Manual Entry
                                </button>
                                <span className="tsrtabv2-indicator" style={tsrTabIndicatorStyle} aria-hidden="true" />
                            </div>
                            )}
                            <div className="tsr-menu-wrapper" ref={hamburgerMenuRef}>
                                <button
                                    type="button"
                                    className="tsr-menu-btn"
                                    onClick={() => setShowHamburgerMenu(!showHamburgerMenu)}
                                >
                                    <FontAwesomeIcon icon={faBars} />
                                </button>
                                {showHamburgerMenu && (
                                    <div className="tsr-menu-dropdown">
                                        <button
                                            type="button"
                                            className="tsr-menu-item"
                                            onClick={() => {
                                                setShowHamburgerMenu(false);
                                                setShowNoTSRModal(true);
                                            }}
                                        >
                                            No TSR Recorded
                                        </button>
                                        <button
                                            type="button"
                                            className="tsr-menu-item"
                                            onClick={() => {
                                                setShowHamburgerMenu(false);
                                                setShowNcSummaryPage(false);
                                                setShowNcPage(true);
                                            }}
                                        >
                                            NC (No Charge)
                                        </button>
                                        <button
                                            type="button"
                                            className="tsr-menu-item"
                                            onClick={() => {
                                                setShowHamburgerMenu(false);
                                                // Load platforms then show picker
                                                http.get('/online-platforms')
                                                    .then(res => {
                                                        const list = (res.platforms || res.data?.platforms || [])
                                                            .filter(p => (p.name || '').toLowerCase() !== 'others');
                                                        // Ensure defaults if backend returns empty
                                                        setPlatforms(list.length > 0 ? list : [
                                                            { platform_id: 'd1', name: 'Zomato', is_default: true },
                                                            { platform_id: 'd2', name: 'Swiggy', is_default: true },
                                                        ]);
                                                    })
                                                    .catch(() => {
                                                        setPlatforms([
                                                            { platform_id: 'd1', name: 'Zomato', is_default: true },
                                                            { platform_id: 'd2', name: 'Swiggy', is_default: true },
                                                        ]);
                                                    });
                                                setNewPlatformName('');
                                                setAddingPlatform(false);
                                                setShowPlatformPicker(true);
                                            }}
                                            style={{
                                                backgroundColor: isOnlineOrderMode ? '#e5e7eb' : 'transparent',
                                                color: '#000000',
                                                fontWeight: '700',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'flex-start',
                                                gap: '4px'
                                            }}
                                        >
                                            <span className="tsr-menu-item-label" style={{ whiteSpace: 'nowrap' }}>Online Orders</span>
                                            {isOnlineOrderMode && (
                                                <span className="tsr-menu-item-check" style={{ marginLeft: 0, fontSize: '0.85rem', color: '#374151' }}>
                                                    ✓ {selectedPlatform || ''}
                                                </span>
                                            )}
                                        </button>
                                        {/* Banquet — the third lane, directly below Online Orders.
                                            Hidden during onboarding and behind the TSR lock: a banquet
                                            neither satisfies the daily TSR requirement nor clears the
                                            lock, so offering it in either flow would mislead. */}
                                        {banquetEnabled && !isOnboarding && !fromLockModal && (
                                            <button
                                                type="button"
                                                className="tsr-menu-item"
                                                onClick={() => {
                                                    setShowHamburgerMenu(false);
                                                    setEntryMode(entryMode === 'banquet' ? 'regular' : 'banquet');
                                                }}
                                                style={{
                                                    backgroundColor: isBanquetMode ? '#e5e7eb' : 'transparent',
                                                    color: '#000000',
                                                    fontWeight: '700',
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'flex-start',
                                                    gap: '4px'
                                                }}
                                            >
                                                <span className="tsr-menu-item-label" style={{ whiteSpace: 'nowrap' }}>Banquet</span>
                                                {isBanquetMode && (
                                                    <span className="tsr-menu-item-check" style={{ marginLeft: 0, fontSize: '0.85rem', color: '#374151' }}>
                                                        ✓ Active
                                                    </span>
                                                )}
                                            </button>
                                        )}
                                        <button
                                            type="button"
                                            className="tsr-menu-item"
                                            onClick={() => {
                                                setShowHamburgerMenu(false);
                                                setShowDiscountModal(true);
                                            }}
                                        >
                                            Discount
                                        </button>
                                        {/* Revert Banquet is gated by the FLAG only, not by isLocalhost
                                            like the two reverts below it. With no edit in v1 it is the
                                            only way to fix a typo, so owners need it. It does reverse
                                            real stock (the banquet's and, after a confirmation, its
                                            wastage's), and the server enforces the revert window, the
                                            closing-count lock and row locks on every call. */}
                                        {banquetEnabled && (
                                            <button
                                                type="button"
                                                className="tsr-menu-item"
                                                onClick={() => {
                                                    setShowHamburgerMenu(false);
                                                    setShowBanquetRevertModal(true);
                                                }}
                                                style={{
                                                    color: '#b45309',
                                                    fontWeight: '700',
                                                    borderTop: '1px solid #fef3c7',
                                                    marginTop: '4px',
                                                    paddingTop: '10px'
                                                }}
                                            >
                                                ↩ Revert Banquet
                                            </button>
                                        )}
                                        <button
                                            type="button"
                                            className="tsr-menu-item"
                                            onClick={() => {
                                                setShowHamburgerMenu(false);
                                                setShowOutletRevertModal(true);
                                            }}
                                            style={{ color: '#b45309', fontWeight: '700' }}
                                        >
                                            ↩ Revert Outlet Sale
                                        </button>
                                        {isLocalhost && (
                                            <button
                                                type="button"
                                                className="tsr-menu-item"
                                                onClick={() => {
                                                    setShowHamburgerMenu(false);
                                                    handleOpenTsrRevert();
                                                }}
                                                style={{
                                                    color: '#dc2626',
                                                    fontWeight: '700',
                                                    borderTop: '1px solid #fee2e2',
                                                    marginTop: '4px',
                                                    paddingTop: '10px'
                                                }}
                                            >
                                                ↩ Revert TSR
                                            </button>
                                        )}
                                        {isLocalhost && (
                                            <button
                                                type="button"
                                                className="tsr-menu-item"
                                                onClick={() => {
                                                    setShowHamburgerMenu(false);
                                                    handleOpenOnlineRevert();
                                                }}
                                                style={{
                                                    color: '#7c3aed',
                                                    fontWeight: '700',
                                                    borderTop: '1px solid #ede9fe',
                                                    marginTop: '4px',
                                                    paddingTop: '10px'
                                                }}
                                            >
                                                ↩ Revert Online Sale
                                            </button>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>

                        {error && error?.type === 'info' && (
                            <div style={{
                                margin: '10px auto 20px',
                                color: '#111827',
                                fontSize: '15px',
                                fontWeight: 700,
                                lineHeight: '1.3',
                                textAlign: 'center',
                                width: '100%',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center'
                            }} role="status">
                                <span>{typeof error === 'string' ? error : error.message || ''}</span>
                            </div>
                        )}

                        {/* Hidden File Inputs (always mounted for scan actions) */}
                        <input
                            type="file"
                            id="bill-scan-input"
                            accept="image/*,application/pdf,.xls,.xlsx,.csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv"
                            onChange={handleFileSelect}
                            style={{ display: 'none' }}
                            disabled={isScanning}
                            multiple
                        />
                        <input
                            type="file"
                            id="gallery-upload-input"
                            accept="image/*,application/pdf,.xls,.xlsx,.csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv"
                            onChange={handleFileSelect}
                            style={{ display: 'none' }}
                            disabled={isScanning}
                            multiple
                        />

                        {/* Multi-image selection display */}
                        {tsrEntryTab === 'scan' && selectedFiles.length > 0 && scanItems.length === 0 && (
                            <div style={{
                                marginBottom: '20px',
                                padding: '20px',
                                backgroundColor: '#ffffff',
                                borderRadius: '12px',
                                border: '1px solid #e5e7eb',
                                boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                            }}>
                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    marginBottom: '16px'
                                }}>
                                    <h4 style={{ margin: '0', fontSize: '16px', fontWeight: '600', color: '#111827' }}>
                                        Selected Files ({selectedFiles.length})
                                    </h4>
                                    <div style={{ display: 'flex', gap: '8px' }}>
                                        <button
                                            onClick={handleScanBill}
                                            disabled={isScanning}
                                            style={{
                                                padding: '8px 20px',
                                                backgroundColor: '#000000',
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: '8px',
                                                cursor: isScanning ? 'not-allowed' : 'pointer',
                                                fontSize: '14px',
                                                fontWeight: '600'
                                            }}
                                        >
                                            Add
                                        </button>
                                        <button
                                            onClick={() => {
                                                setSelectedFiles([]);
                                                setScanItems([]);
                                                // Reset total to only show manual items
                                                const manualSum = manualItems.reduce((acc, item) => acc + (item.apiData?.amount || 0), 0);
                                                setTotalAmount(`₹${manualSum.toFixed(2)}`);
                                            }}
                                            disabled={isScanning}
                                            style={{
                                                padding: '8px 20px',
                                                backgroundColor: '#000000',
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: '8px',
                                                cursor: isScanning ? 'not-allowed' : 'pointer',
                                                fontSize: '14px',
                                                fontWeight: '600'
                                            }}
                                        >
                                            Clear
                                        </button>
                                    </div>
                                </div>

                                <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))',
                                    gap: '10px',
                                    marginBottom: '16px'
                                }}>
                                    {selectedFiles.map((file, index) => (
                                        <div key={index} style={{
                                            position: 'relative',
                                            border: '2px solid #e5e7eb',
                                            borderRadius: '8px',
                                            overflow: 'hidden',
                                            aspectRatio: '1 / 1',
                                            backgroundColor: '#f9fafb'
                                        }}>
                                            {file.type.startsWith('image/') ? (
                                                <img
                                                    src={URL.createObjectURL(file)}
                                                    alt={file.name}
                                                    style={{
                                                        width: '100%',
                                                        height: '100%',
                                                        objectFit: 'cover'
                                                    }}
                                                />
                                            ) : (
                                                <div style={{
                                                    width: '100%',
                                                    height: '100%',
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    padding: '8px',
                                                    boxSizing: 'border-box'
                                                }}>
                                                    <FontAwesomeIcon icon={faUpload} style={{ fontSize: '28px', color: '#6b7280', marginBottom: '6px' }} />
                                                    <p style={{
                                                        margin: 0,
                                                        fontSize: '10px',
                                                        color: '#374151',
                                                        textAlign: 'center',
                                                        wordBreak: 'break-all',
                                                        lineHeight: '1.2',
                                                        overflow: 'hidden',
                                                        display: '-webkit-box',
                                                        WebkitLineClamp: 2,
                                                        WebkitBoxOrient: 'vertical'
                                                    }}>{file.name}</p>
                                                </div>
                                            )}
                                            <button
                                                onClick={() => setSelectedFiles(selectedFiles.filter((_, i) => i !== index))}
                                                style={{
                                                    position: 'absolute',
                                                    top: '4px',
                                                    left: '4px',
                                                    width: '28px',
                                                    height: '28px',
                                                    background: 'rgba(0, 0, 0, 0.7)',
                                                    color: 'white',
                                                    border: 'none',
                                                    borderRadius: '50%',
                                                    cursor: 'pointer',
                                                    padding: '0',
                                                    fontSize: '16px',
                                                    fontWeight: 'bold',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center'
                                                }}
                                            >
                                                ✕
                                            </button>
                                            <div style={{
                                                position: 'absolute',
                                                top: '4px',
                                                right: '4px',
                                                background: '#000000',
                                                color: 'white',
                                                borderRadius: '50%',
                                                width: '24px',
                                                height: '24px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                fontSize: '12px',
                                                fontWeight: 'bold'
                                            }}>
                                                #{index + 1}
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                <button
                                    onClick={processSalesReports}
                                    disabled={isScanning}
                                    style={{
                                        padding: '14px 24px',
                                        backgroundColor: '#000000',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '8px',
                                        cursor: isScanning ? 'not-allowed' : 'pointer',
                                        fontSize: '15px',
                                        fontWeight: 'bold',
                                        width: '100%',
                                        height: '52px',
                                        minHeight: '52px',
                                        maxHeight: '52px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: '8px',
                                        boxSizing: 'border-box'
                                    }}
                                >
                                    {(() => {
                                        const allImages = selectedFiles.every(f => f.type.startsWith('image/'));
                                        const label = allImages ? 'Image' : 'File';
                                        return `Extract Items from ${selectedFiles.length} ${label}${selectedFiles.length > 1 ? 's' : ''}`;
                                    })()}
                                </button>


                                {/* Discounted Items Display */}
                                {discountedItems.length > 0 && (
                                    <div className="tsr-nc-items-box" style={{ backgroundColor: '#fff' }}>
                                        <h4 className="tsr-nc-items-title" style={{ color: '#000000' }}>Discounted Items</h4>
                                        <div className="tsr-nc-items-list">
                                            {discountedItems.map((discountItem, index) => (
                                                <div key={index} className="tsr-nc-item">
                                                    <span className="tsr-nc-item-info">
                                                        {discountItem.itemName} - {discountItem.discountPercentage}% off •
                                                        <span style={{ textDecoration: 'line-through', marginLeft: '4px', color: '#000' }}>
                                                            ₹{discountItem.originalPrice.toFixed(2)}
                                                        </span>
                                                        <span style={{ marginLeft: '4px', fontWeight: 'bold', color: '#000' }}>
                                                            ₹{discountItem.discountedPrice.toFixed(2)}
                                                        </span>
                                                    </span>
                                                    <button
                                                        className="tsr-nc-remove-btn"
                                                        onClick={() => handleRemoveDiscount(index)}
                                                        disabled={isDeducting}
                                                    >
                                                        <FontAwesomeIcon icon={faTrash} />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* ── Banquet lane ────────────────────────────────────────────────
                            Mounted INSTEAD of the scan/upload cards and the manual rows, not
                            beside them. Unmounting is also how banquet state is cleared when
                            the mode is left, so there is no teardown to keep in step. */}
                        {entryMode === 'banquet' && (
                            <BanquetSection
                                eventDate={selectedDate}
                                disabled={isDeducting || isLoadingDateData}
                                getSuggestions={getTopMenuSuggestions}
                                getMenuItemName={getMenuItemName}
                                onRecorded={(result) => {
                                    setError({
                                        type: 'success',
                                        message: `Banquet recorded — ${result.functionName} · ${result.platesOrdered} plates · ₹${Number(result.totalBill).toLocaleString('en-IN')}`,
                                    });
                                    setIsBanquetMode(false);
                                }}
                            />
                        )}

                        {entryMode !== 'banquet' && tsrEntryTab === 'scan' && scanItems.length === 0 && selectedFiles.length === 0 && (
                            <>
                                <div className="tsrscanv2-option-stack">
                                    {/* Scan Bill Section - Green Parity Card */}
                                    <div
                                        className={`tsr-scan-section tsrscanv2-scan-card ${isScanning ? 'tsr-scanning' : ''} ${isOnlineOrderMode && selectedPlatform === 'Swiggy' ? 'platform-swiggy' : ''} ${isOnlineOrderMode && selectedPlatform === 'Zomato' ? 'platform-zomato' : ''}`}
                                        onClick={!isScanning ? handleCameraClick : undefined}
                                    >
                                        <span className="tsrscanv2-corner tsrscanv2-corner-tl" aria-hidden="true" />
                                        <span className="tsrscanv2-corner tsrscanv2-corner-tr" aria-hidden="true" />
                                        <span className="tsrscanv2-corner tsrscanv2-corner-bl" aria-hidden="true" />
                                        <span className="tsrscanv2-corner tsrscanv2-corner-br" aria-hidden="true" />
                                        <div className="tsr-camera-icon tsrscanv2-icon-wrap tsrscanv2-scan-icon-wrap" aria-hidden="true">
                                            <LucideCamera className="tsrscanv2-lucide tsrscanv2-lucide-scan" strokeWidth={2.25} />
                                        </div>
                                        <h3 className="tsr-scan-title tsrscanv2-title">Scan Bill</h3>
                                        <p className="tsr-scan-subtitle tsrscanv2-subtitle">Take photos of your bill(s)</p>
                                    </div>

                                    {/* Upload Section - Dashed Border */}
                                    <div
                                        className={`tsr-upload-section tsrscanv2-upload-card ${isUploadDragOver ? 'tsr-upload-drag-over' : ''} ${isOnlineOrderMode && selectedPlatform === 'Swiggy' ? 'platform-swiggy' : ''} ${isOnlineOrderMode && selectedPlatform === 'Zomato' ? 'platform-zomato' : ''}`}
                                        onClick={!isScanning ? handleGalleryClick : undefined}
                                        onDragOver={handleUploadDragOver}
                                        onDragEnter={handleUploadDragOver}
                                        onDragLeave={handleUploadDragLeave}
                                        onDrop={handleUploadDrop}
                                        role="button"
                                        tabIndex={0}
                                        aria-label="Upload bills by selecting files or dropping them here"
                                        onKeyDown={(event) => {
                                            if (!isScanning && (event.key === 'Enter' || event.key === ' ')) {
                                                event.preventDefault();
                                                handleGalleryClick();
                                            }
                                        }}
                                    >
                                        <div className="tsr-upload-icon tsrscanv2-icon-wrap tsrscanv2-upload-icon-wrap" aria-hidden="true">
                                            <LucideUpload className="tsrscanv2-lucide tsrscanv2-lucide-upload" strokeWidth={2.25} />
                                        </div>
                                        <h3 className="tsr-upload-title tsrscanv2-title">Upload Bills</h3>
                                        <p className="tsr-upload-subtitle tsrscanv2-subtitle">Images, PDFs, or Excel/CSV<br />(multiple files supported)</p>
                                    </div>
                                </div>


                                {/* Discounted Items Display */}
                                {discountedItems.length > 0 && (
                                    <div className="tsr-nc-items-box" style={{ backgroundColor: '#fff' }}>
                                        <h4 className="tsr-nc-items-title" style={{ color: '#000000' }}>Discounted Items</h4>
                                        <div className="tsr-nc-items-list">
                                            {discountedItems.map((discountItem, index) => (
                                                <div key={index} className="tsr-nc-item">
                                                    <span className="tsr-nc-item-info">
                                                        {discountItem.itemName} - {discountItem.discountPercentage}% off •
                                                        <span style={{ textDecoration: 'line-through', marginLeft: '4px', color: '#000' }}>
                                                            ₹{discountItem.originalPrice.toFixed(2)}
                                                        </span>
                                                        <span style={{ marginLeft: '4px', fontWeight: 'bold', color: '#000' }}>
                                                            ₹{discountItem.discountedPrice.toFixed(2)}
                                                        </span>
                                                    </span>
                                                    <button
                                                        className="tsr-nc-remove-btn"
                                                        onClick={() => handleRemoveDiscount(index)}
                                                        disabled={isDeducting}
                                                    >
                                                        <FontAwesomeIcon icon={faTrash} />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </>
                        )}

                        {/* Manual Entry Tab Content (Stock-In-like editable cards) */}
                        {entryMode !== 'banquet' && tsrEntryTab === 'manual' && (
                            <div className="tsr-manual-entry-wrap">
                                <div className="tsr-manual-section-title">Items</div>

                                {/* Editable Item Cards */}
                                {manualItems.map((item, index) => {
                                    const menuItemId = item.apiData?.menuItemId;
                                    const compItems = menuItemId ? complimentaryItemsMap[menuItemId] : [];

                                    return (
                                        <div className="tsr-manual-item-card" key={index}>
                                            <button
                                                type="button"
                                                className="tsr-item-remove-top"
                                                onClick={() => {
                                                    const next = manualItems.filter((_, i) => i !== index);
                                                    setManualItems(next);
                                                    const manualSum = next.reduce((acc, it) => acc + (it.apiData?.amount || 0), 0);
                                                    const scanSum = scanItems.reduce((acc, it) => acc + (it.apiData?.amount || 0), 0);
                                                    setTotalAmount(`₹${(scanSum + manualSum).toFixed(2)}`);
                                                }}
                                                aria-label="Remove item"
                                                title="Remove"
                                            >
                                                ✕
                                            </button>

                                            <div className="tsr-manual-form-group" style={{ position: 'relative' }}>
                                                <label className="tsr-manual-label">Item Name</label>
                                                <input
                                                    type="text"
                                                    className="tsr-manual-input"
                                                    placeholder="Type to search menu items..."
                                                    value={item.item || ''}
                                                    ref={(element) => {
                                                        manualInputRefs.current[`${index}-item`] = element;
                                                    }}
                                                    onChange={(e) => {
                                                        const searchValue = e.target.value;
                                                        handleUpdateManualItem(index, 'itemName', searchValue);

                                                        if (searchValue.trim()) {
                                                            const filtered = getTopMenuSuggestions(searchValue);
                                                            setManualEntrySuggestions(filtered);
                                                            setShowManualSuggestions(index); // Store which card's suggestions to show
                                                            setManualSuggestionIndex(-1);
                                                        } else {
                                                            setManualEntrySuggestions([]);
                                                            setShowManualSuggestions(false);
                                                            setManualSuggestionIndex(-1);
                                                            handleUpdateManualItem(index, 'menuItemId', null);
                                                        }
                                                    }}
                                                    onFocus={(e) => {
                                                        const searchValue = e.target.value;
                                                        if (searchValue && searchValue.trim()) {
                                                            const filtered = getTopMenuSuggestions(searchValue);
                                                            if (filtered.length > 0 || notInMenuOptionFor(searchValue)) {
                                                                setManualEntrySuggestions(filtered);
                                                                setShowManualSuggestions(index);
                                                                setManualSuggestionIndex(-1);
                                                            }
                                                        }
                                                    }}
                                                    onKeyDown={(e) => {
                                                        // The "Add as Not in Menu" row sits after the dishes.
                                                        const optionCount = manualEntrySuggestions.length + (notInMenuOptionFor(item.item) ? 1 : 0);
                                                        if (e.key === 'ArrowDown') {
                                                            if (!optionCount) return;
                                                            e.preventDefault();
                                                            setShowManualSuggestions(index);
                                                            setManualSuggestionIndex(current => Math.min(
                                                                optionCount - 1,
                                                                current < 0 ? 0 : current + 1
                                                            ));
                                                        } else if (e.key === 'ArrowUp') {
                                                            if (!optionCount) return;
                                                            e.preventDefault();
                                                            setShowManualSuggestions(index);
                                                            setManualSuggestionIndex(current => Math.max(
                                                                0,
                                                                current < 0 ? 0 : current - 1
                                                            ));
                                                        } else if (e.key === 'Enter') {
                                                            e.preventDefault();
                                                            if (showManualSuggestions === index && manualSuggestionIndex === manualEntrySuggestions.length && notInMenuOptionFor(item.item)) {
                                                                markManualNotInMenu(index);
                                                                return;
                                                            }
                                                            if (showManualSuggestions === index && manualSuggestionIndex >= 0) {
                                                                selectManualSuggestion(index, manualSuggestionIndex);
                                                            } else {
                                                                setShowManualSuggestions(false);
                                                                setManualEntrySuggestions([]);
                                                                setManualSuggestionIndex(-1);
                                                            }
                                                            focusManualInput(index, 'quantity');
                                                        } else if (e.key === 'Escape') {
                                                            e.preventDefault();
                                                            setShowManualSuggestions(false);
                                                            setManualSuggestionIndex(-1);
                                                        }
                                                    }}
                                                />

                                                {showManualSuggestions === index && (manualEntrySuggestions.length > 0 || notInMenuOptionFor(item.item)) && (
                                                    <div className="tsr-manual-suggestions">
                                                        {manualEntrySuggestions.map((menuItem, idx) => {
                                                            const itemName = menuItem.name || menuItem.item_name || menuItem.menu_item_name || 'Unknown';
                                                            const itemId = menuItem.menu_item_id || menuItem.id;
                                                            const unitPrice = parseFloat(menuItem.price || menuItem.item_price || menuItem.menu_item_price || 0);

                                                            return (
                                                                <div
                                                                    key={itemId || itemName || `manual-suggestion-${idx}`}
                                                                    className={`tsr-manual-suggestion${idx === manualSuggestionIndex ? ' active' : ''}`}
                                                                    onMouseEnter={() => setManualSuggestionIndex(idx)}
                                                                    onClick={() => selectManualSuggestion(index, idx)}
                                                                >
                                                                    <span className="tsr-manual-suggestion-name">{itemName}</span>
                                                                    <span className="tsr-manual-suggestion-price">₹{unitPrice}</span>
                                                                </div>
                                                            );
                                                        })}
                                                        {notInMenuOptionFor(item.item) && (
                                                            <AddAsNotInMenuOption
                                                                typedName={notInMenuOptionFor(item.item)}
                                                                active={manualSuggestionIndex === manualEntrySuggestions.length}
                                                                onHover={() => setManualSuggestionIndex(manualEntrySuggestions.length)}
                                                                onPick={() => markManualNotInMenu(index)}
                                                            />
                                                        )}
                                                    </div>
                                                )}
                                                {item.apiData?.notInMenu && (
                                                    <span className="tsr-nim-badge">Not in Menu</span>
                                                )}
                                            </div>

                                            <div className="tsr-manual-inline-fields">
                                                <div className="tsr-manual-form-group">
                                                    <label className="tsr-manual-label">Quantity</label>
                                                    <input
                                                        type="number"
                                                        className="tsr-manual-input"
                                                        placeholder="Enter Quantity"
                                                        value={item.quantity ?? ''}
                                                        ref={(element) => {
                                                            manualInputRefs.current[`${index}-quantity`] = element;
                                                        }}
                                                        onChange={(e) => handleUpdateManualItem(index, 'quantity', e.target.value)}
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter') {
                                                                e.preventDefault();
                                                                focusManualInput(index, 'total');
                                                            }
                                                        }}
                                                        min={item.apiData?.notInMenu ? '0.01' : '1'}
                                                        step={item.apiData?.notInMenu ? '0.01' : undefined}
                                                    />
                                                </div>

                                                <div className="tsr-manual-form-group">
                                                    <label className="tsr-manual-label">Unit Price</label>
                                                    <input
                                                        type="number"
                                                        className="tsr-manual-input"
                                                        placeholder="0"
                                                        value={item.apiData?.unitPrice === 0 || !item.apiData?.unitPrice ? '' : item.apiData?.unitPrice}
                                                        disabled={!isOnlineOrderMode && !item.apiData?.notInMenu}
                                                        readOnly={!isOnlineOrderMode && !item.apiData?.notInMenu}
                                                        onChange={(e) => {
                                                            if (isOnlineOrderMode || item.apiData?.notInMenu) {
                                                                const inputValue = e.target.value;
                                                                const newPrice = inputValue === '' ? 0 : parseFloat(inputValue) || 0;
                                                                const qty = parseFloat(item.quantity) || 0;
                                                                const newAmount = newPrice * qty;
                                                                handleUpdateManualItem(index, 'unitPrice', newPrice);
                                                                handleUpdateManualItem(index, 'amount', newAmount.toFixed(2));
                                                            }
                                                        }}
                                                        style={{
                                                            backgroundColor: (isOnlineOrderMode || item.apiData?.notInMenu) ? 'white' : '#f3f4f6',
                                                            cursor: (isOnlineOrderMode || item.apiData?.notInMenu) ? 'text' : 'not-allowed',
                                                            color: (isOnlineOrderMode || item.apiData?.notInMenu) ? '#111827' : '#6b7280',
                                                            border: (isOnlineOrderMode || item.apiData?.notInMenu) ? '2px solid #111827' : '1px solid #d1d5db'
                                                        }}
                                                        min="0"
                                                        step="0.01"
                                                    />
                                                </div>

                                                <div className="tsr-manual-form-group">
                                                    <label className="tsr-manual-label">Total Price</label>
                                                    <input
                                                        type="number"
                                                        className="tsr-manual-input"
                                                        placeholder="Enter total amount"
                                                        value={item.apiData?.amount || 0}
                                                        ref={(element) => {
                                                            manualInputRefs.current[`${index}-total`] = element;
                                                        }}
                                                        onChange={(e) => handleUpdateManualItem(index, 'amount', e.target.value)}
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter') {
                                                                e.preventDefault();
                                                                const newIndex = handleAddManualItem();
                                                                focusManualInput(newIndex, 'item');
                                                            }
                                                        }}
                                                        min="0"
                                                        step="0.01"
                                                    />
                                                </div>
                                            </div>

                                            {/* Show complimentary items if menu item selected */}
                                            {compItems && compItems.length > 0 && (
                                                <div className="tsr-item-comp" style={{ marginTop: '12px' }}>
                                                    <div className="tsr-field-label">Complimentary</div>
                                                    <div className="tsr-comp-tags tsr-comp-tags-inline">
                                                        {compItems.map((comp, compIndex) => (
                                                            <span key={`comp-${index}-${compIndex}`} className="tsr-comp-tag">
                                                                {comp.name || comp.item_name}
                                                            </span>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}

                                {/* Add More Items Button */}
                                <button
                                    type="button"
                                    className="tsr-add-more-items-dashed"
                                    onClick={(e) => {
                                        // Prevent focus from jumping to the confirm button when this control gets disabled after adding an empty row
                                        e.currentTarget.blur();
                                        handleAddManualItem();
                                    }}
                                    disabled={isDeducting || !manualItems.every(item =>
                                        (item.item && item.item.trim()) ||
                                        (item.apiData?.item_name && item.apiData.item_name.trim())
                                    )}
                                >
                                    + Add More Items
                                </button>


                                {/* Discounted Items Display */}
                                {discountedItems.length > 0 && (
                                    <div className="tsr-nc-items-box" style={{ backgroundColor: '#fff' }}>
                                        <h4 className="tsr-nc-items-title" style={{ color: '#000000' }}>Discounted Items</h4>
                                        <div className="tsr-nc-items-list">
                                            {discountedItems.map((discountItem, index) => (
                                                <div key={index} className="tsr-nc-item">
                                                    <span className="tsr-nc-item-info">
                                                        {discountItem.itemName} - {discountItem.discountPercentage}% off •
                                                        <span style={{ textDecoration: 'line-through', marginLeft: '4px', color: '#999' }}>
                                                            ₹{discountItem.originalPrice.toFixed(2)}
                                                        </span>
                                                        <span style={{ marginLeft: '4px', fontWeight: 'bold', color: 'var(--success-fg)' }}>
                                                            ₹{discountItem.discountedPrice.toFixed(2)}
                                                        </span>
                                                    </span>
                                                    <button
                                                        className="tsr-nc-remove-btn"
                                                        onClick={() => handleRemoveDiscount(index)}
                                                        disabled={isDeducting}
                                                    >
                                                        <FontAwesomeIcon icon={faTrash} />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Total Card */}
                                {(scanItems.length > 0 || manualItems.length > 0) && (
                                    <div className="tsr-total-card">
                                        <span className="tsr-total-label tsr-total-label--stacked">
                                            Total Sales Amount
                                            <span className="tsr-total-count">{totalPlatesLabel}</span>
                                        </span>
                                        <span className="tsr-total-amount">{totalAmount}</span>
                                    </div>
                                )}
                                {/* Remembered Skips: in the total, never asked about (spec §9.3). */}
                                {setAsideText && (
                                    <p data-testid="tsr-set-aside-note" style={SET_ASIDE_NOTE_STYLE}>{setAsideText}</p>
                                )}
                            </div>
                        )}

                        {/* Scan Tab Content - Standard Table */}
                        {tsrEntryTab === 'scan' && scanItems.length > 0 && (
                            <div className="sales-report-container">
                                {/* One outlet of an outlet report: filed as its own sale. */}
                                {scanOutlet && (
                                    <div
                                        data-testid="tsr-outlet-chip"
                                        style={{
                                            display: 'inline-flex', alignItems: 'center', gap: '6px', margin: '0 0 10px 0',
                                            padding: '4px 12px', borderRadius: '999px', background: '#eef2ff', color: '#3730a3',
                                            fontSize: '13px', fontWeight: 600
                                        }}
                                    >
                                        Outlet: <strong>{scanOutlet.name}</strong>
                                        {scanOutlet.report_date ? <span style={{ fontWeight: 500 }}>· {formatOutletReportDate(scanOutlet.report_date)}</span> : null}
                                    </div>
                                )}
                                <div className="tsr-table-scroll-wrapper">
                                    <Table style={{ width: '100%' }}>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead className="tsr-col-num" style={{ textAlign: 'center', fontSize: '15px', color: '#6b7280' }}>#</TableHead>
                                                <TableHead className="tsr-col-item" style={{ fontSize: '16px' }}>Item</TableHead>
                                                <TableHead className="tsr-col-comp" style={{ fontSize: '16px' }}>Complimentary</TableHead>
                                                <TableHead className="tsr-col-qty" style={{ textAlign: 'center', fontSize: '16px' }}>Qty</TableHead>
                                                <TableHead className="tsr-col-amt" style={{ textAlign: 'right', fontSize: '16px' }}>Amt</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {scanItems.map((item, index) => {
                                                const menuItemId = item.apiData?.menuItemId;
                                                const compItems = menuItemId ? complimentaryItemsMap[menuItemId] : [];

                                                return (
                                                    <TableRow key={index}>
                                                        <TableCell className="tsr-col-num" style={{ textAlign: 'center', fontSize: '13px', color: '#000000', fontWeight: 500, verticalAlign: 'middle' }}>{index + 1}</TableCell>
                                                        <TableCell className="tsr-col-item" style={{ fontWeight: 600, color: '#111827', textAlign: 'left', fontSize: '14px' }}>
                                                            <div className="tsr-cell-content">
                                                                {formatTitleCase(item.item)}
                                                                {/* A4: NC was detected here and dismissed. */}
                                                                {item.ncIgnored && (
                                                                    <span style={{
                                                                        marginLeft: '8px',
                                                                        fontSize: '11px',
                                                                        fontWeight: 600,
                                                                        padding: '2px 6px',
                                                                        borderRadius: '4px',
                                                                        backgroundColor: '#f3f4f6',
                                                                        color: '#6b7280',
                                                                        whiteSpace: 'nowrap'
                                                                    }}>
                                                                        ₹0 — ignored
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </TableCell>
                                                        <TableCell className="tsr-col-comp" style={{ fontSize: '14px' }}>
                                                            <div className="tsr-cell-content">
                                                                {compItems && compItems.length > 0 ? (
                                                                    <div className="tsr-comp-tags">
                                                                        {compItems.map((comp, compIndex) => {
                                                                            const compName = comp.name || comp.item_name;
                                                                            const soldQty = normalizeComplimentaryQty(item.quantity, 1);
                                                                            const storedOverrideQty = complimentaryOverrides[menuItemId]?.[compName];
                                                                            const overrideQty = storedOverrideQty === undefined
                                                                                ? soldQty
                                                                                : normalizeComplimentaryQty(storedOverrideQty, 0);
                                                                            return (
                                                                                <div key={`comp-${index}-${compIndex}`} className="tsr-comp-tag-editable">
                                                                                    <span className="tsr-comp-tag-name">{compName}</span>
                                                                                    <div className="tsr-comp-qty-group">
                                                                                        <span className="tsr-comp-qty-label">x</span>
                                                                                        <input
                                                                                            type="number"
                                                                                            className="tsr-comp-qty-input"
                                                                                            value={String(overrideQty)}
                                                                                            min="0"
                                                                                            onChange={(e) => {
                                                                                                const val = normalizeComplimentaryQty(e.target.value, 0);
                                                                                                setComplimentaryOverrides(prev => ({
                                                                                                    ...prev,
                                                                                                    [menuItemId]: { ...prev[menuItemId], [compName]: val }
                                                                                                }));
                                                                                            }}
                                                                                        />
                                                                                    </div>
                                                                                </div>
                                                                            );
                                                                        })}
                                                                    </div>
                                                                ) : (
                                                                    <span className="tsr-no-comp">—</span>
                                                                )}
                                                            </div>
                                                        </TableCell>
                                                        <TableCell className="tsr-col-qty" style={{ textAlign: 'center', fontSize: '14px' }}>
                                                            {(() => {
                                                                const rawQty = parseFloat(String(item.quantity).replace(/[^0-9.]/g, '')) || 0;
                                                                return `${rawQty} ${rawQty === 1 ? 'plate' : 'plates'}`;
                                                            })()}
                                                        </TableCell>
                                                        <TableCell className="tsr-col-amt" style={{ textAlign: 'right', fontWeight: 600, color: 'var(--success-fg)', fontSize: '14px' }}>
                                                            {isOnlineOrderMode ? (
                                                                <input
                                                                    type="number"
                                                                    className="tsr-table-input tsr-table-input--right"
                                                                    value={item.apiData?.amount || item.amount || ''}
                                                                    onChange={(e) => {
                                                                        const newAmount = parseFloat(e.target.value) || 0;
                                                                        setScanItems(prev => {
                                                                            const updated = [...prev];
                                                                            updated[index] = {
                                                                                ...updated[index],
                                                                                amount: `₹${newAmount.toFixed(2)}`,
                                                                                apiData: {
                                                                                    ...updated[index].apiData,
                                                                                    amount: newAmount,
                                                                                    unitPrice: (parseFloat(updated[index].apiData?.quantity) || 1) > 0
                                                                                        ? newAmount / (parseFloat(updated[index].apiData?.quantity) || 1)
                                                                                        : newAmount
                                                                                }
                                                                            };
                                                                            const manualSum = manualItems.reduce((acc, it) => acc + (it.apiData?.amount || 0), 0);
                                                                            const scanSum = updated.reduce((acc, it) => acc + (it.apiData?.amount || 0), 0);
                                                                            setTotalAmount(`₹${(manualSum + scanSum).toFixed(2)}`);
                                                                            return updated;
                                                                        });
                                                                    }}
                                                                    style={{
                                                                        backgroundColor: 'white',
                                                                        border: '2px solid #111827',
                                                                        borderRadius: '6px',
                                                                        padding: '4px 8px',
                                                                        textAlign: 'right',
                                                                        fontWeight: 600,
                                                                        color: 'var(--success-fg)',
                                                                        fontSize: '14px',
                                                                        width: '100%',
                                                                        maxWidth: '100px'
                                                                    }}
                                                                    min="0"
                                                                    step="0.01"
                                                                />
                                                            ) : (
                                                                item.amount
                                                            )}
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            })}
                                            <TableRow style={{ fontWeight: 700, backgroundColor: '#f9fafb' }}>
                                                <TableCell className="tsr-col-num"></TableCell>
                                                <TableCell className="tsr-col-item tsr-total-label-cell">Total</TableCell>
                                                <TableCell className="tsr-col-comp"></TableCell>
                                                <TableCell className="tsr-col-qty" style={{ textAlign: 'center', fontWeight: 800, fontSize: '16px', color: '#000000' }}>
                                                    {totalPlatesLabel}
                                                </TableCell>
                                                <TableCell className="tsr-col-amt" style={{ textAlign: 'right', fontWeight: 800, fontSize: '16px', color: '#000000' }}>
                                                    {totalAmount}
                                                </TableCell>
                                            </TableRow>
                                        </TableBody>
                                    </Table>
                                </div>
                            </div>
                        )}

                        {/* Total Card - shown for scan tab */}
                        {tsrEntryTab === 'scan' && scanItems.length > 0 && (
                            <div className="tsr-total-card">
                                <span className="tsr-total-label tsr-total-label--stacked">
                                    Total
                                    <span className="tsr-total-count">{totalPlatesLabel}</span>
                                </span>
                                <span className="tsr-total-amount">{totalAmount}</span>
                            </div>
                        )}
                        {/* Remembered Skips: in the total, never asked about (spec §9.3). */}
                        {tsrEntryTab === 'scan' && setAsideText && (
                            <p data-testid="tsr-set-aside-note" style={SET_ASIDE_NOTE_STYLE}>{setAsideText}</p>
                        )}



                        {/* Phase 8: persistent recipe-unit conflict banner.
                    Not a toast — these deductions are quantitatively wrong
                    (a gram-vs-litre mix-up is a 1000x error), so the warning
                    must stay until the recipes are fixed. */}
                        {unitMismatchWarnings.length > 0 && (
                            <div style={{
                                margin: '12px 0',
                                padding: '12px 14px',
                                backgroundColor: '#fffbeb',
                                border: '1px solid #fcd34d',
                                borderRadius: '8px',
                                textAlign: 'left'
                            }}>
                                <div style={{ fontWeight: 700, color: '#92400e', fontSize: '14px', marginBottom: '4px' }}>
                                    ⚠️ {unitMismatchWarnings.length} recipe{unitMismatchWarnings.length === 1 ? ' has' : 's have'} unit conflicts — their deductions are unreliable
                                </div>
                                <div style={{ fontSize: '13px', color: '#78350f', lineHeight: 1.5 }}>
                                    The recipe unit does not match the ingredient's stock unit, so the
                                    quantity removed from stock may be wrong by a large factor.
                                    Open the Recipe Unit Audit to see every affected recipe.
                                </div>
                                <ul style={{ margin: '8px 0 0 18px', fontSize: '12px', color: '#78350f' }}>
                                    {unitMismatchWarnings.slice(0, 5).map((m, i) => (
                                        <li key={i}>
                                            {m.menuItemName || 'Recipe'} / {m.ingredientName}: recipe in {m.recipeUnit}, {m.batchUnit ? `batch in ${m.batchUnit}` : `stock in ${m.stockUnit}`}
                                        </li>
                                    ))}
                                    {unitMismatchWarnings.length > 5 && (
                                        <li>…and {unitMismatchWarnings.length - 5} more</li>
                                    )}
                                </ul>
                            </div>
                        )}

                        {/* Error Display */}
                        {error && (
                            error?.type === 'info' ? null : (
                                <div style={{
                                    margin: '0 0 12px 0',
                                    padding: '12px 16px',
                                    borderRadius: '8px',
                                    backgroundColor: (error?.type === 'success') ? '#f0fdf4' : '#fef2f2',
                                    border: `1px solid ${(error?.type === 'success') ? '#bbf7d0' : '#fecaca'}`,
                                    color: (error?.type === 'success') ? '#166534' : '#991b1b',
                                    fontSize: '14px',
                                    fontWeight: 500,
                                    textAlign: 'center'
                                }}>
                                    <span>{typeof error === 'string' ? error : error.message || 'An error occurred.'}</span>
                                </div>
                            )
                        )}

                        {/* Show confirm button only when current tab has items to submit.
                            Banquet brings its own button — it can be submitted with no items
                            at all (a function booked before its menu is settled), which this
                            condition would never allow. */}
                        {entryMode !== 'banquet' && (
                            (tsrEntryTab === 'scan' && scanItems.length > 0) ||
                            (tsrEntryTab === 'manual' && manualItems.length > 0)
                        ) && !showMatchConfirmModal && !showPriceMismatchModal && !showAutoDiscountModal && !showAutoNcModal && !showManualConfirmModal && !showScanConfirmModal && (
                                <div className="button-container">
                                    <button
                                        className={`confirm-button ${isDeducting ? 'loading' : ''}`}
                                        style={{
                                            height: '52px',
                                            minHeight: '52px',
                                            maxHeight: '52px',
                                            boxSizing: 'border-box',
                                            padding: '0 15px'
                                        }}
                                        onClick={async () => {
                                            // If manual entry tab and has manual items, show confirmation modal first
                                            if (tsrEntryTab === 'manual' && manualItems.length > 0) {
                                                setShowManualConfirmModal(true);
                                                return;
                                            }

                                            // If scan tab and has scan items, show confirmation modal first
                                            if (tsrEntryTab === 'scan' && scanItems.length > 0) {
                                                setShowScanConfirmModal(true);
                                                return;
                                            }

                                            await handleConfirmAndDeduct();
                                        }}
                                        disabled={isDeducting}
                                    >
                                        {isDeducting ? 'Processing...' : 'Confirm Sale Report'}
                                    </button>
                                </div>
                            )}
                    </div>
                </div>
            )}

            {/* Missing Ingredients Modal - Unified for both main ingredients and complimentary items */}
            {showMissingModal && (
                <div className="mi-modal-overlay-unique" onClick={closeMissingModal}>
                    <div className="mi-modal-content-unique" onClick={(e) => e.stopPropagation()}>
                        <div className="mi-modal-header-unique">
                            <h3 className="mi-modal-title-unique">Missing Ingredients</h3>
                            {/* Close button moved to right side inside content header */}
                            <button className="mi-modal-close-unique" onClick={closeMissingModal}>✕</button>
                        </div>

                        <div className="mi-modal-body-unique">
                            <p className="mi-modal-description-unique">
                                The following ingredients are not available in stock. Please select how to handle each item:
                            </p>
                            {/* Global Select All / Deselect All button */}
                            {/* Search bar and Global Select All container */}
                            {(() => {
                                const grouped = (missingIngredients || []).reduce((acc, ing) => {
                                    (acc[ing.itemName] = acc[ing.itemName] || []).push(ing);
                                    return acc;
                                }, {});

                                const filteredEntries = Object.entries(grouped)
                                    .filter(([itemName, ingredients]) => {
                                        const q = miSearchQuery.toLowerCase();
                                        if (!q) return true;
                                        // Match on parent item name
                                        if (itemName.toLowerCase().includes(q)) return true;
                                        // Match on any ingredient / preparation name inside the group
                                        return ingredients.some(ing => {
                                            const ingName = (ing.ingredientName || ing.preparationName || '').toLowerCase();
                                            return ingName.includes(q);
                                        });
                                    })
                                    .sort(([a, aIngs], [b, bIngs]) => {
                                        const q = miSearchQuery.toLowerCase();
                                        if (!q) return 0;
                                        // Score: 0 = parent starts with q, 1 = parent contains q, 2 = sub-item matches only
                                        const score = (name, ings) => {
                                            if (name.toLowerCase().startsWith(q)) return 0;
                                            if (name.toLowerCase().includes(q)) return 1;
                                            return 2;
                                        };
                                        const sa = score(a, aIngs), sb = score(b, bIngs);
                                        if (sa !== sb) return sa - sb;
                                        return a.localeCompare(b);
                                    });

                                return (
                                    <>
                                        {/* Search bar and Global Select All container */}
                                        {(() => {
                                            const visibleKeys = [];
                                            filteredEntries.forEach(([_, ingredients]) => {
                                                ingredients.forEach(ing => {
                                                    visibleKeys.push(getMissingIngredientKey(ing));
                                                });
                                            });

                                            const allGlobalSelected = visibleKeys.length > 0 && visibleKeys.every(k => selectedMissingItems[k]);

                                            return (
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', gap: '12px' }}>
                                                    <div style={{ position: 'relative', flex: '1' }}>
                                                        <input
                                                            type="text"
                                                            placeholder="Search items..."
                                                            value={miSearchQuery}
                                                            onChange={(e) => setMiSearchQuery(e.target.value)}
                                                            style={{
                                                                width: '100%',
                                                                padding: '8px 12px 8px 34px',
                                                                fontSize: '13px',
                                                                border: '1px solid #d1d5db',
                                                                borderRadius: '6px',
                                                                outline: 'none',
                                                                boxSizing: 'border-box'
                                                            }}
                                                        />
                                                        <svg
                                                            style={{
                                                                position: 'absolute',
                                                                left: '10px',
                                                                top: '50%',
                                                                transform: 'translateY(-50%)',
                                                                width: '14px',
                                                                height: '14px',
                                                                color: '#9ca3af'
                                                            }}
                                                            fill="none"
                                                            stroke="currentColor"
                                                            strokeWidth="2"
                                                            viewBox="0 0 24 24"
                                                            xmlns="http://www.w3.org/2000/svg"
                                                        >
                                                            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                                        </svg>
                                                    </div>
                                                    <button
                                                        className="mi-select-all-btn-unique"
                                                        style={{ padding: '8px 16px', fontSize: '13px', fontWeight: '600', flexShrink: 0 }}
                                                        onClick={() => {
                                                            const updated = { ...selectedMissingItems };
                                                            visibleKeys.forEach(k => { updated[k] = !allGlobalSelected; });
                                                            setSelectedMissingItems(updated);
                                                        }}
                                                    >
                                                        {allGlobalSelected ? 'Deselect All' : 'Select All'}
                                                    </button>
                                                </div>
                                            );
                                        })()}

                                        {/* Group by itemName */}
                                        {filteredEntries.length === 0 ? (
                                            <div style={{ textAlign: 'center', padding: '24px 0', color: '#6b7280', fontSize: '14px' }}>
                                                No items match your search.
                                            </div>
                                        ) : (
                                            filteredEntries.map(([itemName, ingredients], index) => {
                                                // Check if all ingredients for this item are selected
                                                const allSelected = ingredients.every(ing => {
                                                    const key = getMissingIngredientKey(ing);
                                                    return selectedMissingItems[key];
                                                });

                                                return (
                                                    <div key={itemName} className="mi-item-group-unique">
                                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                                                            <div className="mi-item-title-unique">{index + 1}. {itemName}</div>
                                                            <button
                                                                className="mi-select-all-btn-unique"
                                                                onClick={() => {
                                                                    const updatedSelection = { ...selectedMissingItems };
                                                                    ingredients.forEach(ing => {
                                                                        const key = getMissingIngredientKey(ing);
                                                                        // Toggle: if all are selected, deselect all; otherwise, select all
                                                                        updatedSelection[key] = !allSelected;
                                                                    });
                                                                    setSelectedMissingItems(updatedSelection);
                                                                }}
                                                            >
                                                                {allSelected ? 'Deselect All' : 'Select All'}
                                                            </button>
                                                        </div>
                                                        <div className="mi-ingredients-list-unique">
                                                            {ingredients.map((ingredient, index) => {
                                                                const key = getMissingIngredientKey(ingredient);

                                                                // ✨ CHECK IF THIS IS A PREPARATION
                                                                if (ingredient.isPreparation) {
                                                                    return (
                                                                        <div
                                                                            key={key}
                                                                            className="mi-ingredient-row-unique"
                                                                            style={{
                                                                                borderLeft: '4px solid #8b5cf6',
                                                                                backgroundColor: '#faf5ff',
                                                                                padding: '16px',
                                                                                marginBottom: '12px'
                                                                            }}
                                                                        >
                                                                            <div style={{ marginBottom: '12px' }}>
                                                                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                                                    <div>
                                                                                        <span style={{
                                                                                            fontSize: '14px',
                                                                                            fontWeight: '600',
                                                                                            color: '#7c3aed'
                                                                                        }}>
                                                                                            {ingredient.preparationName}
                                                                                        </span>
                                                                                        <span style={{
                                                                                            marginLeft: '8px',
                                                                                            fontSize: '11px',
                                                                                            color: '#8b5cf6',
                                                                                            fontWeight: '600',
                                                                                            backgroundColor: '#ede9fe',
                                                                                            padding: '2px 6px',
                                                                                            borderRadius: '4px'
                                                                                        }}>
                                                                                            PREPARATION
                                                                                        </span>
                                                                                    </div>
                                                                                    <input
                                                                                        type="checkbox"
                                                                                        checked={Boolean(selectedMissingItems[key])}
                                                                                        onChange={() => handleToggleMissingItem(key)}
                                                                                        className="mi-ingredient-checkbox-unique"
                                                                                    />
                                                                                </div>
                                                                                <div style={{
                                                                                    fontSize: '12px',
                                                                                    color: '#666',
                                                                                    marginTop: '4px'
                                                                                }}>
                                                                                    Required: {formatShortQty(ingredient.preparationQty, 'N/A')}g
                                                                                    {ingredient.totalWeight && (
                                                                                        <span style={{ marginLeft: '8px', color: '#999' }}>
                                                                                            (Yield: {ingredient.totalWeight}g)
                                                                                        </span>
                                                                                    )}
                                                                                </div>
                                                                            </div>

                                                                            <div style={{
                                                                                marginLeft: '20px',
                                                                                paddingLeft: '12px',
                                                                                borderLeft: '2px solid #ddd6fe'
                                                                            }}>
                                                                                <div style={{
                                                                                    fontSize: '12px',
                                                                                    fontWeight: '600',
                                                                                    color: '#ef4444',
                                                                                    marginBottom: '8px'
                                                                                }}>
                                                                                    ⚠️ Missing components:
                                                                                </div>

                                                                                {ingredient.missingComponents?.map((comp, compIdx) => (
                                                                                    <div
                                                                                        key={compIdx}
                                                                                        style={{
                                                                                            display: 'flex',
                                                                                            justifyContent: 'space-between',
                                                                                            padding: '8px 12px',
                                                                                            backgroundColor: '#fff',
                                                                                            border: '1px solid #fee2e2',
                                                                                            borderRadius: '4px',
                                                                                            marginBottom: '6px',
                                                                                            fontSize: '12px'
                                                                                        }}
                                                                                    >
                                                                                        <div style={{ fontWeight: '500' }}>
                                                                                            ✗ {comp.ingredientName}
                                                                                        </div>
                                                                                        <div style={{ display: 'flex', gap: '12px', fontSize: '11px' }}>
                                                                                            <span style={{ color: '#666' }}>
                                                                                                Need: <strong>{formatShortQty(comp.required)}</strong> {comp.unit}
                                                                                            </span>
                                                                                            <span style={{ color: 'var(--success-fg)' }}>
                                                                                                Have: <strong>{formatShortQty(comp.available)}</strong> {comp.unit}
                                                                                            </span>
                                                                                            <span style={{ color: '#ef4444', fontWeight: '600' }}>
                                                                                                Short: {formatShortQty(comp.shortage)} {comp.unit}
                                                                                            </span>
                                                                                        </div>
                                                                                    </div>
                                                                                ))}

                                                                                <div style={{
                                                                                    marginTop: '12px',
                                                                                    padding: '8px 12px',
                                                                                    backgroundColor: '#eff6ff',
                                                                                    borderRadius: '4px',
                                                                                    fontSize: '11px',
                                                                                    color: '#1e40af'
                                                                                }}>
                                                                                    💡 Tip: Stock up on these ingredients or use a different preparation
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    );
                                                                }

                                                                // Regular ingredient (non-preparation)
                                                                return (
                                                                    <div
                                                                        key={key}
                                                                        className="mi-ingredient-row-unique"
                                                                        style={ingredient.isComplimentary ? {
                                                                            borderLeft: '3px solid #f59e0b',
                                                                            backgroundColor: '#fffbeb'
                                                                        } : {}}
                                                                    >
                                                                        <div className="mi-ingredient-left-unique">
                                                                            <span className="mi-ingredient-name-unique">
                                                                                {ingredient.ingredientName}
                                                                                {ingredient.isComplimentary && (
                                                                                    <span style={{
                                                                                        marginLeft: '8px',
                                                                                        fontSize: '11px',
                                                                                        color: '#f59e0b',
                                                                                        fontWeight: '600',
                                                                                        backgroundColor: '#fef3c7',
                                                                                        padding: '2px 6px',
                                                                                        borderRadius: '4px'
                                                                                    }}>
                                                                                        COMPLIMENTARY
                                                                                    </span>
                                                                                )}
                                                                            </span>
                                                                            <span className="mi-ingredient-details-unique">
                                                                                Required: {formatShortQty(ingredient.required)} {ingredient.unit} | Available: {formatShortQty(ingredient.available)} {ingredient.availableUnit || ingredient.unit}
                                                                            </span>
                                                                        </div>
                                                                        <div className="mi-ingredient-right-unique">
                                                                            <input
                                                                                type="checkbox"
                                                                                checked={Boolean(selectedMissingItems[key])}
                                                                                onChange={() => handleToggleMissingItem(key)}
                                                                                className="mi-ingredient-checkbox-unique"
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>
                                                );
                                            })
                                        )}
                                    </>
                                );
                            })()}
                        </div>

                        <div className="mi-modal-footer-unique">
                            <button
                                className="mi-modal-btn-unique mi-btn-negative-unique"
                                onClick={handleConfirmNegativeStock}
                                disabled={getValidSelectedMissingKeys().length === 0}
                            >
                                Confirm & Create Negative Stock
                            </button>

                            <button
                                className="mi-modal-btn-unique mi-btn-remove-unique"
                                onClick={handleRemoveFromRecipe}
                                disabled={getValidSelectedMissingKeys().length === 0}
                            >
                                Remove and Update Recipe
                            </button>
                        </div>
                    </div>
                </div>
            )}
            

            )}


            {/* ═══ Menu Match Confirmation Modal ═══
                First link in the chain. A wrong menu match deducts the wrong recipe,
                so this must be settled before NC/discount/price are even meaningful.
                Non-dismissible by design: silently skipping it is exactly the failure
                mode that let "Butter Chicken Mac & Cheese" deduct Mac & Cheese CHICKEN. */}
            {showMatchConfirmModal && (
                <div className="modal-overlay" style={{ pointerEvents: 'auto' }}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '660px', position: 'relative' }}>
                        <div className="modal-header" style={{ position: 'relative' }}>
                            <h2 style={{ color: '#000', textAlign: 'center', flex: 1 }}>
                                Confirm Menu Matches
                            </h2>
                            <button
                                type="button"
                                className="modal-close"
                                onClick={() => setShowMatchConfirmModal(false)}
                                style={{
                                    position: 'absolute',
                                    right: '16px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    background: 'none',
                                    border: 'none',
                                    fontSize: '22px',
                                    color: '#6b7280',
                                    cursor: 'pointer',
                                    lineHeight: 1,
                                    padding: '4px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                                title="Close"
                                aria-label="Close"
                            >
                                ✕
                            </button>
                        </div>
                        <div className="modal-body">
                            <p className="modal-description" style={{ color: '#555', textAlign: 'left', marginBottom: '14px' }}>
                                We couldn't be certain which menu item these bill lines refer to.
                                Confirming the right one makes sure the correct recipe is deducted —
                                and we'll remember your choice, so you'll only be asked once.
                            </p>

                            <div style={{ maxHeight: '380px', overflowY: 'auto' }}>
                                {matchConfirmations.map((c) => {
                                    const query = matchConfirmSearch[c.scanIndex] || '';
                                    const suggestions = query.trim()
                                        ? getTopMenuSuggestions(query)
                                        : [];
                                    const resolvedName = c.matchedName;
                                    return (
                                        <div key={c.scanIndex} style={{
                                            border: '1px solid #e5e7eb',
                                            borderRadius: '10px',
                                            padding: '12px',
                                            marginBottom: '10px',
                                            background: c.reassigned ? '#f0fdf4' : '#fff'
                                        }}>
                                            <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '2px' }}>
                                                Bill says {c.quantity ? `(qty ${c.quantity})` : ''}
                                            </div>
                                            <div style={{ fontWeight: 700, color: '#111', marginBottom: '8px' }}>
                                                {c.billName}
                                            </div>

                                            <div style={{ fontSize: '12px', color: '#6b7280' }}>Deducts recipe for</div>
                                            <div style={{
                                                fontWeight: 600,
                                                color: c.reassigned ? '#15803d' : '#111',
                                                marginBottom: '6px'
                                            }}>
                                                {resolvedName}
                                                {c.reassigned && <span style={{ fontSize: '12px', fontWeight: 500 }}> — changed</span>}
                                            </div>

                                            {(() => {
                                                // Hide internal scoring jargon ("fuzzy match (score 0.750)",
                                                // "ambiguous … margin …"). Keep only plain-English, actionable
                                                // reasons — collisions and price mismatches.
                                                const r = c.confirmReason || '';
                                                const isJargon = /^fuzzy match|^ambiguous|chosen by exact price|score|margin/i.test(r);
                                                if (!r || c.reassigned || isJargon) return null;
                                                return (
                                                    <div style={{
                                                        fontSize: '12px',
                                                        color: '#b45309',
                                                        background: '#fffbeb',
                                                        border: '1px solid #fde68a',
                                                        borderRadius: '6px',
                                                        padding: '6px 8px',
                                                        marginBottom: '8px'
                                                    }}>
                                                        {r}
                                                    </div>
                                                );
                                            })()}

                                            {/* Runners-up: one tap to correct, no typing needed */}
                                            {c.candidates && c.candidates.length > 1 && (
                                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '8px' }}>
                                                    {c.candidates
                                                        .filter(cand => cand.menu_item_id !== c.resolvedMenuItemId)
                                                        .slice(0, 3)
                                                        .map(cand => (
                                                            <button
                                                                key={cand.menu_item_id}
                                                                type="button"
                                                                onClick={() => handleMatchConfirmReassign(c.scanIndex, { menu_item_id: cand.menu_item_id, name: cand.name, price: cand.price })}
                                                                style={{
                                                                    fontSize: '12px',
                                                                    padding: '4px 8px',
                                                                    borderRadius: '999px',
                                                                    border: '1px solid #d1d5db',
                                                                    background: '#f9fafb',
                                                                    cursor: 'pointer'
                                                                }}
                                                            >
                                                                {cand.name} {cand.price_exact ? '· ₹ matches' : ''}
                                                            </button>
                                                        ))}
                                                </div>
                                            )}

                                            {/* Full menu search for anything not in the shortlist */}
                                            <div style={{ position: 'relative' }}>
                                                <input
                                                    type="text"
                                                    value={query}
                                                    placeholder="Search for a different menu item…"
                                                    onChange={(e) => {
                                                        setMatchConfirmSearch(prev => ({ ...prev, [c.scanIndex]: e.target.value }));
                                                        setMatchConfirmOpenIdx(c.scanIndex);
                                                    }}
                                                    onFocus={() => setMatchConfirmOpenIdx(c.scanIndex)}
                                                    style={{
                                                        width: '100%',
                                                        padding: '7px 10px',
                                                        border: '1px solid #d1d5db',
                                                        borderRadius: '6px',
                                                        fontSize: '13px'
                                                    }}
                                                />
                                                {matchConfirmOpenIdx === c.scanIndex && suggestions.length > 0 && (
                                                    <div style={{
                                                        position: 'absolute',
                                                        top: '100%',
                                                        left: 0,
                                                        right: 0,
                                                        background: '#fff',
                                                        border: '1px solid #d1d5db',
                                                        borderRadius: '6px',
                                                        marginTop: '2px',
                                                        maxHeight: '160px',
                                                        overflowY: 'auto',
                                                        zIndex: 20,
                                                        boxShadow: '0 6px 18px rgba(0,0,0,0.12)'
                                                    }}>
                                                        {suggestions.map(mi => (
                                                            <div
                                                                key={mi.menu_item_id || mi.id}
                                                                onClick={() => handleMatchConfirmReassign(c.scanIndex, mi)}
                                                                style={{ padding: '7px 10px', cursor: 'pointer', fontSize: '13px', borderBottom: '1px solid #f3f4f6' }}
                                                            >
                                                                {getMenuItemName(mi)}
                                                                {mi.price != null && (
                                                                    <span style={{ color: '#6b7280', fontSize: '12px' }}> · ₹{parseFloat(mi.price).toFixed(0)}</span>
                                                                )}
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button
                                className="btn-primary"
                                style={{ width: '100%' }}
                                onClick={handleMatchConfirmSubmit}
                            >
                                Confirm {matchConfirmations.length} Match{matchConfirmations.length === 1 ? '' : 'es'} &amp; Continue
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Auto-Detected NC (Non-Chargeable) Modal */}
            {showAutoNcModal && (
                <div className="modal-overlay tsr-nc-modal-overlay" style={{ pointerEvents: 'auto' }}>
                    <div className="modal-content tsr-nc-modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header tsr-nc-modal-header">
                            <h2>NC - Non Chargeable</h2>
                        </div>
                        <div className="modal-body tsr-nc-modal-body">
                            <div className="tsr-nc-table-wrapper">
                                <table className="tsr-nc-table">
                                    <thead>
                                        <tr>
                                            <th className="tsr-nc-th-item">Item</th>
                                            <th className="tsr-nc-th-tot">
                                                <span className="tsr-nc-desktop-only">Total Qty</span>
                                                <span className="tsr-nc-mobile-only">Total</span>
                                            </th>
                                            <th className="tsr-nc-th-nc">
                                                <span className="tsr-nc-desktop-only">NC Qty</span>
                                                <span className="tsr-nc-mobile-only">NC</span>
                                            </th>
                                            <th className="tsr-nc-th-amt">
                                                <span className="tsr-nc-desktop-only">NC Amount</span>
                                                <span className="tsr-nc-mobile-only">Amount</span>
                                            </th>
                                            <th className="tsr-nc-th-reason">Reason</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {autoDetectedNcItems.map((ncItem, idx) => (
                                            <tr key={idx}>
                                                <td className="tsr-nc-col-item">{ncItem.itemName}</td>
                                                <td className="tsr-nc-col-tot">{ncItem.totalQty}</td>
                                                <td className="tsr-nc-col-nc" style={{ color: 'var(--success-fg)', fontWeight: '700' }}>{ncItem.ncQty}</td>
                                                <td className="tsr-nc-col-amt" style={{ color: 'var(--success-fg)', fontWeight: '600' }}>₹{ncItem.ncAmount.toFixed(2)}</td>
                                                <td className="tsr-nc-col-reason">
                                                    <input
                                                        ref={(el) => {
                                                            ncReasonInputRefs.current[idx] = el;
                                                        }}
                                                        className="tsr-nc-reason-input"
                                                        type="text"
                                                        placeholder="Reason"
                                                        value={ncItem.reason}
                                                        onChange={(e) => {
                                                            const updated = [...autoDetectedNcItems];
                                                            updated[idx] = { ...updated[idx], reason: e.target.value };
                                                            setAutoDetectedNcItems(updated);
                                                        }}
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter') {
                                                                e.preventDefault();
                                                                if (idx + 1 < autoDetectedNcItems.length) {
                                                                    ncReasonInputRefs.current[idx + 1]?.focus();
                                                                } else {
                                                                    ncConfirmBtnRef.current?.focus();
                                                                }
                                                            }
                                                        }}
                                                    />
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                    <tfoot>
                                        <tr style={{ borderTop: '2px solid #333', backgroundColor: 'white' }}>
                                            <td colSpan="3" style={{ fontWeight: '700', textAlign: 'right', padding: '5px 4px 2px 4px', fontSize: '12px' }}>Total NC Amount</td>
                                            <td style={{ textAlign: 'right', fontWeight: '700', color: 'var(--success-fg)', whiteSpace: 'nowrap', padding: '5px 4px 2px 4px', fontSize: '12px' }}>
                                                ₹{autoDetectedNcItems.reduce((sum, d) => sum + d.ncAmount, 0).toFixed(2)}
                                            </td>
                                            <td></td>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>
                        </div>
                        <div className="modal-footer tsr-nc-modal-footer">
                            <button
                                className="tsr-nc-btn-cancel"
                                onClick={() => {
                                    // A4: cancelling must not erase the finding. Tag the
                                    // affected scan rows so the table still shows that NC
                                    // was detected and deliberately ignored, instead of
                                    // the evidence vanishing with the modal.
                                    const ignoredIds = new Set(
                                        autoDetectedNcItems.map(nc => nc.menuItemId).filter(Boolean)
                                    );
                                    if (ignoredIds.size > 0) {
                                        setScanItems(prev => prev.map(row =>
                                            ignoredIds.has(row.apiData?.menuItemId)
                                                ? { ...row, ncIgnored: true }
                                                : row
                                        ));
                                    }
                                    setShowAutoNcModal(false);
                                    setAutoDetectedNcItems([]);
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                ref={ncConfirmBtnRef}
                                className="tsr-nc-btn-confirm"
                                onClick={() => {
                                    // Validate all reasons are filled
                                    const missingReasons = autoDetectedNcItems.filter(item => !item.reason.trim());
                                    if (missingReasons.length > 0) {
                                        alert('Please provide a reason for all NC items before proceeding.');
                                        return;
                                    }

                                    // Add NC items to the existing ncItems state (for wastage recording during TSR submit)
                                    const newNcItems = autoDetectedNcItems.map(item => ({
                                        menuItemId: item.menuItemId,
                                        itemName: item.itemName,
                                        quantity: item.ncQty,
                                        price: item.dbPrice,
                                        notes: item.reason.trim(),
                                        reason: item.reason.trim()
                                    }));
                                    setNcItems(prev => [...prev, ...newNcItems]);

                                    // Take the free plates out of each NC row's quantity. The money
                                    // stays what the bill collected for the paid plates: free plates
                                    // add none, and re-pricing them at the NC value (a fraction of the
                                    // rate on some POS) cut real takings. Only the NC row itself
                                    // changes — another line on the same dish is left alone.
                                    const ncFor = (scanItem) => autoDetectedNcItems.find(nc => (nc.rowKey
                                        ? nc.rowKey === scanItem.rowKey
                                        : (nc.menuItemId === scanItem.menuItemId || nc.menuItemId === scanItem.apiData?.menuItemId)));
                                    const updatedScanItems = scanItems.map(scanItem => {
                                        const ncMatch = ncFor(scanItem);
                                        if (!ncMatch || !(ncMatch.billedQty >= 0)) return scanItem;
                                        const newQty = ncMatch.billedQty;
                                        const keptAmount = newQty > 0 ? (Number(scanItem.apiData?.amount) || 0) : 0;
                                        return {
                                            ...scanItem,
                                            quantity: `${newQty} ${scanItem.quantity?.toString().replace(/^\d+(\.\d+)?\s*/, '') || 'plate'}`,
                                            amount: `₹${keptAmount.toFixed(2)}`,
                                            apiData: {
                                                ...scanItem.apiData,
                                                quantity: newQty,
                                                rate: newQty > 0 ? keptAmount / newQty : 0,
                                                amount: keptAmount
                                            }
                                        };
                                    }).filter(item => item.apiData?.quantity > 0);
                                    setScanItems(updatedScanItems);

                                    // Recalculate total
                                    const newScanSum = updatedScanItems.reduce((acc, scanItem) => acc + (scanItem.apiData?.amount || 0), 0);
                                    const manualSum = manualItems.reduce((acc, item) => acc + (item.apiData?.amount || 0), 0);
                                    setTotalAmount(`₹${(newScanSum + manualSum).toFixed(2)}`);

                                    setShowAutoNcModal(false);

                                    // Chain to next modal: Discount → Mismatch
                                    if (autoDetectedDiscounts.length > 0 || billTotalDiscount > 0) {
                                        setShowAutoDiscountModal(true);
                                    } else if (priceMismatches.length > 0) {
                                        setShowPriceMismatchModal(true);
                                    } else {
                                        setError({
                                            type: 'success',
                                            message: `${newNcItems.length} NC item(s) flagged for wastage recording.`
                                        });
                                    }
                                }}
                            >
                                Confirm
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ══════ Unmapped Items Warning Modal ══════ */}
            {/* Re-used FILE warning. Raised before the file is parsed, so both answers
                are cheap — that is the point of checking the bytes rather than waiting
                for the numbers. The scan loop is parked on a promise while this is up. */}
            {/* ── Wrong day's file ────────────────────────────────────────────────
                The file states another day than the TSR day being filed, or several
                days as one total. Refused by the server — nothing was loaded. */}
            {fileDateRefusal && (
                <div className="modal-overlay" style={{ pointerEvents: 'auto' }}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
                        <div className="modal-header">
                            <h2 style={{ color: '#000', textAlign: 'center', flex: 1, margin: '20px 20px 0 20px', fontSize: '1.3rem', fontWeight: 700 }}>
                                {FILE_DATE_REFUSAL_TITLES[fileDateRefusal.code] || "Wrong day's file"}
                            </h2>
                        </div>
                        <div className="modal-body" style={{ padding: '16px 20px 12px 20px' }}>
                            <p style={{
                                color: '#92400e', backgroundColor: '#fffbeb', border: '1px solid #fde68a',
                                borderRadius: '6px', padding: '10px 12px', textAlign: 'left',
                                marginBottom: '12px', fontSize: '14px', lineHeight: '1.5'
                            }}>
                                {fileDateRefusal.error}
                            </p>
                            <p style={{ color: '#374151', fontSize: '13px', textAlign: 'left' }}>
                                <strong>File:</strong> {fileDateRefusal.fileName}. Nothing has been loaded.
                            </p>
                        </div>
                        <div className="modal-footer" style={{ display: 'flex', padding: '10px 20px 20px 20px' }}>
                            <button
                                type="button"
                                onClick={() => setFileDateRefusal(null)}
                                style={{ flex: 1, padding: '12px 24px', backgroundColor: '#000', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '15px' }}
                            >
                                OK
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* A file that states no day: warned about, and it stays on the sheet. */}
            <NoDateFileWarning
                files={noDateFiles}
                dateLabel={selectedWastageDateLabel}
                onClose={() => setNoDateFiles([])}
            />

            {/* ── Outlet picker ───────────────────────────────────────────────────
                A POS outlet report is filed one outlet at a time, as an ordinary
                TSR: the chosen outlet's lines load into the scan table and go
                through every normal popup and /tsr/submit, carrying the outlet. */}
            {outletPicker && (
                <OutletPickerPopup
                    picker={outletPicker.picker}
                    onCancel={() => setOutletPicker(null)}
                    onPick={(name) => {
                        const { file, confirmReupload } = outletPicker;
                        setOutletPicker(null);
                        processSalesReports({ file, outlet: name, confirmReupload });
                    }}
                />
            )}

            {fileReuseWarning && (
                <div className="modal-overlay" style={{ pointerEvents: 'auto' }}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
                        <div className="modal-header">
                            <h2 style={{ color: '#000', textAlign: 'center', flex: 1, margin: '20px 20px 0 20px', fontSize: '1.3rem', fontWeight: 700 }}>
                                This file has been used before
                            </h2>
                        </div>
                        <div className="modal-body" style={{ padding: '16px 20px 12px 20px' }}>
                            <p style={{
                                color: '#92400e', backgroundColor: '#fffbeb', border: '1px solid #fde68a',
                                borderRadius: '6px', padding: '10px 12px', textAlign: 'left',
                                marginBottom: '14px', fontSize: '14px', lineHeight: '1.5'
                            }}>
                                {fileReuseWarning.error}
                            </p>

                            <div style={{
                                display: 'flex', flexDirection: 'column', gap: '6px',
                                backgroundColor: '#f9fafb', border: '1px solid #e5e7eb',
                                borderRadius: '6px', padding: '10px 12px', marginBottom: '14px',
                                fontSize: '13px', color: '#374151', textAlign: 'left'
                            }}>
                                <div><strong>File:</strong> {fileReuseWarning.fileName}</div>
                                {fileReuseWarning.existing?.dates?.length > 0 && (
                                    <div>
                                        <strong>Already filed for:</strong>{' '}
                                        {fileReuseWarning.existing.dates.map(formatDisplayDate).join(', ')}
                                        {fileReuseWarning.existing.date_count > fileReuseWarning.existing.dates.length &&
                                            ` +${fileReuseWarning.existing.date_count - fileReuseWarning.existing.dates.length} more`}
                                    </div>
                                )}
                                {fileReuseWarning.existing?.sale_ids?.length > 0 && (
                                    <div><strong>Sale{fileReuseWarning.existing.sale_ids.length > 1 ? 's' : ''}:</strong>{' '}
                                        {fileReuseWarning.existing.sale_ids.map((id) => `#${id}`).join(', ')}</div>
                                )}
                            </div>

                            <p style={{ color: '#555', textAlign: 'left', marginBottom: '10px', fontSize: '14px', lineHeight: '1.6' }}>
                                {fileReuseWarning.message}
                            </p>
                            <p style={{ color: '#065f46', fontSize: '13px', textAlign: 'left', margin: 0, fontWeight: 600 }}>
                                Nothing has been scanned yet — no AI cost and no sales recorded.
                            </p>
                        </div>
                        <div className="modal-footer" style={{ display: 'flex', gap: '12px', justifyContent: 'space-between', padding: '10px 20px 20px 20px' }}>
                            <button
                                onClick={() => reuploadDecisionRef.current?.('skip')}
                                style={{
                                    flex: 1, padding: '12px 24px', backgroundColor: '#000', color: 'white',
                                    border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '15px'
                                }}
                            >
                                Skip This File
                            </button>
                            <button
                                onClick={() => reuploadDecisionRef.current?.('use')}
                                style={{
                                    flex: 1, padding: '12px 24px', backgroundColor: '#fff', color: '#000',
                                    border: '1px solid #d1d5db', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '15px'
                                }}
                            >
                                Use It Anyway
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Duplicate / near-duplicate warning. Two ways out, both explicit — the one
                thing it must never do is look like a generic failure, because "that
                didn't work, try again" is how every one of these duplicates was made. */}
            {duplicateWarning && (
                <div className="modal-overlay" style={{ pointerEvents: 'auto' }}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
                        <div className="modal-header">
                            <h2 style={{ color: '#000', textAlign: 'center', flex: 1, margin: '20px 20px 0 20px', fontSize: '1.3rem', fontWeight: 700 }}>
                                {duplicateWarning.title}
                            </h2>
                        </div>
                        <div className="modal-body" style={{ padding: '16px 20px 12px 20px' }}>
                            <p style={{
                                color: '#92400e',
                                backgroundColor: '#fffbeb',
                                border: '1px solid #fde68a',
                                borderRadius: '6px',
                                padding: '10px 12px',
                                textAlign: 'left',
                                marginBottom: '14px',
                                fontSize: '14px',
                                lineHeight: '1.5'
                            }}>
                                {duplicateWarning.error}
                            </p>

                            {duplicateWarning.existing && (
                                <div style={{
                                    display: 'flex', flexDirection: 'column', gap: '6px',
                                    backgroundColor: '#f9fafb', border: '1px solid #e5e7eb',
                                    borderRadius: '6px', padding: '10px 12px', marginBottom: '14px',
                                    fontSize: '13px', color: '#374151', textAlign: 'left'
                                }}>
                                    {duplicateWarning.existing.report_date && (
                                        <div><strong>Already recorded on:</strong> {formatDisplayDate(duplicateWarning.existing.report_date)}</div>
                                    )}
                                    {duplicateWarning.existing.sale_id && (
                                        <div><strong>Sale:</strong> #{duplicateWarning.existing.sale_id}</div>
                                    )}
                                    {duplicateWarning.reportDate && (
                                        <div><strong>You are filing for:</strong> {formatDisplayDate(duplicateWarning.reportDate)}</div>
                                    )}
                                </div>
                            )}

                            <p style={{ color: '#555', textAlign: 'left', marginBottom: '10px', fontSize: '14px', lineHeight: '1.6' }}>
                                {duplicateWarning.message}
                            </p>
                            <p style={{ color: '#065f46', fontSize: '13px', textAlign: 'left', margin: 0, fontWeight: 600 }}>
                                Nothing has been recorded yet — no sales and no stock were deducted.
                            </p>
                        </div>
                        <div className="modal-footer" style={{ display: 'flex', gap: '10px', justifyContent: 'space-between', padding: '12px 20px 20px 20px' }}>
                            <button
                                onClick={handleCancelDuplicate}
                                style={{
                                    flex: 1,
                                    height: '42px',
                                    padding: '0 10px',
                                    backgroundColor: '#000',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    fontWeight: '600',
                                    fontSize: '14px',
                                    whiteSpace: 'nowrap',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleConfirmDuplicate}
                                style={{
                                    flex: 1,
                                    height: '42px',
                                    padding: '0 10px',
                                    backgroundColor: '#fff',
                                    color: '#000',
                                    border: '1px solid #d1d5db',
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    fontWeight: '600',
                                    fontSize: '14px',
                                    whiteSpace: 'nowrap',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                            >
                                Continue Anyway
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showUnmappedWarningModal && (
                <div className="tsr-unmapped-modal-overlay">
                    <div className="tsr-unmapped-modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="tsr-unmapped-modal-header">
                            <h2>Unmapped Items Detected</h2>
                        </div>
                        <div className="tsr-unmapped-modal-body">
                            {/* A3: state the NC loss explicitly. Continuing is allowed,
                                but the user must not discover afterwards that
                                non-chargeable units went unrecorded. */}
                            {unmatchedNcItems.length > 0 && (
                                <p className="tsr-unmapped-alert-box">
                                    <strong>
                                        {unmatchedNcItems.reduce((s, n) => s + (n.ncQty || 0), 0)} non-chargeable unit(s)
                                    </strong>{' '}
                                    on unmapped items will <strong>NOT</strong> be recorded if you continue.
                                    Map these items to record them as NC wastage.
                                </p>
                            )}

                            <div className="tsr-unmapped-list-container">
                                {unmappedItemsList.map((item, idx) => {
                                    // A size name whose price matched no remembered size: still Not
                                    // in Menu, never sent to the nearest size (Review Focus #2).
                                    const priceNote = priceUnresolvedNote(item.priceUnresolved);
                                    return (
                                        <div key={item.menuItemId || `unrec-${idx}`} style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '10px',
                                            padding: '10px 14px',
                                            borderBottom: idx < unmappedItemsList.length - 1 ? '1px solid #f3f4f6' : 'none',
                                            backgroundColor: idx % 2 === 0 ? '#fff' : '#fafafa',
                                            boxSizing: 'border-box'
                                        }}>
                                            <div style={{
                                                width: '8px',
                                                height: '8px',
                                                borderRadius: '50%',
                                                backgroundColor: item.reason === 'nc_unmatched' ? '#7c3aed'
                                                    : item.reason === 'not_in_menu' ? '#ef4444' : '#f59e0b',
                                                flexShrink: 0
                                            }} />
                                            <span style={{ fontSize: '13.5px', fontWeight: 600, color: '#111827', flex: 1, textAlign: 'left', lineHeight: 1.3 }}>
                                                {item.itemName}
                                                {priceNote && (
                                                    <span style={{ display: 'block', fontSize: '12px', fontWeight: 500, color: '#6b7280', marginTop: '2px' }}>
                                                        {priceNote}
                                                    </span>
                                                )}
                                            </span>
                                            <span style={{
                                                fontSize: '11px',
                                                fontWeight: 600,
                                                padding: '3px 8px',
                                                borderRadius: '4px',
                                                backgroundColor: item.reason === 'nc_unmatched' ? '#f5f3ff'
                                                    : item.reason === 'not_in_menu' ? '#fef2f2' : '#fffbeb',
                                                color: item.reason === 'nc_unmatched' ? '#6d28d9'
                                                    : item.reason === 'not_in_menu' ? '#dc2626' : '#d97706',
                                                whiteSpace: 'nowrap',
                                                flexShrink: 0
                                            }}>
                                                {item.reason === 'nc_unmatched'
                                                    ? `Has ${item.ncQty} NC unit${item.ncQty === 1 ? '' : 's'}`
                                                    : item.reason === 'not_in_menu' ? 'Not in Menu' : 'No Ingredients'}
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                        <div className="tsr-unmapped-modal-footer">
                            <button
                                className="tsr-unmapped-btn tsr-unmapped-btn-primary"
                                onClick={handleGoToItemMap}
                            >
                                Map Items Now
                            </button>
                            <button
                                className="tsr-unmapped-btn tsr-unmapped-btn-secondary"
                                onClick={handleContinueWithUnmapped}
                            >
                                Continue Anyway
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Auto-Detected Discount Info Modal - INFORMATIONAL */}
            {showAutoDiscountModal && (
                <div className="tsr-discount-detected-modal-overlay">
                    <div className="tsr-discount-detected-modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="tsr-discount-detected-modal-header">
                            <h2 className="tsr-discount-detected-modal-title">
                                Discounts Detected
                            </h2>
                        </div>
                        <div className="tsr-discount-detected-modal-body">
                            <p className="tsr-discount-detected-modal-description">
                                {autoDetectedDiscounts.length > 0
                                    ? 'The following items were sold at discounted prices. The bill\'s discount column matches the price difference from your menu database.'
                                    : 'Discounts were found in the uploaded bill file.'}
                            </p>

                            {autoDetectedDiscounts.length > 0 && (
                                <div className="tsr-discount-detected-table-wrap">
                                    <Table className="tsr-discount-detected-table">
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead className="tsr-discount-detected-col-item">Item</TableHead>
                                                <TableHead className="tsr-discount-detected-col-menu-price">Menu Price</TableHead>
                                                <TableHead className="tsr-discount-detected-col-billed-price">Billed Price</TableHead>
                                                <TableHead className="tsr-discount-detected-col-disc-unit">Disc/Unit</TableHead>
                                                <TableHead className="tsr-discount-detected-col-qty">Qty</TableHead>
                                                <TableHead className="tsr-discount-detected-col-total-disc">Total Disc</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {autoDetectedDiscounts.map((disc, idx) => (
                                                <TableRow key={idx}>
                                                    <TableCell className="tsr-discount-detected-col-item tsr-discount-detected-item-name">{disc.itemName}</TableCell>
                                                    <TableCell className="tsr-discount-detected-col-menu-price">₹{disc.dbPrice.toFixed(2)}</TableCell>
                                                    <TableCell className="tsr-discount-detected-col-billed-price tsr-discount-detected-value-negative">₹{disc.billedUnitPrice.toFixed(2)}</TableCell>
                                                    <TableCell className="tsr-discount-detected-col-disc-unit tsr-discount-detected-value-warn">-₹{disc.discountPerUnit.toFixed(2)}</TableCell>
                                                    <TableCell className="tsr-discount-detected-col-qty">{disc.quantity}</TableCell>
                                                    <TableCell className="tsr-discount-detected-col-total-disc tsr-discount-detected-value-strong-negative">-₹{disc.totalDiscount.toFixed(2)}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                        <TableFooter>
                                            <TableRow className="tsr-discount-detected-verified-row">
                                                <TableCell colSpan={5} className="tsr-discount-detected-verified-label">Total Discount</TableCell>
                                                <TableCell className="tsr-discount-detected-verified-value">
                                                    -₹{autoDetectedDiscounts.reduce((sum, d) => sum + d.totalDiscount, 0).toFixed(2)}
                                                </TableCell>
                                            </TableRow>
                                        </TableFooter>
                                    </Table>
                                </div>
                            )}

                            {/* Bill Total Discount from the file */}
                            {billTotalDiscount > 0 && (
                                <div className="tsr-discount-detected-bill-total-box">
                                    <span className="tsr-discount-detected-bill-total-label">
                                        Discount
                                    </span>
                                    <span className="tsr-discount-detected-bill-total-value">
                                        -₹{billTotalDiscount.toFixed(2)}
                                    </span>
                                </div>
                            )}
                        </div>
                        <div className="tsr-discount-detected-modal-footer">
                            <button
                                className="tsr-discount-detected-modal-btn"
                                onClick={() => {
                                    setShowAutoDiscountModal(false);
                                    // If there are also genuine price mismatches, show that modal now
                                    if (priceMismatches.length > 0) {
                                        setShowPriceMismatchModal(true);
                                    } else {
                                        setError({
                                            type: 'success',
                                            message: `Discounts acknowledged. ${autoDetectedDiscounts.length} discounted item(s) processed successfully.`
                                        });
                                    }
                                }}
                            >
                                OK
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Price Mismatch Modal - NON-DISMISSIBLE */}
            {showPriceMismatchModal && (
                <div className="tsr-price-mismatch-overlay" style={{ pointerEvents: 'auto' }}>
                    <div className="tsr-price-mismatch-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
                        <div className="tsr-price-mismatch-header">
                            <h2 className="tsr-price-mismatch-title" style={{ color: '#000', margin: 0, textAlign: 'left' }}>Price Mismatch Detected</h2>
                            {/* No close button - user must click the action button */}
                        </div>
                        <div className="tsr-price-mismatch-body">
                            <p className="tsr-price-mismatch-description" style={{ color: '#000', textAlign: 'left' }}>
                                <strong>Warning:</strong> The following items have different prices in the scanned bill compared to your menu database.
                            </p>

                            <div className="tsr-price-mismatch-table-wrapper" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                                <table className="tsr-price-mismatch-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                                    <thead>
                                        <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
                                            <th style={{ padding: '12px 8px', textAlign: 'left', fontWeight: '600' }}>Item Name</th>
                                            <th style={{ padding: '12px 8px', textAlign: 'left', fontWeight: '600' }}>Scanned Price</th>
                                            <th style={{ padding: '12px 8px', textAlign: 'left', fontWeight: '600' }}>Database Price</th>
                                            <th style={{ padding: '12px 8px', textAlign: 'left', fontWeight: '600' }}>Difference</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {priceMismatches.map((mismatch, idx) => (
                                            <tr key={idx} style={{ borderBottom: '1px solid #dee2e6' }}>
                                                <td style={{ padding: '12px 8px 12px 0', fontWeight: '500' }}>{mismatch.itemName}</td>
                                                <td style={{
                                                    padding: '12px 8px',
                                                    textAlign: 'left',
                                                    color: '#000',
                                                    fontWeight: '600'
                                                }}>
                                                    ₹{mismatch.scannedPrice}
                                                </td>
                                                <td style={{
                                                    padding: '12px 8px',
                                                    textAlign: 'left',
                                                    color: '#000',
                                                    fontWeight: '600'
                                                }}>
                                                    ₹{mismatch.databasePrice}
                                                </td>
                                                <td style={{
                                                    padding: '12px 8px',
                                                    textAlign: 'left',
                                                    color: '#000',
                                                    fontWeight: '600'
                                                }}>
                                                    ₹{mismatch.difference}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                        </div>
                        <div className="tsr-price-mismatch-footer" style={{ display: 'flex', gap: '12px', justifyContent: 'center', alignItems: 'center' }}>
                            {/* Button 1: Proceed Anyway → substitute DB price into scan items */}
                            <button
                                className="tsr-price-mismatch-btn tsr-price-mismatch-btn-primary"
                                style={{
                                    backgroundColor: '#000',
                                    color: 'white',
                                    padding: '0 20px',
                                    height: '46px',
                                    minHeight: '46px',
                                    border: 'none',
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    fontWeight: '600',
                                    fontSize: '14px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flex: 1,
                                    boxSizing: 'border-box'
                                }}
                                onClick={() => {
                                    // Substitute DB prices into scanItems for all mismatched items
                                    if (priceMismatches.length > 0) {
                                        const mismatchMap = {};
                                        priceMismatches.forEach(m => {
                                            if (m.menuItemId) {
                                                mismatchMap[m.menuItemId] = {
                                                    dbPrice: parseFloat(m.databasePrice),
                                                    quantity: parseFloat(m.quantity) || 1
                                                };
                                            }
                                        });

                                        setScanItems(prev => prev.map(item => {
                                            const mid = item.apiData?.menuItemId;
                                            if (mid && mismatchMap[mid]) {
                                                const { dbPrice, quantity } = mismatchMap[mid];
                                                const safeQty = parseFloat(item.apiData?.quantity) || quantity || 1;
                                                const newAmount = dbPrice * safeQty;
                                                return {
                                                    ...item,
                                                    amount: `₹${newAmount.toFixed(2)}`,
                                                    apiData: {
                                                        ...item.apiData,
                                                        rate: dbPrice,
                                                        unitPrice: dbPrice,
                                                        amount: newAmount
                                                    }
                                                };
                                            }
                                            return item;
                                        }));

                                        // Recalculate total after DB price substitution
                                        setTimeout(() => {
                                            setScanItems(prev => {
                                                const scanSum = prev.reduce((acc, it) => acc + (it.apiData?.amount || 0), 0);
                                                const manualSum = manualItems.reduce((acc, it) => acc + (it.apiData?.amount || 0), 0);
                                                setTotalAmount(`₹${(scanSum + manualSum).toFixed(2)}`);
                                                return prev;
                                            });
                                        }, 0);
                                    }

                                    setShowPriceMismatchModal(false);
                                    setPriceMismatches([]);
                                    setError({
                                        type: 'success',
                                        message: `Proceeding with database prices (${priceMismatches.length} item(s) price corrected to menu price)`
                                    });
                                }}
                            >
                                Proceed Anyway
                            </button>

                            {/* Button 2: Use scanned price AND update menu DB price */}
                            <button
                                className="tsr-price-mismatch-btn tsr-price-mismatch-btn-secondary"
                                style={{
                                    backgroundColor: '#fff',
                                    color: '#000',
                                    padding: '0 16px',
                                    height: '46px',
                                    minHeight: '46px',
                                    border: '1.5px solid #000',
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    fontWeight: '600',
                                    fontSize: '14px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '6px',
                                    flex: 1,
                                    boxSizing: 'border-box'
                                }}
                                onClick={async () => {
                                    // Build the updates payload — use scanned price for each mismatch
                                    const updates = priceMismatches
                                        .filter(m => m.menuItemId)
                                        .map(m => ({
                                            menuItemId: m.menuItemId,
                                            newPrice: parseFloat(m.scannedPrice)
                                        }));

                                    // Close modal and proceed immediately with scanned prices
                                    setShowPriceMismatchModal(false);
                                    setPriceMismatches([]);
                                    setError({
                                        type: 'info',
                                        message: `Proceeding with scanned prices — updating ${updates.length} menu item price(s) in the background...`
                                    });

                                    // Fire-and-forget: update menu item prices in DB
                                    if (updates.length > 0) {
                                        try {
                                            const token = localStorage.getItem('token');
                                            const businessId = localStorage.getItem('businessId');
                                            const API_BASE = process.env.REACT_APP_API_URL;
                                            const resp = await fetch(`${API_BASE}/menu/items/update-price`, {
                                                method: 'PATCH',
                                                headers: {
                                                    'Authorization': `Bearer ${token}`,
                                                    'Content-Type': 'application/json',
                                                    'x-business-id': businessId
                                                },
                                                body: JSON.stringify({ updates })
                                            });
                                            const result = await resp.json();
                                            if (result.success) {
                                                const names = result.results
                                                    .filter(r => r.success)
                                                    .map(r => r.name)
                                                    .join(', ');
                                                setError({
                                                    type: 'success',
                                                    message: `✅ Menu prices updated: ${names || updates.length + ' item(s)'}. Scanned prices are now the menu prices.`
                                                });
                                            } else {
                                                setError({
                                                    type: 'warning',
                                                    message: `Scanned prices kept — but menu price update failed: ${result.error}`
                                                });
                                            }
                                        } catch (err) {
                                            setError({
                                                type: 'warning',
                                                message: `Scanned prices kept — menu price sync failed (${err.message}). Please update manually.`
                                            });
                                        }
                                    }
                                }}
                            >
                                Update Menu Price &amp; Proceed
                            </button>
                        </div>
                    </div>
                </div>
            )}


            {/* Manual Entry Confirmation Modal */}
            {showManualConfirmModal && (
                <div className="tsr-manual-confirm-overlay" style={{ pointerEvents: 'auto' }}>
                    <div className="tsr-manual-confirm-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
                        <div className="tsr-manual-confirm-header">
                            <h2 style={{ color: '#000', margin: '20px 20px 10px 20px' }}>Confirm Sale Items</h2>
                            <button
                                className="tsr-manual-confirm-close"
                                onClick={() => setShowManualConfirmModal(false)}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    fontSize: '24px',
                                    cursor: 'pointer',
                                    color: '#000'
                                }}
                            >
                                ×
                            </button>
                        </div>
                        <div className="tsr-manual-confirm-body" style={{ padding: '10px 20px' }}>
                            <div className="tsr-table-scroll-wrapper">
                                <Table style={{ width: '100%' }}>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead className="tsr-col-num" style={{ textAlign: 'center', fontSize: '15px', color: '#6b7280' }}>#</TableHead>
                                            <TableHead className="tsr-col-item" style={{ fontSize: '16px' }}>Item</TableHead>
                                            <TableHead className="tsr-col-comp" style={{ fontSize: '16px' }}>Complimentary</TableHead>
                                            <TableHead className="tsr-col-qty" style={{ textAlign: 'center', fontSize: '16px' }}>Qty</TableHead>
                                            <TableHead className="tsr-col-amt" style={{ textAlign: 'right', fontSize: '16px' }}>Amt</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {manualItems.map((item, index) => {
                                            const menuItemId = item.apiData?.menuItemId;
                                            const compItems = menuItemId ? complimentaryItemsMap[menuItemId] : [];

                                            return (
                                                <TableRow key={index}>
                                                    <TableCell className="tsr-col-num" style={{ textAlign: 'center', fontSize: '13px', color: '#9ca3af', fontWeight: 500, verticalAlign: 'middle' }}>{index + 1}</TableCell>
                                                    <TableCell className="tsr-col-item" style={{ fontWeight: 600, color: '#111827', textAlign: 'left' }}>
                                                        <div className="tsr-cell-content">
                                                            {formatTitleCase(item.item)}
                                                            {item.apiData?.notInMenu && <span className="tsr-nim-badge">Not in Menu</span>}
                                                        </div>
                                                    </TableCell>
                                                    <TableCell className="tsr-col-comp">
                                                        <div className="tsr-cell-content">
                                                            {compItems && compItems.length > 0 ? (
                                                                <div className="tsr-comp-tags">
                                                                    {compItems.map((comp, compIndex) => {
                                                                        const compName = comp.name || comp.item_name;
                                                                        const soldQty = normalizeComplimentaryQty(item.quantity, 1);
                                                                        const storedOverrideQty = complimentaryOverrides[menuItemId]?.[compName];
                                                                        const overrideQty = storedOverrideQty === undefined
                                                                            ? soldQty
                                                                            : normalizeComplimentaryQty(storedOverrideQty, 0);
                                                                        return (
                                                                            <div key={`comp-${index}-${compIndex}`} className="tsr-comp-tag-editable">
                                                                                <span className="tsr-comp-tag-name">{compName}</span>
                                                                                <div className="tsr-comp-qty-group">
                                                                                    <span className="tsr-comp-qty-label">x</span>
                                                                                    <input
                                                                                        type="number"
                                                                                        className="tsr-comp-qty-input"
                                                                                        value={String(overrideQty)}
                                                                                        min="0"
                                                                                        onChange={(e) => {
                                                                                            const val = normalizeComplimentaryQty(e.target.value, 0);
                                                                                            setComplimentaryOverrides(prev => ({
                                                                                                ...prev,
                                                                                                [menuItemId]: { ...prev[menuItemId], [compName]: val }
                                                                                            }));
                                                                                        }}
                                                                                    />
                                                                                </div>
                                                                            </div>
                                                                        );
                                                                    })}
                                                                </div>
                                                            ) : (
                                                                <span className="tsr-no-comp">—</span>
                                                            )}
                                                        </div>
                                                    </TableCell>
                                                    <TableCell className="tsr-col-qty" style={{ textAlign: 'center' }}>
                                                        {(() => {
                                                            const rawQty = parseFloat(String(item.quantity).replace(/[^0-9.]/g, '')) || 0;
                                                            return `${rawQty} ${rawQty === 1 ? 'plate' : 'plates'}`;
                                                        })()}
                                                    </TableCell>
                                                    <TableCell className="tsr-col-amt" style={{ textAlign: 'right', fontWeight: 600, color: 'var(--success-fg)' }}>
                                                        {item.amount}
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                        <TableRow style={{ fontWeight: 700, backgroundColor: '#f9fafb' }}>
                                            <TableCell className="tsr-col-num"></TableCell>
                                            <TableCell className="tsr-col-item tsr-total-label-cell">Total</TableCell>
                                            <TableCell className="tsr-col-comp"></TableCell>
                                            <TableCell className="tsr-col-qty" style={{ textAlign: 'center', fontWeight: 800, fontSize: '16px', color: '#111827' }}>
                                                {totalPlatesLabel}
                                            </TableCell>
                                            <TableCell className="tsr-col-amt" style={{ textAlign: 'right', fontWeight: 800, fontSize: '16px', color: '#111827' }}>
                                                {totalAmount}
                                            </TableCell>
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </div>
                        </div>
                        <div className="tsr-manual-confirm-footer" style={{ padding: '10px 20px 20px 20px', marginTop: 0 }}>
                            <button
                                className="confirm-button"
                                style={{
                                    width: '100%',
                                    height: '48px',
                                    backgroundColor: '#000',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    fontWeight: '600',
                                    fontSize: '16px'
                                }}
                                onClick={async () => {
                                    setShowManualConfirmModal(false);
                                    await handleConfirmAndDeduct();
                                }}
                            >
                                Confirm Sale Report
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Scan Bill Confirmation Modal */}
            {showScanConfirmModal && (
                <div className="tsr-scan-confirm-overlay" style={{ pointerEvents: 'auto' }}>
                    <div className="tsr-scan-confirm-content" onClick={(e) => e.stopPropagation()}>
                        <div className="tsr-scan-confirm-header">
                            <h2 className="tsr-scan-confirm-title">Confirm Sale</h2>
                        </div>
                        <div className="tsr-scan-confirm-body">
                            <p>Do you want to proceed with confirming this bill?</p>
                            {/* A4: rows with no usable unit price are silently dropped
                                by the submit filter. Count them with the SAME predicate
                                here (the filter has not run yet at this point) so the
                                exclusion is stated before the user commits. */}
                            {(() => {
                                const parseNum = (val) => {
                                    if (typeof val === 'number') return val;
                                    const n = Number(String(val ?? '').replace(/[^0-9.-]/g, ''));
                                    return Number.isFinite(n) ? n : 0;
                                };
                                const zeroCount = scanItems.filter(row => {
                                    const apiData = row.apiData || row;
                                    if (!(apiData.menuItemId || row.menuItemId)) return false;
                                    return parseNum(apiData.rate || apiData.unitPrice || row.rate) <= 0;
                                }).length;
                                if (zeroCount === 0) return null;
                                return (
                                    <p style={{
                                        marginTop: '10px',
                                        color: '#6b7280',
                                        backgroundColor: '#f9fafb',
                                        border: '1px solid #e5e7eb',
                                        borderRadius: '6px',
                                        padding: '8px 10px',
                                        fontSize: '13px',
                                        textAlign: 'left'
                                    }}>
                                        <strong>{zeroCount} row(s) with ₹0 amount</strong> will be excluded from this submission.
                                    </p>
                                );
                            })()}
                        </div>
                        <div className="tsr-scan-confirm-footer">
                            <button
                                className="tsr-scan-confirm-btn tsr-scan-confirm-btn-cancel"
                                onClick={() => setShowScanConfirmModal(false)}
                            >
                                Cancel
                            </button>
                            <button
                                className="tsr-scan-confirm-btn tsr-scan-confirm-btn-primary"
                                onClick={async () => {
                                    setShowScanConfirmModal(false);
                                    await handleConfirmAndDeduct();
                                }}
                            >
                                Confirm
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Discount Modal */}
            {showDiscountModal && (
                <div
                    className="modal-overlay discount-modal-overlay"
                    style={{ pointerEvents: 'auto' }}
                    onClick={(e) => {
                        if (e.target === e.currentTarget) {
                            setShowDiscountModal(false);
                            setDiscountForm({ itemName: '', menuItemId: null, originalPrice: 0, discountPercentage: '' });
                            setDiscountSuggestions([]);
                            setShowDiscountSuggestions(false);
                        }
                    }}
                >
                    <div className="modal-content discount-modal-card" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
                        <div className="modal-header discount-modal-header">
                            <h2 className="discount-modal-title" style={{ color: '#000', textAlign: 'center', flex: 1 }}>Add Discount</h2>
                            <button
                                onClick={() => {
                                    setShowDiscountModal(false);
                                    setDiscountForm({ itemName: '', menuItemId: null, originalPrice: 0, discountPercentage: '' });
                                    setDiscountSuggestions([]);
                                    setShowDiscountSuggestions(false);
                                }}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    fontSize: '24px',
                                    cursor: 'pointer',
                                    color: '#000000'
                                }}
                            >
                                ×
                            </button>
                        </div>
                        <div className="modal-body discount-modal-body" style={{ textAlign: 'left' }}>
                            {/* Item Name Search */}
                            <div style={{ marginBottom: '8px', position: 'relative' }}>
                                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', color: '#000000', textAlign: 'left' }}>
                                    Item Name *
                                </label>
                                <input
                                    type="text"
                                    value={discountForm.itemName}
                                    onChange={handleDiscountItemNameChange}
                                    placeholder="Search for menu item..."
                                    style={{
                                        width: '100%',
                                        padding: '10px',
                                        border: '1px solid #ddd',
                                        borderRadius: '8px',
                                        fontSize: '14px',
                                        minHeight: '48px'
                                    }}
                                />
                                {showDiscountSuggestions && discountSuggestions.length > 0 && (
                                    <div style={{
                                        position: 'absolute',
                                        top: '100%',
                                        left: 0,
                                        right: 0,
                                        backgroundColor: 'white',
                                        border: '1px solid #ddd',
                                        borderTop: 'none',
                                        borderRadius: '0 0 8px 8px',
                                        maxHeight: '200px',
                                        overflowY: 'auto',
                                        zIndex: 1000,
                                        boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                                    }}>
                                        {discountSuggestions.map((item, idx) => (
                                            <div
                                                key={idx}
                                                onClick={() => handleDiscountItemSelect(item)}
                                                style={{
                                                    padding: '10px',
                                                    cursor: 'pointer',
                                                    borderBottom: idx < discountSuggestions.length - 1 ? '1px solid #f0f0f0' : 'none',
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center'
                                                }}
                                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
                                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                                            >
                                                <span style={{ fontWeight: '500' }}>{item.name}</span>
                                                <span style={{ color: '#666', fontSize: '12px' }}>₹{item.price}</span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Original Price (Read-only, auto-filled) */}
                            {discountForm.menuItemId && (
                                <div style={{ marginBottom: '20px' }}>
                                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#333', textAlign: 'left' }}>
                                        Original Price
                                    </label>
                                    <input
                                        type="text"
                                        value={`₹${discountForm.originalPrice.toFixed(2)}`}
                                        readOnly
                                        style={{
                                            width: '100%',
                                            padding: '10px',
                                            border: '1px solid #ddd',
                                            borderRadius: '8px',
                                            fontSize: '14px',
                                            backgroundColor: '#f5f5f5',
                                            color: '#666'
                                        }}
                                    />
                                </div>
                            )}

                            {/* Discount Percentage */}
                            <div style={{ marginBottom: '4px' }}>
                                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', color: '#000000', textAlign: 'left' }}>
                                    Discount Percentage (%) *
                                </label>
                                <input
                                    type="number"
                                    min="0"
                                    max="100"
                                    step="0.1"
                                    value={discountForm.discountPercentage}
                                    onChange={(e) => setDiscountForm(prev => ({ ...prev, discountPercentage: e.target.value }))}
                                    placeholder="Enter discount %"
                                    style={{
                                        width: '100%',
                                        padding: '10px',
                                        border: '1px solid #ddd',
                                        borderRadius: '8px',
                                        fontSize: '14px',
                                        minHeight: '48px'
                                    }}
                                />
                            </div>

                            {/* Discounted Price Preview */}
                            {discountForm.menuItemId && discountForm.discountPercentage && (
                                <div style={{
                                    padding: '15px',
                                    backgroundColor: '#f0fdf4',
                                    borderRadius: '8px',
                                    border: '1px solid #86efac'
                                }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <span style={{ fontWeight: '600', color: '#166534' }}>New Price:</span>
                                        <span style={{ fontSize: '18px', fontWeight: '700', color: '#166534' }}>
                                            ₹{(discountForm.originalPrice * (1 - parseFloat(discountForm.discountPercentage || 0) / 100)).toFixed(2)}
                                        </span>
                                    </div>
                                    <div style={{ marginTop: '5px', fontSize: '12px', color: '#166534', width: '100%', textAlign: 'left' }}>
                                        You save: ₹{(discountForm.originalPrice * (parseFloat(discountForm.discountPercentage || 0) / 100)).toFixed(2)}
                                    </div>
                                </div>
                            )}

                            {/* Existing Discounts List */}
                            {discountedItems.length > 0 && (
                                <div style={{ marginTop: '8px', paddingTop: '10px', borderTop: '1px solid #e5e7eb', textAlign: 'left' }}>
                                    <h3 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '10px', color: '#333' }}>
                                        Applied Discounts ({discountedItems.length})
                                    </h3>
                                    <div className="discount-applied-list" style={{ maxHeight: '150px', overflowY: 'auto' }}>
                                        {discountedItems.map((item, index) => (
                                            <div
                                                key={index}
                                                className="discount-applied-item"
                                                style={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                    padding: '8px 10px',
                                                    backgroundColor: '#f9fafb',
                                                    borderRadius: '6px',
                                                    marginBottom: '8px'
                                                }}
                                            >
                                                <div style={{ flex: 1 }}>
                                                    <div style={{ fontWeight: '500', fontSize: '13px' }}>{item.itemName}</div>
                                                    <div style={{ fontSize: '11px', color: '#666' }}>
                                                        {item.discountPercentage}% off • ₹{item.originalPrice.toFixed(2)} → ₹{item.discountedPrice.toFixed(2)}
                                                    </div>
                                                </div>
                                                <button
                                                    onClick={() => handleRemoveDiscount(index)}
                                                    style={{
                                                        background: 'none',
                                                        border: 'none',
                                                        color: '#ef4444',
                                                        cursor: 'pointer',
                                                        fontSize: '18px',
                                                        padding: '0 8px'
                                                    }}
                                                >
                                                    <FontAwesomeIcon icon={faTrash} />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="modal-footer discount-modal-footer" style={{ display: 'flex', gap: '12px', justifyContent: 'space-between' }}>
                            <button
                                className="discount-action-btn discount-action-btn-cancel"
                                onClick={() => {
                                    setShowDiscountModal(false);
                                    setDiscountForm({ itemName: '', menuItemId: null, originalPrice: 0, discountPercentage: '' });
                                    setDiscountSuggestions([]);
                                    setShowDiscountSuggestions(false);
                                }}
                                style={{
                                    flex: 1,
                                    padding: '12px 24px',
                                    backgroundColor: '#fff',
                                    color: '#374151',
                                    border: '1px solid #d1d5db',
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    fontWeight: '600',
                                    fontSize: '16px'
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                className="discount-action-btn discount-action-btn-primary"
                                onClick={handleDiscountSubmit}
                                style={{
                                    flex: 1,
                                    padding: '12px 24px',
                                    backgroundColor: '#000',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    fontWeight: '600',
                                    fontSize: '16px'
                                }}
                            >
                                Add Discount
                            </button>
                        </div>
                    </div>
                </div>
            )}


            {/* Success Modal */}
            {showSuccessModal && (
                <div style={{
                    position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999
                }}>
                    <div style={{
                        width: 'min(380px, 90vw)', background: '#fff', borderRadius: 12,
                        boxShadow: '0 8px 32px rgba(0,0,0,0.12)', padding: '32px 24px',
                        textAlign: 'center'
                    }}>
                        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '16px' }}>
                            <div style={{
                                width: '60px', height: '60px', borderRadius: '50%', border: '3px solid var(--success-fg)',
                                backgroundColor: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center'
                            }}>
                                <Check size={36} color="var(--success-fg)" strokeWidth={3} />
                            </div>
                        </div>
                        <div style={{ fontSize: '18px', fontWeight: '600', marginBottom: '16px', color: '#1f2937' }}>
                            Sales Bill Uploaded Successfully
                        </div>
                        {lastSaleNc && lastSaleNc.plates > 0 && (
                            <div style={{ fontSize: '14px', color: '#374151', marginTop: '-8px', marginBottom: '16px' }}>
                                {lastSaleNc.plates} NC plate{lastSaleNc.plates === 1 ? '' : 's'} recorded as Non Chargeable
                            </div>
                        )}
                        <StockAbsorbedNote countDateLabel={lastSaleCountDate ? formatDisplayDate(lastSaleCountDate) : null} />
                        <NcStockWarnings warnings={lastSaleNc?.warnings} />
                        <button
                            onClick={async () => {
                                setShowSuccessModal(false);
                                setLastSaleNc(null);
                                setLastSaleCountDate(null);

                                // Navigate to scan bill tab
                                setTsrEntryTab('scan');

                                if (fromLockModal) {
                                    if (typeof onRecoveryTsrCompleted === 'function') {
                                        onRecoveryTsrCompleted();
                                    }
                                    setTimeout(() => {
                                        goTo && goTo('dashboard');
                                    }, 250);
                                    return;
                                }

                                // An outlet report is filed outlet by outlet: the same
                                // file comes back with the picker for the next one
                                // (the outlet just filed now shows as Filed).
                                const filedOutlet = scanOutlet;

                                // Clear the sales data after modal is closed
                                setScanItems([]);
                                setManualItems([]);
                                setNcItems([]); // Clear NC items as well
                                setDiscountedItems([]);
                                setAutoDetectedDiscounts([]);
                                setBillTotalDiscount(0);
                                setTotalAmount('₹0');
                                setSelectedFiles([]);
                                setIsOnlineOrderMode(false); // Disable online order mode after successful sale

                                // If onComplete callback is provided (during onboarding), call it
                                if (onComplete) {
                                    onComplete();
                                } else if (filedOutlet?.file) {
                                    processSalesReports({ file: filedOutlet.file, confirmReupload: filedOutlet.confirmReupload });
                                }
                            }}
                            style={{
                                padding: '10px 24px', backgroundColor: '#000000', color: '#fff', border: 'none',
                                borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer'
                            }}
                        >
                            OK
                        </button>
                    </div>
                </div>
            )}

            {/* TSR Processing Modal */}
            {isScanning && (
                <div className="tsr-processing-overlay">
                    <div className="tsr-processing-content">
                        <div className="tsr-processing-spinner"></div>
                        <h3>Processing Your Receipt{selectedFiles.length > 1 ? 's' : ''}</h3>
                        <p className="tsr-processing-file-info">
                            {fileProcessingProgress.total > 0 && `File ${Math.ceil(fileProcessingProgress.current)} of ${fileProcessingProgress.total}`}
                        </p>
                        <div className="tsr-processing-progress">
                            <div
                                className="tsr-progress-bar"
                                style={{ width: `${scanProgress}%` }}
                            ></div>
                        </div>
                        <p className="tsr-processing-percent">{scanProgress.toFixed(0)}% Complete</p>
                        <p className="tsr-processing-note">Review required</p>
                        <button
                            type="button"
                            className="tsr-processing-cancel-btn"
                            onClick={handleCancelScan}
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            )}

            {/* Processing Overlay */}
            {isDeducting && (
                <div className="processing-overlay" style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 9999
                }}>
                    <div className="processing-content" style={{
                        backgroundColor: 'white',
                        borderRadius: '12px',
                        padding: '40px',
                        maxWidth: '400px',
                        width: '90%',
                        textAlign: 'center',
                        boxShadow: '0 10px 40px rgba(0,0,0,0.3)'
                    }}>
                        <div className="processing-spinner" style={{
                            width: '50px',
                            height: '50px',
                            border: '4px solid #f3f3f3',
                            borderTop: '4px solid #000',
                            borderRadius: '50%',
                            animation: 'spin 1s linear infinite',
                            margin: '0 auto 20px'
                        }}></div>

                        <h3 style={{ margin: '0 0 10px 0', fontSize: '24px', color: '#333' }}>
                            Processing Your Sale
                        </h3>

                        <p className="processing-message" style={{
                            fontSize: '14px',
                            color: '#666',
                            marginBottom: '20px',
                            minHeight: '20px'
                        }}>
                            {processingMessage}
                        </p>

                        <div className="processing-progress" style={{
                            width: '100%',
                            height: '8px',
                            backgroundColor: '#f0f0f0',
                            borderRadius: '4px',
                            overflow: 'hidden',
                            marginBottom: '10px'
                        }}>
                            <div
                                className="progress-bar"
                                style={{
                                    width: `${processingProgress}%`,
                                    height: '100%',
                                    backgroundColor: '#000',
                                    transition: 'width 0.3s ease'
                                }}
                            ></div>
                        </div>

                        <p style={{ fontSize: '14px', fontWeight: '600', color: '#000', margin: 0 }}>
                            {processingProgress.toFixed(0)}% Complete
                        </p>
                    </div>
                </div>
            )}

            {/* No TSR Recorded Modal */}
            {showNoTSRModal && (
                <div style={{
                    position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999
                }}>
                    <div style={{
                        width: 'min(420px, 90vw)', background: '#fff', borderRadius: 12,
                        boxShadow: '0 8px 32px rgba(0,0,0,0.12)', padding: '32px 24px',
                        textAlign: 'center'
                    }}>
                        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '20px' }}>
                            <div style={{
                                width: '52px',
                                height: '52px',
                                borderRadius: '50%',
                                border: '2px solid #ef4444',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}>
                                <FontAwesomeIcon icon={faXmark} style={{ color: '#ef4444', fontSize: '20px' }} />
                            </div>
                        </div>

                        <div style={{
                            fontSize: '20px',
                            fontWeight: '700',
                            marginBottom: '12px',
                            color: '#1f2937'
                        }}>
                            No TSR Recorded
                        </div>

                        <div style={{
                            fontSize: '15px',
                            color: '#000000',
                            marginBottom: '28px',
                            lineHeight: '1.6'
                        }}>
                            No sales have been recorded.
                        </div>

                        <div style={{ display: 'flex', gap: '12px' }}>
                            <button
                                onClick={() => setShowNoTSRModal(false)}
                                style={{
                                    flex: 1,
                                    padding: '12px 24px',
                                    backgroundColor: '#f3f4f6',
                                    color: '#111827',
                                    border: '1px solid #e5e7eb',
                                    borderRadius: '8px',
                                    fontSize: '15px',
                                    fontWeight: '600',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease'
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={async () => {
                                    try {
                                        const API_BASE = process.env.REACT_APP_API_URL;
                                        const token = localStorage.getItem('token');
                                        const businessId = localStorage.getItem('businessId');

                                        const reportDate = selectedDate || targetDate || getISTDate();

                                        const response = await fetch(`${API_BASE}/tsr/submit-no-sales`, {
                                            method: 'POST',
                                            headers: {
                                                'Content-Type': 'application/json',
                                                'Authorization': `Bearer ${token}`,
                                                'x-business-id': businessId
                                            },
                                            body: JSON.stringify({ reportDate })
                                        });

                                        const result = await response.json();

                                        if (result.success) {
                                            setShowNoTSRModal(false);

                                            // If from onboarding, complete the onboarding flow
                                            if (isOnboarding && onComplete) {
                                                onComplete();
                                                return;
                                            }

                                            // If from lock modal, navigate to dashboard and force TSR lock check
                                            if (fromLockModal) {
                                                if (typeof onRecoveryTsrCompleted === 'function') {
                                                    onRecoveryTsrCompleted();
                                                }
                                            }

                                            // Navigate immediately to dashboard (Home) for all regular usage
                                            if (goTo) goTo('dashboard');
                                        } else {
                                            // Check if error is due to wastage not being recorded (skip during onboarding)
                                            if (result.code === 'WASTAGE_NOT_RECORDED' && !isOnboarding) {
                                                setShowNoTSRModal(false);
                                                setShowWastageWarningModal(true);
                                            } else {
                                                console.error('❌ Failed to record No TSR:', result.error);
                                                // Alert the user, then close the modal and navigate to dashboard anyway
                                                alert('Failed to record No TSR: ' + (result.message || result.error));
                                                setShowNoTSRModal(false);
                                                if (goTo) goTo('dashboard');
                                            }
                                        }
                                    } catch (error) {
                                        console.error('❌ Error recording No TSR:', error);
                                        alert('Error recording No TSR. Please check your connection.');
                                        setShowNoTSRModal(false);
                                        if (goTo) goTo('dashboard');
                                    }
                                }}
                                style={{
                                    flex: 1,
                                    padding: '12px 24px',
                                    backgroundColor: '#000000',
                                    color: '#fff',
                                    border: 'none',
                                    borderRadius: '8px',
                                    fontSize: '15px',
                                    fontWeight: '600',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = '#1f2937';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = '#000000';
                                }}
                            >
                                Confirm
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Wastage Warning Modal */}
            {showWastageWarningModal && !dateStatus.wastageClosed && (
                <div style={{
                    position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999
                }}>
                    <div style={{
                        width: 'min(490px, 90vw)', minHeight: '234px', background: '#fff', borderRadius: 12,
                        boxShadow: '0 8px 32px rgba(0,0,0,0.12)', padding: '16px',
                        textAlign: 'center', display: 'flex', flexDirection: 'column',
                        justifyContent: 'center', alignItems: 'center', gap: '12px'
                    }}>
                        <div style={{ display: 'flex', justifyContent: 'center' }}>
                            <div style={{
                                width: '80px', height: '80px', borderRadius: '50%',
                                backgroundColor: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center'
                            }}>
                                <FontAwesomeIcon icon={faExclamationTriangle} size="3x" color="#f59e0b" />
                            </div>
                        </div>
                        <div style={{ fontSize: '1.125rem', fontWeight: '600', margin: '-7px 0 10px 0', color: '#000000', lineHeight: 1.3 }}>
                            Please record wastage for {selectedWastageDateLabel}<br />before submitting Sales Bill for this date
                        </div>
                        <button
                            onClick={() => {
                                setShowWastageWarningModal(false);
                                if (onBeforeNavigateToWastage) {
                                    onBeforeNavigateToWastage(selectedWastageDate);
                                }
                                if (goTo) {
                                    goTo('inventory-stock-out', { recoveryDate: selectedWastageDate });
                                }
                            }}
                            style={{
                                padding: '8px 16px', backgroundColor: '#000000', color: '#fff', border: 'none',
                                borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer',
                                width: 'auto', marginBottom: '12px'
                            }}
                        >
                            Go to Wastage
                        </button>
                        {/* The server still asks at submit; this only undoes a mis-tap on the calendar. */}
                        {userPickedDateRef.current && (
                            <button
                                type="button"
                                onClick={() => setShowWastageWarningModal(false)}
                                style={{
                                    padding: '8px 16px', backgroundColor: '#fff', color: '#000', border: '1px solid #d1d5db',
                                    borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer',
                                    width: 'auto', marginBottom: '12px'
                                }}
                            >
                                Choose another date
                            </button>
                        )}
                    </div>
                </div>
            )}

            {/* NC (No Charge) Modal */}
            {showNcModal && (
                <div style={{
                    position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999
                }}>
                    <div style={{
                        width: 'min(450px, 90vw)', background: '#fff', borderRadius: 12,
                        boxShadow: '0 8px 32px rgba(0,0,0,0.12)', padding: '24px',
                        maxHeight: '85vh', overflowY: 'auto'
                    }}>
                        <h3 style={{ marginBottom: '20px', fontSize: '1.2rem', fontWeight: '600' }}>
                            Add NC Item (Owner Use)
                        </h3>

                        {ncModalError && (
                            <div style={{
                                backgroundColor: '#fee2e2', border: '1px solid #fca5a5',
                                borderRadius: '6px', padding: '10px', marginBottom: '15px',
                                color: '#991b1b', fontSize: '0.875rem'
                            }}>
                                {ncModalError}
                            </div>
                        )}

                        <div style={{ marginBottom: '16px' }}>
                            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', fontSize: '0.9rem' }}>
                                Item Name
                            </label>
                            <div className="tsr-nc-search-container" style={{ position: 'relative' }}>
                                <input
                                    type="text"
                                    value={ncForm.itemName}
                                    onChange={handleNcItemNameChange}
                                    placeholder="Search for menu item..."
                                    style={{
                                        width: '100%', padding: '10px', border: '1px solid #d1d5db',
                                        borderRadius: '6px', fontSize: '0.95rem'
                                    }}
                                    autoFocus
                                />
                                {showNcSuggestions && ncSuggestions.length > 0 && (
                                    <div
                                        className="tsr-nc-suggestions"
                                        style={{
                                            position: 'absolute', top: '100%', left: 0, right: 0,
                                            backgroundColor: '#fff', border: '1px solid #d1d5db',
                                            borderRadius: '6px', marginTop: '4px', maxHeight: '200px',
                                            overflowY: 'auto', boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                                            zIndex: 10000
                                        }}
                                    >
                                        {ncSuggestions.map((item) => (
                                            <div
                                                key={item.menu_item_id}
                                                onMouseDown={(e) => {
                                                    e.preventDefault();
                                                    handleNcItemSelect(item);
                                                }}
                                                style={{
                                                    padding: '10px 12px', cursor: 'pointer',
                                                    borderBottom: '1px solid #e5e7eb',
                                                    transition: 'background-color 0.2s',
                                                    backgroundColor: 'transparent'
                                                }}
                                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                            >
                                                <div style={{ fontWeight: '500', fontSize: '0.9rem', pointerEvents: 'none' }}>
                                                    {item.name}
                                                </div>
                                                <div style={{ fontSize: '0.8rem', color: '#6b7280', pointerEvents: 'none' }}>
                                                    Price: ₹{item.price || 0}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px', marginBottom: '20px' }}>
                            <div>
                                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', fontSize: '0.9rem' }}>
                                    Quantity
                                </label>
                                <input
                                    type="number"
                                    min="1"
                                    value={ncForm.quantity}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        // Allow empty string during typing
                                        if (val === '') {
                                            setNcForm({ ...ncForm, quantity: '' });
                                        } else {
                                            const numVal = parseInt(val);
                                            // Only accept values >= 1
                                            if (!isNaN(numVal) && numVal >= 1) {
                                                setNcForm({ ...ncForm, quantity: numVal });
                                            }
                                        }
                                    }}
                                    onBlur={(e) => {
                                        // On blur, ensure minimum value of 1
                                        const val = parseInt(e.target.value);
                                        if (isNaN(val) || val < 1) {
                                            setNcForm({ ...ncForm, quantity: 1 });
                                        }
                                    }}
                                    style={{
                                        width: '100%', padding: '10px', border: '1px solid #d1d5db',
                                        borderRadius: '6px', fontSize: '0.95rem'
                                    }}
                                />
                            </div>

                            <div>
                                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', fontSize: '0.9rem' }}>
                                    Price (per item)
                                </label>
                                <input
                                    type="text"
                                    value={ncForm.price ? `₹${ncForm.price}` : ''}
                                    readOnly
                                    style={{
                                        width: '100%', padding: '10px', border: '1px solid #d1d5db',
                                        borderRadius: '6px', fontSize: '0.95rem', backgroundColor: '#f9fafb',
                                        color: '#6b7280'
                                    }}
                                    placeholder="Select an item first"
                                />
                            </div>
                        </div>

                        <div style={{ display: 'flex', gap: '10px' }}>
                            <button
                                onClick={() => {
                                    setShowNcModal(false);
                                    setNcForm({ itemName: '', quantity: 1, price: '' });
                                    setNcModalError('');
                                    setNcSuggestions([]);
                                    setShowNcSuggestions(false);
                                    setSelectedNcMenuItemId(null);
                                }}
                                style={{
                                    flex: 1, padding: '10px', backgroundColor: '#f3f4f6',
                                    color: '#374151', border: 'none', borderRadius: '8px',
                                    fontSize: '0.95rem', fontWeight: '500', cursor: 'pointer'
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleNcSubmit}
                                style={{
                                    flex: 1, padding: '10px', backgroundColor: '#000',
                                    color: '#fff', border: 'none', borderRadius: '8px',
                                    fontSize: '0.95rem', fontWeight: '500', cursor: 'pointer'
                                }}
                            >
                                Add NC Item
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* NC Success Modal */}
            {showNcSuccessModal && (
                <div style={{
                    position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999
                }}>
                    <div style={{
                        width: 'min(420px, 90vw)', background: '#fff', borderRadius: 12,
                        boxShadow: '0 8px 32px rgba(0,0,0,0.12)', padding: '24px',
                        textAlign: 'center'
                    }}>
                        <div style={{
                            width: '64px', height: '64px', borderRadius: '50%',
                            backgroundColor: '#dcfce7', display: 'flex', alignItems: 'center',
                            justifyContent: 'center', margin: '0 auto 20px'
                        }}>
                            <FontAwesomeIcon icon={faCheckCircle} size="2x" color="#16a34a" />
                        </div>
                        <h3 style={{ fontSize: '1.3rem', fontWeight: '600', marginBottom: '12px' }}>
                            NC Items Recorded!
                        </h3>
                        <p style={{ color: '#6b7280', marginBottom: '16px', fontSize: '0.9rem' }}>
                            The following items have been recorded as wastage (Owner Use):
                        </p>
                        <div style={{
                            backgroundColor: '#f9fafb', borderRadius: '8px',
                            padding: '12px', marginBottom: '20px', textAlign: 'left'
                        }}>
                            {ncSuccessMessage.map((item, index) => (
                                <div key={index} style={{
                                    padding: '6px 0', fontSize: '0.85rem',
                                    borderBottom: index < ncSuccessMessage.length - 1 ? '1px solid #e5e7eb' : 'none'
                                }}>
                                    • {item}
                                </div>
                            ))}
                        </div>
                        <button
                            onClick={() => {
                                setShowNcSuccessModal(false);
                                setNcSuccessMessage([]);
                            }}
                            style={{
                                width: '100%', padding: '12px', backgroundColor: '#000',
                                color: '#fff', border: 'none', borderRadius: '8px',
                                fontSize: '0.95rem', fontWeight: '600', cursor: 'pointer'
                            }}
                        >
                            OK
                        </button>
                    </div>
                </div>
            )}

            {/* ========== TSR REVERT MODAL (LOCALHOST / DEV ONLY) ========== */}
            {showTsrRevertModal && isLocalhost && (
                <div style={{
                    position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.55)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    zIndex: 99999, padding: '16px'
                }}>
                    <div style={{
                        background: '#fff', borderRadius: '16px', padding: '28px 24px',
                        maxWidth: '520px', width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
                        maxHeight: '85vh', overflowY: 'auto'
                    }}>
                        {/* Header */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <span style={{ fontSize: '22px' }}>↩</span>
                                <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: '700', color: '#111' }}>Revert TSR</h2>
                            </div>
                            <button
                                onClick={() => { setShowTsrRevertModal(false); setTsrRevertSuccess(null); setTsrRevertError(null); setTsrRevertConfirmId(null); }}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '22px', color: '#6b7280', lineHeight: 1 }}
                            >✕</button>
                        </div>
                        <p style={{ color: '#000000', fontSize: '0.82rem', fontWeight: '600', marginBottom: '4px' }}>
                            DEVELOPMENT ONLY — Not available in production
                        </p>
                        <p style={{ color: '#6b7280', fontSize: '0.85rem', marginBottom: '20px' }}>
                            Select a TSR to fully revert. This will restore all deducted stock, delete the sale record, and reset daily operations status.
                        </p>

                        {/* Success */}
                        {tsrRevertSuccess && (
                            <div style={{ background: '#ffffff', border: '1px solid #000000', borderRadius: '10px', padding: '16px', marginBottom: '16px' }}>
                                <p style={{ color: '#000000', fontWeight: '700', marginBottom: '6px' }}>TSR Reverted Successfully!</p>
                                <div style={{ color: '#000000', fontSize: '0.85rem' }}>
                                    <div>Sale Date: <strong>{tsrRevertSuccess.data?.sale_date}</strong></div>
                                    <div>Amount: <strong>₹{(tsrRevertSuccess.data?.total_amount_reverted || 0).toFixed(2)}</strong></div>
                                    <div>Stock items restored: <strong>{tsrRevertSuccess.data?.stock_items_restored || 0}</strong></div>
                                    <div>Negative batches removed: <strong>{tsrRevertSuccess.data?.negative_batches_removed || 0}</strong></div>
                                    <div>Line items deleted: <strong>{tsrRevertSuccess.data?.line_items_deleted || 0}</strong></div>
                                    <div>Daily ops reset: <strong>{tsrRevertSuccess.data?.daily_ops_updated ? 'Yes' : 'No (other sales exist)'}</strong></div>
                                </div>
                                <button
                                    onClick={() => { setShowTsrRevertModal(false); setTsrRevertSuccess(null); }}
                                    style={{ marginTop: '12px', width: '100%', padding: '10px', background: '#000000', color: '#fff', border: 'none', borderRadius: '8px', fontWeight: '600', cursor: 'pointer', fontSize: '0.9rem' }}
                                >
                                    Done
                                </button>
                            </div>
                        )}

                        {/* Error */}
                        {tsrRevertError && (
                            <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '10px', padding: '12px', marginBottom: '16px', color: '#991b1b', fontSize: '0.88rem', fontWeight: '600' }}>
                                ❌ {tsrRevertError}
                            </div>
                        )}

                        {/* Loading */}
                        {tsrRevertLoading && (
                            <div style={{ textAlign: 'center', padding: '32px', color: '#6b7280' }}>
                                <div style={{ fontSize: '32px', marginBottom: '8px' }}>⏳</div>
                                <p>Fetching recent TSRs...</p>
                            </div>
                        )}

                        {/* TSR List */}
                        {!tsrRevertLoading && !tsrRevertSuccess && (
                            <>
                                {/* How far back reverting reaches. Shown above the list so the
                                    boundary is understood before a choice is made, not after a refusal. */}
                                {tsrRevertWindow && (
                                    <div style={{
                                        fontSize: '0.8rem', color: '#374151', background: '#f8fafc',
                                        border: '1px solid #e5e7eb', borderRadius: '8px',
                                        padding: '8px 12px', marginBottom: '12px', lineHeight: 1.5
                                    }}>
                                        {tsrRevertWindow.basis === 'closing_count' ? (
                                            <>Reverting is open for sales <strong>after {tsrRevertWindow.floor}</strong> — the last confirmed closing stock count. Anything on or before that date is certified and locked.</>
                                        ) : (
                                            <>No closing stock count yet, so reverting is limited to the <strong>last 30 days</strong> (from {tsrRevertWindow.floor}).</>
                                        )}
                                        {tsrRevertList.length >= (tsrRevertWindow.max_rows || 50) && (
                                            <> Showing the most recent {tsrRevertWindow.max_rows} only.</>
                                        )}
                                    </div>
                                )}

                                {tsrRevertList.length === 0 ? (
                                    <div style={{ textAlign: 'center', padding: '32px', color: '#9ca3af', fontSize: '0.95rem' }}>
                                        {tsrRevertWindow?.basis === 'closing_count'
                                            ? `Nothing to revert — every sale up to ${tsrRevertWindow.floor} is certified by a closing stock count.`
                                            : 'No TSR records found.'}
                                    </div>
                                ) : (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', maxHeight: '55vh', overflowY: 'auto' }}>
                                        {tsrRevertList.map((tsr) => {
                                            const isConfirming = tsrRevertConfirmId === tsr.sale_id;
                                            const isReverting = tsrRevertingId === tsr.sale_id;
                                            const date = tsr.transaction_date
                                                ? (typeof tsr.transaction_date === 'string' ? tsr.transaction_date.slice(0, 10) : new Date(tsr.transaction_date).toLocaleDateString('en-CA'))
                                                : 'Unknown Date';
                                            const time = tsr.created_at
                                                ? new Date(tsr.created_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })
                                                : '';
                                            return (
                                                <div key={tsr.sale_id} style={{
                                                    border: isConfirming ? '2px solid #dc2626' : '1px solid #e5e7eb',
                                                    borderRadius: '12px', padding: '16px',
                                                    background: isConfirming ? '#fff5f5' : '#fafafa',
                                                    transition: 'all 0.2s'
                                                }}>
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
                                                        <div>
                                                            <div style={{ fontWeight: '700', fontSize: '1rem', color: '#111' }}>
                                                                Sale #{tsr.sale_id}
                                                                {/* Untagged sales predate provenance tracking, so the revert
                                                                    falls back to a ±30-minute time window and can restore stock
                                                                    belonging to another TSR submitted alongside it. The owner
                                                                    should see that BEFORE choosing, not in a post-mortem. */}
                                                                {tsr.has_provenance === false && (
                                                                    <span
                                                                        title="Recorded before stock movements were tagged. Reverting restores by time window, so quantities may be approximate if another TSR was submitted within half an hour."
                                                                        style={{
                                                                            marginLeft: '8px', fontSize: '0.62rem', fontWeight: 700,
                                                                            background: '#fef3c7', color: '#92400e',
                                                                            padding: '2px 7px', borderRadius: '999px',
                                                                            verticalAlign: 'middle', whiteSpace: 'nowrap'
                                                                        }}
                                                                    >
                                                                        LEGACY — APPROXIMATE
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <div style={{ fontSize: '0.82rem', color: '#6b7280', marginTop: '2px' }}>{date} • {time}</div>
                                                        </div>
                                                        <div style={{ textAlign: 'right' }}>
                                                            <div style={{ fontWeight: '700', fontSize: '1.05rem', color: '#059669' }}>₹{(tsr.total_amount || 0).toFixed(2)}</div>
                                                            <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>{tsr.items.length} item{tsr.items.length !== 1 ? 's' : ''}</div>
                                                        </div>
                                                    </div>

                                                    {/* Items preview */}
                                                    {tsr.items.length > 0 && (
                                                        <div style={{ marginBottom: '12px' }}>
                                                            {tsr.items.slice(0, 4).map((item, i) => (
                                                                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.82rem', color: '#374151', padding: '3px 0', borderBottom: i < Math.min(tsr.items.length, 4) - 1 ? '1px solid #f3f4f6' : 'none' }}>
                                                                    <span>{item.item_name}</span>
                                                                    <span style={{ color: '#6b7280' }}>×{item.quantity_sold} — ₹{(item.line_item_amount || 0).toFixed(2)}</span>
                                                                </div>
                                                            ))}
                                                            {tsr.items.length > 4 && (
                                                                <div style={{ fontSize: '0.78rem', color: '#9ca3af', marginTop: '4px' }}>+{tsr.items.length - 4} more items...</div>
                                                            )}
                                                        </div>
                                                    )}

                                                    {/* Action buttons */}
                                                    {!isConfirming ? (
                                                        <button
                                                            onClick={() => { setTsrRevertConfirmId(tsr.sale_id); setTsrRevertError(null); }}
                                                            disabled={!!tsrRevertingId}
                                                            style={{
                                                                width: '100%', padding: '9px', backgroundColor: '#000000',
                                                                color: '#fff', border: 'none', borderRadius: '8px',
                                                                fontSize: '0.9rem', fontWeight: '700', cursor: tsrRevertingId ? 'not-allowed' : 'pointer',
                                                                opacity: tsrRevertingId ? 0.5 : 1
                                                            }}
                                                        >
                                                            Revert This TSR
                                                        </button>
                                                    ) : (
                                                        <div>
                                                            <p style={{ color: '#991b1b', fontWeight: '700', fontSize: '0.88rem', marginBottom: '10px', textAlign: 'center' }}>
                                                                Are you sure? This action cannot be undone.
                                                            </p>
                                                            <div style={{ display: 'flex', gap: '10px' }}>
                                                                <button
                                                                    onClick={() => setTsrRevertConfirmId(null)}
                                                                    disabled={isReverting}
                                                                    style={{
                                                                        flex: 1, padding: '9px', background: '#f3f4f6',
                                                                        color: '#374151', border: 'none', borderRadius: '8px',
                                                                        fontWeight: '600', cursor: 'pointer', fontSize: '0.9rem'
                                                                    }}
                                                                >
                                                                    Cancel
                                                                </button>
                                                                <button
                                                                    onClick={() => handleConfirmRevert(tsr.sale_id)}
                                                                    disabled={isReverting}
                                                                    style={{
                                                                        flex: 1, padding: '9px', backgroundColor: '#000000',
                                                                        color: '#fff', border: 'none', borderRadius: '8px',
                                                                        fontWeight: '700', cursor: isReverting ? 'not-allowed' : 'pointer',
                                                                        fontSize: '0.9rem', opacity: isReverting ? 0.7 : 1
                                                                    }}
                                                                >
                                                                    {isReverting ? '⏳ Reverting...' : '✓ Yes, Revert'}
                                                                </button>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
            )}
            {/* ========== END TSR REVERT MODAL ========== */}

            {/* ========== ONLINE SALE REVERT MODAL (LOCALHOST / DEV ONLY) ========== */}
            {/* Banquet revert. Flag-gated only — the one way to correct a banquet.
                It restores the banquet's stock (and its wastage's, once confirmed);
                the server enforces the revert window and the closing-count lock. */}
            {showOutletRevertModal && (
                <OutletRevertModal
                    open
                    onClose={() => setShowOutletRevertModal(false)}
                    onReverted={() => {
                        try { window.dispatchEvent(new CustomEvent('daily-operations:refresh')); } catch (_) { }
                    }}
                />
            )}
            {showBanquetRevertModal && banquetEnabled && (
                <BanquetRevertModal
                    onClose={() => setShowBanquetRevertModal(false)}
                    onReverted={(reverted) => {
                        setError({
                            type: 'success',
                            message: `Banquet reverted — ${reverted.functionName} · ${reverted.eventDate}`,
                        });
                    }}
                />
            )}

            {showOnlineRevertModal && isLocalhost && (
                <div style={{
                    position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.55)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    zIndex: 99999, padding: '16px'
                }}>
                    <div style={{
                        background: '#fff', borderRadius: '16px', padding: '28px 24px',
                        maxWidth: '520px', width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
                        maxHeight: '85vh', overflowY: 'auto'
                    }}>
                        {/* Header */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <span style={{ fontSize: '22px' }}>↩</span>
                                <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: '700', color: '#111' }}>Revert Online Sale</h2>
                            </div>
                            <button
                                onClick={() => { setShowOnlineRevertModal(false); setOnlineRevertSuccess(null); setOnlineRevertError(null); setOnlineRevertConfirmId(null); }}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '22px', color: '#6b7280', lineHeight: 1 }}
                            >✕</button>
                        </div>
                        <p style={{ color: '#000000', fontSize: '0.82rem', fontWeight: '600', marginBottom: '4px' }}>
                            DEVELOPMENT ONLY — Not available in production
                        </p>
                        <p style={{ color: '#6b7280', fontSize: '0.85rem', marginBottom: '20px' }}>
                            Select an online order to fully revert. This will restore all deducted stock and delete the online sale record.
                        </p>

                        {/* Success */}
                        {onlineRevertSuccess && (
                            <div style={{ background: '#ffffff', border: '1px solid #000000', borderRadius: '10px', padding: '16px', marginBottom: '16px' }}>
                                <p style={{ color: '#000000', fontWeight: '700', marginBottom: '6px' }}>Online Sale Reverted Successfully!</p>
                                <div style={{ color: '#000000', fontSize: '0.85rem' }}>
                                    {onlineRevertSuccess.message && <div>{onlineRevertSuccess.message}</div>}
                                    {onlineRevertSuccess.data?.stock_items_restored != null && <div>Stock items restored: <strong>{onlineRevertSuccess.data.stock_items_restored}</strong></div>}
                                    {onlineRevertSuccess.data?.order_date && <div>Order Date: <strong>{onlineRevertSuccess.data.order_date}</strong></div>}
                                </div>
                                <button
                                    onClick={() => { setShowOnlineRevertModal(false); setOnlineRevertSuccess(null); }}
                                    style={{ marginTop: '12px', width: '100%', padding: '10px', background: '#000000', color: '#fff', border: 'none', borderRadius: '8px', fontWeight: '600', cursor: 'pointer', fontSize: '0.9rem' }}
                                >
                                    Done
                                </button>
                            </div>
                        )}

                        {/* Error */}
                        {onlineRevertError && (
                            <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '10px', padding: '12px', marginBottom: '16px', color: '#991b1b', fontSize: '0.88rem', fontWeight: '600' }}>
                                ❌ {onlineRevertError}
                            </div>
                        )}

                        {/* Loading */}
                        {onlineRevertLoading && (
                            <div style={{ textAlign: 'center', padding: '32px', color: '#6b7280' }}>
                                <div style={{ fontSize: '32px', marginBottom: '8px' }}>⏳</div>
                                <p>Fetching recent online orders...</p>
                            </div>
                        )}

                        {/* Online Order List */}
                        {!onlineRevertLoading && !onlineRevertSuccess && (
                            <>
                                {onlineRevertList.length === 0 ? (
                                    <div style={{ textAlign: 'center', padding: '32px', color: '#9ca3af', fontSize: '0.95rem' }}>
                                        No recent online orders found.
                                    </div>
                                ) : (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                        {onlineRevertList.map((order) => {
                                            const isConfirming = onlineRevertConfirmId === order.online_order_id;
                                            const isReverting = onlineRevertingId === order.online_order_id;
                                            const date = order.order_date
                                                ? (typeof order.order_date === 'string' ? order.order_date.slice(0, 10) : new Date(order.order_date).toLocaleDateString('en-CA'))
                                                : 'Unknown Date';
                                            const time = order.created_at
                                                ? new Date(order.created_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })
                                                : '';
                                            const lineItems = order.line_items || [];
                                            // Use line_items_total (sum of line_item_amount) to match the sales overview figure
                                            const totalAmt = parseFloat(order.line_items_total || order.total_amount || 0);
                                            return (
                                                <div key={order.online_order_id} style={{
                                                    border: isConfirming ? '2px solid #7c3aed' : '1px solid #e5e7eb',
                                                    borderRadius: '12px', padding: '16px',
                                                    background: isConfirming ? '#faf5ff' : '#fafafa',
                                                    transition: 'all 0.2s'
                                                }}>
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
                                                        <div>
                                                            <div style={{ fontWeight: '700', fontSize: '1rem', color: '#111' }}>Order #{order.online_order_id}</div>
                                                            <div style={{ fontSize: '0.82rem', color: '#6b7280', marginTop: '2px' }}>
                                                                {date}{time ? ` • ${time}` : ''}
                                                                {order.platform_source && (
                                                                    <span style={{ marginLeft: '6px', background: '#ede9fe', color: '#7c3aed', borderRadius: '4px', padding: '1px 6px', fontSize: '0.75rem', fontWeight: '600' }}>
                                                                        {order.platform_source}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </div>
                                                        <div style={{ textAlign: 'right' }}>
                                                            <div style={{ fontWeight: '700', fontSize: '1.05rem', color: '#059669' }}>₹{totalAmt.toFixed(2)}</div>
                                                            <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>{lineItems.length} item{lineItems.length !== 1 ? 's' : ''}</div>
                                                        </div>
                                                    </div>

                                                    {/* Items preview */}
                                                    {lineItems.length > 0 && (
                                                        <div style={{ marginBottom: '12px' }}>
                                                            {lineItems.slice(0, 4).map((item, i) => (
                                                                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.82rem', color: '#374151', padding: '3px 0', borderBottom: i < Math.min(lineItems.length, 4) - 1 ? '1px solid #f3f4f6' : 'none' }}>
                                                                    <span>{item.item_name || item.name}</span>
                                                                    <span style={{ color: '#6b7280' }}>×{item.quantity_sold || item.quantity} — ₹{parseFloat(item.line_item_amount || item.final_line_amount || 0).toFixed(2)}</span>
                                                                </div>
                                                            ))}
                                                            {lineItems.length > 4 && (
                                                                <div style={{ fontSize: '0.78rem', color: '#9ca3af', marginTop: '4px' }}>+{lineItems.length - 4} more items...</div>
                                                            )}
                                                        </div>
                                                    )}

                                                    {/* Action buttons */}
                                                    {!isConfirming ? (
                                                        <button
                                                            onClick={() => { setOnlineRevertConfirmId(order.online_order_id); setOnlineRevertError(null); }}
                                                            disabled={!!onlineRevertingId}
                                                            style={{
                                                                width: '100%', padding: '9px', backgroundColor: '#7c3aed',
                                                                color: '#fff', border: 'none', borderRadius: '8px',
                                                                fontSize: '0.9rem', fontWeight: '700', cursor: onlineRevertingId ? 'not-allowed' : 'pointer',
                                                                opacity: onlineRevertingId ? 0.5 : 1
                                                            }}
                                                        >
                                                            Revert This Order
                                                        </button>
                                                    ) : (
                                                        <div>
                                                            <p style={{ color: '#7c3aed', fontWeight: '700', fontSize: '0.88rem', marginBottom: '10px', textAlign: 'center' }}>
                                                                Are you sure? This action cannot be undone.
                                                            </p>
                                                            <div style={{ display: 'flex', gap: '10px' }}>
                                                                <button
                                                                    onClick={() => { setOnlineRevertConfirmId(null); }}
                                                                    disabled={isReverting}
                                                                    style={{
                                                                        flex: 1, padding: '9px', background: '#f3f4f6',
                                                                        color: '#374151', border: 'none', borderRadius: '8px',
                                                                        fontWeight: '600', cursor: 'pointer', fontSize: '0.9rem'
                                                                    }}
                                                                >
                                                                    Cancel
                                                                </button>
                                                                <button
                                                                    onClick={() => handleConfirmOnlineRevert(order.online_order_id)}
                                                                    disabled={isReverting}
                                                                    style={{
                                                                        flex: 1, padding: '9px', backgroundColor: '#7c3aed',
                                                                        color: '#fff', border: 'none', borderRadius: '8px',
                                                                        fontWeight: '700', cursor: isReverting ? 'not-allowed' : 'pointer',
                                                                        fontSize: '0.9rem', opacity: isReverting ? 0.7 : 1
                                                                    }}
                                                                >
                                                                    {isReverting ? '⏳ Reverting...' : '✓ Yes, Revert'}
                                                                </button>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
            )}
            {/* ========== END ONLINE SALE REVERT MODAL ========== */}

            {/* ========== SWIGGY ORDER REPORT PARSING INDICATOR ========== */}
            {orderReportParsing && (
                <div className="tsr-or-parsing-overlay">
                    <div className="tsr-or-parsing-card">
                        <div className="tsr-or-parsing-spinner" />
                        <div className="tsr-or-parsing-text">Parsing platform report…</div>
                    </div>
                </div>
            )}

            {/* ========== DAY-WISE (MULTI-DATE) TSR IMPORT ========== */}
            {dayWiseReport && (
                <DayWiseTsrModal
                    report={dayWiseReport}
                    onClose={() => setDayWiseReport(null)}
                    // Step aside while the shared shortage modal is up: it sits
                    // at a lower z-index than this one, and stacking two review
                    // modals asks the operator to answer two questions at once.
                    hidden={showMissingModal}
                    onShortage={({ missingIngredients: missing, proceed }) => {
                        // Reuse the TSR's own Missing Ingredients modal rather
                        // than a second popup: same grouping by dish, same
                        // search, same "Confirm & Create Negative Stock" /
                        // "Remove from Recipe" choices the operator already knows.
                        setMissingIngredients(missing);
                        setSelectedMissingItems(getFilteredSelection(missing, {}));
                        setDayWiseProceed({ fn: proceed });
                        setShowMissingModal(true);
                    }}
                    onImported={() => {
                        // Imported dates each get their own DailyOperations row,
                        // so the lock state the page shows is now stale.
                        try { window.dispatchEvent(new CustomEvent('daily-operations:refresh')); } catch (_) { }
                    }}
                />
            )}

            {/* ========== SWIGGY ORDER REPORT REVIEW MODAL (Online Order Mode) ========== */}
            {showOrderReportModal && orderReportData && (
                <SwiggyOrderReportModal
                    orderReport={orderReportData}
                    menuList={orderReportMenu}
                    onClose={() => { setShowOrderReportModal(false); setOrderReportData(null); }}
                    onImported={() => {
                        // Online orders are recorded per-date; refresh daily-operations lock state
                        try { window.dispatchEvent(new CustomEvent('daily-operations:refresh')); } catch (_) { }
                    }}
                    onEditRecipe={(menuItemId) => {
                        // "Remove and Update Recipe" → open the recipe editor for that item
                        try {
                            if (menuItemId) localStorage.setItem('autoSelectRecipeId', menuItemId);
                            const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
                            const isQRUser = storedUser?.isQRBilling === true || storedUser?.business?.isQRBilling === true;
                            setShowOrderReportModal(false);
                            setOrderReportData(null);
                            goTo && goTo(isQRUser ? 'qr-itemmap' : 'itemmap');
                        } catch (_) { /* no-op */ }
                    }}
                />
            )}

            {/* ========== PLATFORM PICKER MODAL ========== */}
            {showPlatformPicker && (
                <div
                    style={{
                        position: 'fixed', inset: 0, zIndex: 9999,
                        background: 'rgba(0,0,0,0.45)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        padding: '16px',
                    }}
                    onClick={() => setShowPlatformPicker(false)}
                >
                    <div
                        style={{
                            background: '#fff',
                            borderRadius: '24px',
                            padding: '24px 20px 32px',
                            width: '100%',
                            maxWidth: '480px',
                            boxShadow: '0 10px 32px rgba(0,0,0,0.15)',
                        }}
                        onClick={e => e.stopPropagation()}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '18px' }}>
                            <h2 style={{ margin: 0, fontSize: '1.15rem', fontWeight: '700', color: '#111' }}>
                                Select Platform
                            </h2>
                            {isOnlineOrderMode && (
                                <button
                                    type="button"
                                    onClick={() => {
                                        setIsOnlineOrderMode(false);
                                        setSelectedPlatform(null);
                                        setShowPlatformPicker(false);
                                    }}
                                    style={{
                                        background: 'none', border: 'none', cursor: 'pointer',
                                        color: '#dc2626', fontSize: '0.85rem', fontWeight: '600',
                                        padding: '4px 8px',
                                    }}
                                >
                                    Disable Online Mode
                                </button>
                            )}
                        </div>
                        <p style={{ margin: '0 0 16px', fontSize: '0.85rem', color: '#6b7280' }}>
                            All sales will be categorised under the selected platform in Online Reports.
                        </p>

                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '20px' }}>
                            {platforms.map(p => {
                                const isSelected = selectedPlatform === p.name;
                                const colorMap = {
                                    Zomato: { bg: isSelected ? '#e23744' : '#fff4f4', color: isSelected ? '#fff' : '#e23744', border: '#e23744' },
                                    Swiggy: { bg: isSelected ? '#fc8019' : '#fff5ee', color: isSelected ? '#fff' : '#fc8019', border: '#fc8019' },
                                    Others: { bg: isSelected ? '#374151' : '#f3f4f6', color: isSelected ? '#fff' : '#374151', border: '#9ca3af' },
                                };
                                const s = colorMap[p.name] || {
                                    bg: isSelected ? '#111827' : '#f9fafb',
                                    color: isSelected ? '#fff' : '#111827',
                                    border: isSelected ? '#111827' : '#d1d5db',
                                };
                                return (
                                    <button
                                        key={p.platform_id || p.name}
                                        type="button"
                                        onClick={() => setSelectedPlatform(p.name)}
                                        style={{
                                            padding: '10px 20px',
                                            borderRadius: '50px',
                                            border: `2px solid ${s.border}`,
                                            background: s.bg,
                                            color: s.color,
                                            fontWeight: '700',
                                            fontSize: '0.95rem',
                                            cursor: 'pointer',
                                            transition: 'all 0.15s',
                                            display: 'flex', alignItems: 'center', gap: '6px',
                                        }}
                                    >
                                        {isSelected && <span>&#10003; </span>}{p.name}
                                    </button>
                                );
                            })}
                        </div>

                        <div style={{ display: 'flex', gap: '10px' }}>
                            <button
                                type="button"
                                onClick={() => setShowPlatformPicker(false)}
                                style={{
                                    flex: 1, padding: '13px', borderRadius: '12px',
                                    border: '1.5px solid #d1d5db', background: '#fff',
                                    color: '#374151', fontWeight: '600', cursor: 'pointer', fontSize: '0.95rem',
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                disabled={!selectedPlatform}
                                onClick={() => {
                                    if (!selectedPlatform) return;
                                    setIsOnlineOrderMode(true);
                                    setError({ type: 'info', message: `Online Order Mode \u2014 ${selectedPlatform}` });
                                    setShowPlatformPicker(false);
                                }}
                                style={{
                                    flex: 1, padding: '13px', borderRadius: '12px',
                                    border: 'none',
                                    background: selectedPlatform ? '#111827' : '#e5e7eb',
                                    color: selectedPlatform ? '#fff' : '#9ca3af',
                                    fontWeight: '700',
                                    cursor: selectedPlatform ? 'pointer' : 'not-allowed',
                                    fontSize: '0.95rem', transition: 'all 0.2s',
                                }}
                            >
                                Confirm
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {/* ========== END PLATFORM PICKER MODAL ========== */}

        </>
    );
};

export default TodaysSalesReport;
